// src/utils/enhancedHtmlReportGenerator.js
import Chart from 'chart.js/auto';

/**
 * Enhanced HTML Report Generator
 * Generates professional slide-style HTML reports with embedded charts, 
 * advanced data visualization, and modern responsive design
 */
export class EnhancedHtmlReportGenerator {
    constructor(reportData, kpiData, chartData, logoData, options = {}) {
        this.reportData = reportData;
        this.kpiData = kpiData;
        this.chartData = chartData;
        this.logoData = logoData;
        this.chartInstances = new Map();
        this.currentPage = 1;
        this.analysisId = options.analysisId || reportData?.metadata?.analysisId;

        // 🔍 COMPREHENSIVE DATA LOGGING
        console.log('🚀 [EnhancedHtmlReportGenerator] Constructor - Full Data Received:');
        console.log('📊 ReportData Structure:', {
            keys: reportData ? Object.keys(reportData) : 'null',
            metadata: reportData?.metadata || 'missing',
            workshops: reportData?.workshops ? Object.keys(reportData.workshops) : 'missing',
            hasAnalysisControls: !!reportData?.analysisControls,
            fullReportData: reportData // Log the entire object
        });

        if (reportData?.workshops) {
            console.log('🏭 Workshops Data:');
            Object.entries(reportData.workshops).forEach(([workshopKey, workshopData]) => {
                console.log(`  📋 ${workshopKey}:`, {
                    keys: workshopData ? Object.keys(workshopData) : 'null',
                    data: workshopData
                });
            });
        }

        if (reportData?.workshops?.workshop5) {
            console.log('🎯 Workshop5 Detailed Analysis:', {
                keys: Object.keys(reportData.workshops.workshop5),
                treatmentPlanDetails: reportData.workshops.workshop5.treatmentPlanDetails,
                riskTreatmentPlans: reportData.workshops.workshop5.riskTreatmentPlans,
                fullWorkshop5: reportData.workshops.workshop5
            });
        }

        console.log('📈 KPI Data:', kpiData);
        console.log('📊 Chart Data:', chartData);
        this.colors = {
            primary: '#1e3a8a',
            secondary: '#3b82f6',
            accent: '#60a5fa',
            success: '#10b981',
            warning: '#f59e0b',
            danger: '#ef4444',
            info: '#6366f1',
            purple: '#8b5cf6',
            dark: '#0f172a',
            light: '#f8fafc',
            border: '#e2e8f0',
            textPrimary: '#0f172a',
            textSecondary: '#64748b',
            textWhite: '#ffffff'
        };
    }

    /**
     * Generate complete HTML report
     * @returns {Promise<Blob>} HTML blob
     */
    async generateCompleteReport() {
        try {
            console.log('[EnhancedHtmlReportGenerator] Starting HTML report generation');

            // Validate required data before generation
            if (!this.reportData) {
                throw new Error('Impossible de g\u00e9n\u00e9rer le rapport : aucune donn\u00e9e d\'analyse fournie. Veuillez v\u00e9rifier que l\'analyse contient des donn\u00e9es valides.');
            }

            if (!this.reportData.metadata) {
                throw new Error('Impossible de g\u00e9n\u00e9rer le rapport : les m\u00e9tadonn\u00e9es de l\'analyse sont manquantes. Veuillez rafra\u00eechir la page et r\u00e9essayer.');
            }

            const htmlContent = await this.generateHtmlDocument();
            const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
            console.log('[EnhancedHtmlReportGenerator] HTML report generated successfully');
            return blob;
        } catch (error) {
            console.error('[EnhancedHtmlReportGenerator] Error generating HTML report:', error);

            // Provide user-friendly error messages
            if (error.message.includes('Impossible de g\u00e9n\u00e9rer le rapport')) {
                throw error; // Re-throw user-friendly errors as-is
            }

            // Handle specific error types with clear messages
            if (error.name === 'TypeError' && error.message.includes('Cannot read property')) {
                throw new Error('Erreur lors de la g\u00e9n\u00e9ration du rapport : certaines donn\u00e9es de l\'analyse sont corrompues ou manquantes. Veuillez contacter le support technique.');
            }

            if (error.name === 'RangeError') {
                throw new Error('Erreur lors de la g\u00e9n\u00e9ration du rapport : les donn\u00e9es sont trop volumineuses. Veuillez r\u00e9duire la taille de votre analyse ou contacter le support.');
            }

            // Generic fallback with helpful context
            throw new Error(`Erreur inattendue lors de la g\u00e9n\u00e9ration du rapport HTML. Veuillez r\u00e9essayer dans quelques instants. Si le probl\u00e8me persiste, contactez le support technique avec le code d'erreur : ${error.name || 'UNKNOWN'}`);
        }
    }

    /**
     * Generate complete HTML document
     * @returns {Promise<string>} Complete HTML document
     */
    async generateHtmlDocument() {
        this.currentPage = 1; // Reset page counter

        return `<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Rapport EBIOS RM - ${this.reportData.metadata?.analysisName || 'Analyse'}</title>
    ${this.generateStyles()}
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
</head>
<body>
    <div class="report-container">
        ${this.generateCoverPage()}
        ${this.generateTableOfContents()}
        ${this.generateExecutiveSummary()}
        ${await this.generateWorkshopAnalysis()}
        ${this.generateAppendices()}
    </div>
    ${this.generateScripts()}
</body>
</html>`;
    }

    /**
     * Generate CSS styles with enhanced design
     * @returns {string} CSS styles
     */
    generateStyles() {
        const { colors } = this;
        return `<style>
        /* Professional Report Styles with Modern Design */
        :root {
            --primary-color: ${colors.primary};
            --secondary-color: ${colors.secondary};
            --accent-color: ${colors.accent};
            --success-color: ${colors.success};
            --warning-color: ${colors.warning};
            --danger-color: ${colors.danger};
            --info-color: ${colors.info};
            --purple-color: ${colors.purple};
            --dark-color: ${colors.dark};
            --light-bg: ${colors.light};
            --light-border: ${colors.border};
            --text-primary: ${colors.textPrimary};
            --text-secondary: ${colors.textSecondary};
            --text-white: ${colors.textWhite};
            --shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
            --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            zoom: 0.75;
            color: var(--text-primary);
            background-color: #f5f7fa;
            counter-reset: page;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
        }
        
        .report-container {
            max-width: 1400px;
            margin: 0 auto;
            background: white;
            box-shadow: var(--shadow-xl);
            position: relative;
            overflow: hidden;
        }
        
        /* Slide-Style Pages with Enhanced Design */
        .slide {
            position: relative;
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            padding: 90px 70px 70px;
            page-break-after: always;
            counter-increment: page;
            background: linear-gradient(to bottom, #ffffff, #fafbfc);
        }
        
        .slide-header {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 70px;
            background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
            color: var(--text-white);
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 70px;
            z-index: 10;
            box-shadow: var(--shadow);
        }
        
        .slide-title {
            font-size: 1.6rem;
            font-weight: 700;
            letter-spacing: -0.025em;
        }
        
        .slide-footer {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 50px;
            background: var(--light-bg);
            border-top: 1px solid var(--light-border);
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 70px;
            z-index: 10;
        }
        
        .footer-logos {
            display: flex;
            align-items: center;
            gap: 15px;
        }
        
        .footer-logo {
            max-height: 25px;
            opacity: 0.7;
            transition: opacity 0.3s ease;
        }
        
        .footer-logo:hover {
            opacity: 1;
        }
        
        .page-number::before {
            content: counter(page);
        }
        
        .slide-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 20px 0;
            max-width: 100%;
        }
        
        /* Enhanced Cover Page */
        .cover-slide {
            background: linear-gradient(135deg, var(--dark-color) 0%, var(--primary-color) 50%, var(--secondary-color) 100%);
            color: var(--text-white);
            position: relative;
            overflow: hidden;
        }
        
        .cover-slide::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></svg>');
            opacity: 0.2;
            z-index: 0;
        }
        
        .cover-slide::after {
            content: "";
            position: absolute;
            bottom: -100px;
            right: -100px;
            width: 400px;
            height: 400px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.05);
            z-index: 0;
        }
        
        .cover-content {
            position: relative;
            z-index: 2;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            height: 100%;
            text-align: center;
        }
        
        .cover-logos {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 50px;
            margin-bottom: 50px;
            flex-wrap: wrap;
        }
        
        .cover-logo {
            max-height: 120px;
            filter: brightness(0) invert(1);
            transition: transform 0.3s ease;
        }
        
        .cover-logo:hover {
            transform: scale(1.05);
        }
        
        .cover-title {
            font-size: 5rem;
            font-weight: 900;
            margin-bottom: 30px;
            letter-spacing: -0.03em;
            line-height: 1.1;
            background: linear-gradient(to right, #ffffff, #e2e8f0);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            text-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
        }
        
        .cover-subtitle {
            font-size: 2.5rem;
            font-weight: 300;
            margin-bottom: 50px;
            opacity: 0.9;
            color: var(--text-white);
        }
        
        .cover-info {
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            border-radius: 20px;
            padding: 40px;
            border: 1px solid rgba(255, 255, 255, 0.2);
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
            max-width: 700px;
            width: 100%;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .cover-info:hover {
            transform: translateY(-5px);
            box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
        }
        
        .cover-info h3 {
            font-size: 1.5rem;
            margin-bottom: 25px;
            font-weight: 700;
            color: var(--text-white);
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }
        
        .cover-info p {
            margin: 15px 0;
            font-size: 1.1rem;
            display: flex;
            justify-content: space-between;
            color: var(--text-white);
            padding: 8px 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .cover-info p:last-child {
            border-bottom: none;
        }
        
        .cover-info strong {
            font-weight: 700;
            opacity: 0.8;
        }
        
        /* Enhanced Table of Contents */
        .toc-slide {
            background: linear-gradient(to bottom, #ffffff, #f8fafc);
        }
        
        .toc-container {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 40px;
        }
        
        .toc-section {
            background: white;
            border-radius: 16px;
            padding: 30px;
            box-shadow: var(--shadow-lg);
            border-left: 5px solid var(--secondary-color);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .toc-section:hover {
            transform: translateY(-5px);
            box-shadow: var(--shadow-xl);
        }
        
        .toc-section h3 {
            font-size: 1.5rem;
            margin-bottom: 20px;
            color: var(--primary-color);
            font-weight: 700;
            position: relative;
            padding-bottom: 15px;
        }
        
        .toc-section h3::after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            width: 50px;
            height: 3px;
            background: var(--secondary-color);
            border-radius: 2px;
        }
        
        .toc-entry {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 0;
            border-bottom: 1px dotted var(--light-border);
            transition: var(--transition);
        }
        
        .toc-entry:hover {
            background-color: rgba(59, 130, 246, 0.05);
            padding-left: 10px;
            border-radius: 8px;
        }
        
        .toc-entry a {
            color: var(--primary-color);
            text-decoration: none;
            font-weight: 600;
            transition: color 0.2s ease;
            display: flex;
            align-items: center;
        }
        
        .toc-entry a i {
            margin-right: 8px;
            font-size: 0.9rem;
            opacity: 0.7;
        }
        
        .toc-entry a:hover {
            color: var(--secondary-color);
            text-decoration: underline;
        }
        
        .toc-entry.toc-sub {
            margin-left: 20px;
            font-size: 0.95rem;
        }
        
        .page-number {
            color: var(--text-secondary);
            font-size: 0.95rem;
            font-weight: 600;
            background: var(--light-bg);
            padding: 4px 10px;
            border-radius: 20px;
        }
        
        /* Enhanced Headers and Typography */
        h1 {
            font-size: 3rem;
            color: var(--primary-color);
            margin-bottom: 2rem;
            position: relative;
            padding-bottom: 1.5rem;
            font-weight: 800;
            letter-spacing: -0.025em;
        }
        
        h1::after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100px;
            height: 5px;
            background: linear-gradient(to right, var(--secondary-color), var(--accent-color));
            border-radius: 3px;
        }
        
        h2 {
            font-size: 2.4rem;
            color: var(--primary-color);
            margin: 2rem 0 1.5rem 0;
            position: relative;
            padding-left: 1.5rem;
            font-weight: 700;
        }
        
        h2::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 6px;
            background: linear-gradient(to bottom, var(--secondary-color), var(--accent-color));
            border-radius: 3px;
        }
        
        h3 {
            font-size: 1.8rem;
            color: var(--text-primary);
            margin: 1.8rem 0 1rem 0;
            font-weight: 700;
        }
        
        h4 {
            font-size: 1.5rem;
            color: var(--text-primary);
            margin: 1.5rem 0 0.75rem 0;
            font-weight: 600;
        }
        
        p {
            margin-bottom: 1.2rem;
            text-align: justify;
            font-size: 1.1rem;
            line-height: 1.8;
            color: var(--text-primary);
        }
        
        /* Enhanced KPI Cards */
        .kpi-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 2rem;
            margin: 2rem 0;
        }
        
        .kpi-card {
            background: white;
            border: 1px solid var(--light-border);
            border-radius: 16px;
            padding: 2rem;
            text-align: center;
            box-shadow: var(--shadow-lg);
            transition: var(--transition);
            border-left: 5px solid var(--secondary-color);
            position: relative;
            overflow: hidden;
        }
        
        .kpi-card::before {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            width: 100px;
            height: 100px;
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
            border-radius: 0 0 0 100%;
            z-index: 0;
        }
        
        .kpi-card:hover {
            transform: translateY(-8px);
            box-shadow: var(--shadow-xl);
        }
        
        .kpi-value {
            font-size: 3.2rem;
            font-weight: 800;
            color: var(--primary-color);
            margin-bottom: 0.8rem;
            position: relative;
            z-index: 1;
        }
        
        .kpi-label {
            color: var(--text-secondary);
            font-size: 1.1rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            position: relative;
            z-index: 1;
        }
        
        .kpi-icon {
            position: absolute;
            top: 20px;
            right: 20px;
            font-size: 2rem;
            color: var(--secondary-color);
            opacity: 0.3;
        }
        
        /* Enhanced Tables */
        .table-container {
            overflow-x: auto;
            margin: 2rem 0;
            border-radius: 16px;
            box-shadow: var(--shadow-lg);
        }
        
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
            background-color: white;
        }
        
        th, td {
            padding: 1.2rem;
            text-align: left;
            border-bottom: 1px solid var(--light-border);
        }
        
        th {
            background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
            color: white;
            font-weight: 700;
            position: sticky;
            top: 0;
            z-index: 10;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            font-size: 0.9rem;
        }
        
        tr:nth-child(even) {
            background-color: var(--light-bg);
        }
        
        tr:hover {
            background-color: rgba(59, 130, 246, 0.05);
        }
        
        /* Enhanced Treatment Plan Table */
        .treatment-plan-table {
            font-size: 0.85rem;
            border-collapse: collapse;
            border: 1px solid #d1d5db;
        }
        
        .treatment-plan-table th {
            background-color: #f9fafb;
            color: #4b5563;
            font-weight: 600;
            padding: 0.75rem;
            border: 1px solid #d1d5db;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            font-size: 0.75rem;
        }
        
        .treatment-plan-table td {
            padding: 0.75rem;
            border: 1px solid #d1d5db;
            vertical-align: top;
            background-color: white;
        }
        
        .treatment-plan-table tr:nth-child(even) {
            background-color: rgba(0, 0, 0, 0.02);
        }
        
        .treatment-plan-table tbody tr:hover {
            background-color: rgba(59, 130, 246, 0.05);
        }
        
        .treatment-plan-table .nested-controls-table {
            width: 100%;
            font-size: 0.75rem;
            border-collapse: collapse;
        }
        
        .treatment-plan-table .nested-controls-table th {
            background: none;
            color: var(--text-secondary);
            font-weight: 600;
            text-transform: uppercase;
            font-size: 0.7rem;
            padding: 0 0 0.25rem 0;
            border: none;
        }
        
        .treatment-plan-table .nested-controls-table td {
            padding: 0.5rem 0.25rem;
            border-top: 1px solid rgba(0, 0, 0, 0.1);
            vertical-align: top;
        }
        
        .treatment-plan-table .nested-controls-table tr:first-child td {
            border-top: none;
        }
        
        .control-details {
            font-size: 0.8rem;
            line-height: 1.4;
        }
        
        .control-details > div {
            margin-bottom: 0.5rem;
        }
        
        .control-name {
            font-weight: 700;
            color: var(--text-primary);
        }
        
        .control-meta {
            color: var(--text-secondary);
        }
        
        /* Enhanced Info Boxes */
        .info-box {
            padding: 2rem;
            margin: 2rem 0;
            border-radius: 16px;
            border-left: 5px solid;
            box-shadow: var(--shadow-lg);
            transition: var(--transition);
            position: relative;
            overflow: hidden;
        }
        
        .info-box::before {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            width: 150px;
            height: 150px;
            opacity: 0.05;
            z-index: 0;
        }
        
        .info-box:hover {
            transform: translateY(-5px);
            box-shadow: var(--shadow-xl);
        }
        
        .info-box.success {
            background-color: rgba(16, 185, 129, 0.1);
            border-color: var(--success-color);
            color: #065f46;
        }
        
        .info-box.success::before {
            background: radial-gradient(circle, var(--success-color), transparent 70%);
        }
        
        .info-box.warning {
            background-color: rgba(245, 158, 11, 0.1);
            border-color: var(--warning-color);
            color: #92400e;
        }
        
        .info-box.warning::before {
            background: radial-gradient(circle, var(--warning-color), transparent 70%);
        }
        
        .info-box.error {
            background-color: rgba(239, 68, 68, 0.1);
            border-color: var(--danger-color);
            color: #991b1b;
        }
        
        .info-box.error::before {
            background: radial-gradient(circle, var(--danger-color), transparent 70%);
        }
        
        .info-box.info {
            background-color: rgba(59, 130, 246, 0.1);
            border-color: var(--secondary-color);
            color: var(--primary-color);
        }
        
        .info-box.info::before {
            background: radial-gradient(circle, var(--secondary-color), transparent 70%);
        }
        
        .info-box.ai {
            background-color: rgba(139, 92, 246, 0.1);
            border-color: var(--purple-color);
            color: #5b21b6;
        }
        
        .info-box.ai::before {
            background: radial-gradient(circle, var(--purple-color), transparent 70%);
        }
        
        .info-box h3 {
            position: relative;
            z-index: 1;
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }
        
        .info-box h3 i {
            font-size: 1.5rem;
        }

        /* Radar Visualization Section Styles */
        .radar-visualization-section {
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            border: 2px solid #cbd5e1;
            border-radius: 16px;
            padding: 2.5rem;
            margin: 2.5rem 0;
            box-shadow: var(--shadow-lg);
            position: relative;
            overflow: hidden;
        }

        .radar-visualization-section::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(90deg, var(--primary-color), var(--accent-color), var(--secondary-color));
        }

        .radar-visualization-section .section-title {
            color: var(--primary-color);
            font-size: 1.75rem;
            font-weight: 700;
            margin-bottom: 1.5rem;
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }

        .radar-visualization-section .section-title i {
            font-size: 2rem;
            color: var(--accent-color);
        }

        .visualization-container {
            background: white;
            border-radius: 12px;
            padding: 1.5rem;
            margin: 1.5rem 0;
            box-shadow: var(--shadow);
            text-align: center;
        }

        .visualization-container img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .visualization-description {
            background: rgba(255, 255, 255, 0.8);
            border-radius: 12px;
            padding: 1.5rem;
            margin-top: 1.5rem;
            border-left: 4px solid var(--accent-color);
        }

        .visualization-description p {
            margin-bottom: 1rem;
            color: var(--text-primary);
            font-size: 1rem;
            line-height: 1.6;
        }

        .visualization-description ul {
            list-style: none;
            padding: 0;
        }

        .visualization-description li {
            margin-bottom: 0.75rem;
            padding-left: 1.5rem;
            position: relative;
            color: var(--text-secondary);
            font-size: 0.95rem;
        }

        .visualization-description li span {
            position: absolute;
            left: 0;
            top: 0;
            font-size: 1.2rem;
        }
        
        /* Enhanced Status Badges */
        .status-badge {
            display: inline-flex;
            align-items: center;
            padding: 0.5rem 1rem;
            border-radius: 9999px;
            font-size: 0.85rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            gap: 0.5rem;
        }
        
        .status-badge i {
            font-size: 0.8rem;
        }
        
        .status-badge.completed {
            background-color: rgba(16, 185, 129, 0.15);
            color: #065f46;
        }
        
        .status-badge.in-progress {
            background-color: rgba(245, 158, 11, 0.15);
            color: #92400e;
        }
        
        .status-badge.not-started {
            background-color: rgba(239, 68, 68, 0.15);
            color: #991b1b;
        }
        
        .status-badge.info {
            background-color: rgba(59, 130, 246, 0.15);
            color: #1e40af;
        }
        
        .status-badge.secondary {
            background-color: rgba(107, 114, 128, 0.15);
            color: #374151;
        }
        
        /* Enhanced Progress Bars */
        .progress-container {
            width: 100%;
            background-color: var(--light-border);
            border-radius: 9999px;
            height: 12px;
            margin: 1.5rem 0;
            overflow: hidden;
            box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
        }
        
        .progress-bar {
            height: 100%;
            border-radius: 9999px;
            transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;
        }
        
        .progress-bar::after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 0),
                rgba(255, 255, 255, 0.3),
                rgba(255, 255, 255, 0)
            );
            animation: shimmer 2s infinite;
        }
        
        @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
        }
        
        .progress-bar.success {
            background: linear-gradient(90deg, var(--success-color), #34d399);
        }
        
        .progress-bar.warning {
            background: linear-gradient(90deg, var(--warning-color), #fbbf24);
        }
        
        .progress-bar.danger {
            background: linear-gradient(90deg, var(--danger-color), #f87171);
        }
        
        /* Enhanced Chart Containers */
        .chart-container {
            position: relative;
            height: 300px;
            margin: 2rem 0;
            padding: 1.5rem;
            background: white;
            border-radius: 16px;
            box-shadow: var(--shadow-lg);
        }
        
        .chart-title {
            font-size: 1.2rem;
            font-weight: 700;
            color: var(--primary-color);
            margin-bottom: 1rem;
            text-align: center;
        }
        
        /* Enhanced Print Styles */
        @media print {
            body {
                background: white;
                color: black;
            }
            
            .report-container {
                box-shadow: none;
                max-width: none;
            }
            
            .slide {
                page-break-after: always;
                min-height: 100vh;
            }
            
            .kpi-card, .info-box {
                box-shadow: none;
                border: 1px solid #ddd;
            }
        }
        
        /* Enhanced Responsive Design */
        @media (max-width: 1200px) {
            .slide {
                padding: 80px 50px 60px;
            }
            
            .slide-header, .slide-footer {
                padding: 0 50px;
            }
            
            .cover-title {
                font-size: 4rem;
            }
            
            .cover-subtitle {
                font-size: 2rem;
            }
            
            .cover-logos {
                gap: 40px;
            }
            
            .cover-logo {
                max-height: 100px;
            }
        }
        
        @media (max-width: 992px) {
            .slide {
                padding: 70px 40px 50px;
            }
            
            .slide-header, .slide-footer {
                padding: 0 40px;
            }
            
            .slide-title {
                font-size: 1.4rem;
            }
            
            .cover-title {
                font-size: 3.5rem;
            }
            
            .cover-subtitle {
                font-size: 1.8rem;
            }
            
            .cover-logos {
                flex-direction: column;
                gap: 30px;
            }
            
            .toc-container {
                grid-template-columns: 1fr;
            }
            
            .kpi-grid {
                grid-template-columns: repeat(2, 1fr);
            }
            
            h1 {
                font-size: 2.5rem;
            }
            
            h2 {
                font-size: 2rem;
            }
        }
        
        @media (max-width: 768px) {
            .slide {
                padding: 60px 20px 40px;
            }
            
            .slide-header, .slide-footer {
                padding: 0 20px;
            }
            
            .slide-title {
                font-size: 1.2rem;
            }
            
            .cover-title {
                font-size: 2.5rem;
            }
            
            .cover-subtitle {
                font-size: 1.5rem;
            }
            
            .cover-logos {
                gap: 20px;
            }
            
            .cover-logo {
                max-height: 70px;
            }
            
            .kpi-grid {
                grid-template-columns: 1fr;
            }
            
            h1 {
                font-size: 2rem;
            }
            
            h2 {
                font-size: 1.6rem;
            }
            
            .cover-info {
                padding: 25px;
            }
        }
    </style>`;
    }

    /**
     * Generate cover page with enhanced design
     * @returns {string} Cover page HTML
     */
    generateCoverPage() {
        const metadata = this.reportData.metadata || {};
        const companyName = metadata.companyName || 'Organisation';
        const analysisName = metadata.analysisName || 'Analyse EBIOS RM';
        const createdBy = metadata.createdBy || 'Non sp\u00e9cifi\u00e9';
        const createdAt = metadata.createdAt ? new Date(metadata.createdAt).toLocaleDateString('fr-FR') : 'Non sp\u00e9cifi\u00e9';

        return `
    <div class="slide cover-slide">
        <div class="slide-header">
            <div class="slide-title">Rapport EBIOS RM</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <div class="cover-content">
                <h1 class="cover-title">Rapport EBIOS RM</h1>
                <h2 class="cover-subtitle">${analysisName}</h2>
                
                <div class="cover-info">
                    <h3><i class="fas fa-info-circle"></i> Informations du Rapport</h3>
                    <p><strong>Organisation:</strong> <span>${companyName}</span></p>
                    <p><strong>Cr\u00e9\u00e9 par:</strong> <span>${createdBy}</span></p>
                    <p><strong>Date de cr\u00e9ation:</strong> <span>${createdAt}</span></p>
                    <p><strong>G\u00e9n\u00e9r\u00e9 le:</strong> <span>${new Date().toLocaleDateString('fr-FR')}</span></p>
                    <p><strong>Version:</strong> <span>${metadata.version || '1.0'}</span></p>
                </div>
            </div>
        </div>
    </div>`;
    }

    /**
     * Generate table of contents with enhanced design
     * @returns {string} Table of contents HTML
     */
    generateTableOfContents() {
        this.currentPage++;
        const workshops = this.reportData.workshops || {};
        const hasEnhancedSections = this.hasMinimumDataForEnhancedAnalysis(workshops);

        return `
    <div class="slide toc-slide">
        <div class="slide-header">
            <div class="slide-title">Table des Mati\u00e8res</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Table des Mati\u00e8res</h1>
            <div class="toc-container">
                <div class="toc-section">
                    <h3><i class="fas fa-list"></i> Sections Principales</h3>
                    <div class="toc-entry">
                        <a href="#executive-summary"><i class="fas fa-file-alt"></i> 1. R\u00e9sum\u00e9 Ex\u00e9cutif</a>
                        <span class="page-number">${this.currentPage + 1}</span>
                    </div>
                    <div class="toc-entry">
                        <a href="#workshop-analysis"><i class="fas fa-tasks"></i> 2. Analyse D\u00e9taill\u00e9e des Ateliers</a>
                        <span class="page-number">${this.currentPage + 2}</span>
                    </div>
                    ${hasEnhancedSections ? `
                    <div class="toc-entry">
                        <a href="#cross-analysis"><i class="fas fa-project-diagram"></i> 3. Analyse Transversale</a>
                        <span class="page-number">${this.currentPage + 8}</span>
                    </div>
                    <div class="toc-entry">
                        <a href="#risk-matrix"><i class="fas fa-table"></i> 4. Matrice des Risques</a>
                        <span class="page-number">${this.currentPage + 9}</span>
                    </div>
                    <div class="toc-entry">
                        <a href="#implementation-roadmap"><i class="fas fa-road"></i> 5. Feuille de Route</a>
                        <span class="page-number">${this.currentPage + 10}</span>
                    </div>
                    ` : ''}
                    <div class="toc-entry">
                        <a href="#appendices"><i class="fas fa-paperclip"></i> ${hasEnhancedSections ? '6' : '3'}. Annexes</a>
                        <span class="page-number">${this.currentPage + (hasEnhancedSections ? 11 : 8)}</span>
                    </div>
                </div>
                <div class="toc-section">
                    <h3><i class="fas fa-sitemap"></i> D\u00e9tail des Ateliers</h3>
                    <div class="toc-entry toc-sub">
                        <a href="#workshop1"><i class="fas fa-clipboard-list"></i> 2.1. Atelier 1 - Cadrage et Socle de S\u00e9curit\u00e9</a>
                        <span class="page-number">${this.currentPage + 3}</span>
                    </div>
                    <div class="toc-entry toc-sub">
                        <a href="#workshop2"><i class="fas fa-user-secret"></i> 2.2. Atelier 2 - Sources de Risque et Objectifs Vis\u00e9s</a>
                        <span class="page-number">${this.currentPage + 4}</span>
                    </div>
                    <div class="toc-entry toc-sub">
                        <a href="#workshop3"><i class="fas fa-chess"></i> 2.3. Atelier 3 - Sc\u00e9narios Strat\u00e9giques</a>
                        <span class="page-number">${this.currentPage + 5}</span>
                    </div>
                    <div class="toc-entry toc-sub">
                        <a href="#workshop4"><i class="fas fa-cogs"></i> 2.4. Atelier 4 - Sc\u00e9narios Op\u00e9rationnels</a>
                        <span class="page-number">${this.currentPage + 6}</span>
                    </div>
                    <div class="toc-entry toc-sub">
                        <a href="#workshop5"><i class="fas fa-shield-alt"></i> 2.5. Atelier 5 - Traitement du Risque</a>
                        <span class="page-number">${this.currentPage + 7}</span>
                    </div>
                    <div class="toc-entry toc-sub">
                        <a href="#cross-analysis"><i class="fas fa-project-diagram"></i> 2.6. Analyse Transversale</a>
                        <span class="page-number">${this.currentPage + 8}</span>
                    </div>
                    <div class="toc-entry toc-sub">
                        <a href="#risk-matrix"><i class="fas fa-table"></i> 2.7. Matrice des Risques Consolid\u00e9e</a>
                        <span class="page-number">${this.currentPage + 9}</span>
                    </div>
                    <div class="toc-entry toc-sub">
                        <a href="#implementation-roadmap"><i class="fas fa-road"></i> 2.8. Feuille de Route d'Impl\u00e9mentation</a>
                        <span class="page-number">${this.currentPage + 10}</span>
                    </div>
                </div>
            </div>
        </div>

    </div>`;
    }

    /**
     * Generate executive summary with enhanced design
     * @returns {string} Executive summary HTML
     */
    generateExecutiveSummary() {
        this.currentPage++;
        const workshops = this.reportData.workshops || {};
        const summary = this.reportData.executiveSummary || {};
        const contextInfo = summary.contextInfo || {};
        const metrics = summary.metrics || {};
        const riskAnalysis = summary.riskAnalysis || {};

        // Fallback to workshop data if executive summary not available
        const businessValuesCount = metrics.totalBusinessValues || workshops.workshop1?.businessValues?.length || 0;
        const dreadedEventsCount = metrics.totalDreadedEvents || workshops.workshop1?.dreadedEvents?.length || 0;
        const riskSourcesCount = metrics.totalRiskSources || workshops.workshop2?.riskSources?.length || 0;
        const strategicScenariosCount = metrics.totalStrategicScenarios || workshops.workshop3?.strategicScenarios?.length || 0;
        const operationalScenariosCount = metrics.totalOperationalScenarios || workshops.workshop4?.operationalScenarios?.length || 0;
        const treatmentPlansCount = metrics.totalTreatmentPlans || 0;
        const securityControlsCount = metrics.totalSecurityControls || 0;

        return `
    <div class="slide" id="executive-summary">
        <div class="slide-header">
            <div class="slide-title">R\u00e9sum\u00e9 Ex\u00e9cutif</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>R\u00e9sum\u00e9 Ex\u00e9cutif</h1>
            
            <!-- Key Metrics Dashboard -->
            <div class="kpi-grid">
                <div class="kpi-card">
                    <i class="fas fa-building kpi-icon"></i>
                    <div class="kpi-value">${businessValuesCount}</div>
                    <div class="kpi-label">Valeurs M\u00e9tier</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-exclamation-circle kpi-icon"></i>
                    <div class="kpi-value">${dreadedEventsCount}</div>
                    <div class="kpi-label">\u00c9v\u00e9nements Redout\u00e9s</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-user-shield kpi-icon"></i>
                    <div class="kpi-value">${riskSourcesCount}</div>
                    <div class="kpi-label">Sources de Risque</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-project-diagram kpi-icon"></i>
                    <div class="kpi-value">${strategicScenariosCount + operationalScenariosCount}</div>
                    <div class="kpi-label">Sc\u00e9narios de Risque</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-shield-alt kpi-icon"></i>
                    <div class="kpi-value">${treatmentPlansCount}</div>
                    <div class="kpi-label">Plans de Traitement</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-cog kpi-icon"></i>
                    <div class="kpi-value">${securityControlsCount}</div>
                    <div class="kpi-label">Contr\u00f4les de S\u00e9curit\u00e9</div>
                </div>
            </div>

            <!-- Progression des Ateliers -->
            <h2>Progression des Ateliers</h2>
            
            <!-- Enhanced Analysis Status -->
            ${(() => {
                const hasEnhancedAnalysis = this.hasMinimumDataForEnhancedAnalysis(workshops);
                return `
                <div class="info-box ${hasEnhancedAnalysis ? 'success' : 'warning'}">
                    <h3><i class="fas fa-${hasEnhancedAnalysis ? 'check-circle' : 'clock'}"></i> Statut de l'Analyse Avanc\u00e9e</h3>
                    <p>${hasEnhancedAnalysis ?
                        'L\'analyse contient suffisamment de donn\u00e9es pour g\u00e9n\u00e9rer les sections d\'analyse transversale, matrice de risques consolid\u00e9e et feuille de route d\'impl\u00e9mentation.' :
                        'L\'analyse n\u00e9cessite des donn\u00e9es suppl\u00e9mentaires pour activer les sections d\'analyse avanc\u00e9e. Compl\u00e9tez les ateliers pour d\u00e9bloquer l\'analyse transversale compl\u00e8te.'
                    }</p>
                    ${!hasEnhancedAnalysis ? `
                        <div style="margin-top: 1rem; padding: 1rem; background: rgba(255, 255, 255, 0.8); border-radius: 8px;">
                            <strong>Prochaines \u00e9tapes recommand\u00e9es :</strong>
                            <ul style="margin: 0.5rem 0 0 1.5rem;">
                                ${businessValuesCount === 0 ? '<li>Identifier les valeurs m\u00e9tier dans l\'Atelier 1</li>' : ''}
                                ${riskSourcesCount === 0 ? '<li>Documenter les sources de risque dans l\'Atelier 2</li>' : ''}
                                ${(strategicScenariosCount + operationalScenariosCount) === 0 ? '<li>D\u00e9velopper des sc\u00e9narios dans les Ateliers 3 ou 4</li>' : ''}
                            </ul>
                        </div>
                    ` : ''}
                </div>
                `;
            })()}
            
            <div class="info-box success">
                <h3><i class="fas fa-chart-line"></i> \u00c9tat d'Avancement par Atelier</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem;">
                    ${Object.entries(workshops).map(([key, workshop]) => {
                const workshopNumber = key.replace('workshop', '');
                const status = this.getCompletionStatus(workshop);
                const label = this.getCompletionLabel(workshop);
                const percentage = this.getCompletionPercentage(workshop);

                return `
                            <div style="background: white; border-radius: 12px; padding: 1.5rem; text-align: center; border-left: 4px solid ${this.getStatusColor(status)};">
                                <div style="font-size: 1.5rem; font-weight: 700; color: #1e293b; margin-bottom: 0.5rem;">Atelier ${workshopNumber}</div>
                                <div style="margin-bottom: 1rem;">
                                    <span class="status-badge ${status}">${label}</span>
                                </div>
                                <div class="progress-container" style="height: 8px; margin: 1rem 0;">
                                    <div class="progress-bar" style="width: ${percentage}%; background: ${this.getStatusColor(status)};"></div>
                                </div>
                                <div style="font-size: 0.9rem; color: #6b7280;">${percentage}% compl\u00e9t\u00e9</div>
                            </div>
                        `;
            }).join('')}
                </div>
            </div>
            
            <!-- Informations G\u00e9n\u00e9rales -->
            <div class="info-box info">
                <h3><i class="fas fa-info-circle"></i> Informations G\u00e9n\u00e9rales</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem; font-size: 1rem;">
                    <p><strong><i class="fas fa-file-signature"></i> Nom de l'analyse :</strong> ${contextInfo.analysisName || this.reportData.metadata?.analysisName || 'Analyse EBIOS RM'}</p>
                    <p><strong><i class="fas fa-calendar-alt"></i> Date de cr\u00e9ation :</strong> ${contextInfo.createdAt ? new Date(contextInfo.createdAt).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR')}</p>
                    <p><strong><i class="fas fa-building"></i> Organisation :</strong> ${contextInfo.organizationName || this.reportData.metadata?.companyName || 'Non d\u00e9fini'}</p>
                    <p><strong><i class="fas fa-industry"></i> Secteur d'activit\u00e9 :</strong> ${contextInfo.sector || 'Non d\u00e9fini'}</p>
                    <p style="grid-column: 1 / -1;"><strong><i class="fas fa-border-all"></i> P\u00e9rim\u00e8tre :</strong> ${contextInfo.scope || 'Non d\u00e9fini'}</p>
                    <p style="grid-column: 1 / -1;"><strong><i class="fas fa-bullseye"></i> Objectif de l'analyse :</strong> ${contextInfo.analysisObjective || 'Analyse de risques selon la m\u00e9thode EBIOS RM'}</p>
                    ${contextInfo.missions?.length > 0 ? `
                        <div style="grid-column: 1 / -1;">
                            <strong><i class="fas fa-tasks"></i> Missions :</strong>
                            <ul style="margin: 0.5rem 0 0 1.5rem;">
                                ${contextInfo.missions.map(mission => `<li>${mission}</li>`).join('')}
                            </ul>
                        </div>
                    ` : ''}
                    ${contextInfo.contraintes?.length > 0 ? `
                        <div style="grid-column: 1 / -1;">
                            <strong><i class="fas fa-exclamation-triangle"></i> Contraintes :</strong>
                            <ul style="margin: 0.5rem 0 0 1.5rem;">
                                ${contextInfo.contraintes.map(item => `<li>${item}</li>`).join('')}
                            </ul>
                        </div>
                    ` : ''}
                    ${contextInfo.hypotheses?.length > 0 ? `
                        <div style="grid-column: 1 / -1;">
                            <strong><i class="fas fa-lightbulb"></i> Hypoth\u00e8ses :</strong>
                            <ul style="margin: 0.5rem 0 0 1.5rem;">
                                ${contextInfo.hypotheses.map(item => `<li>${item}</li>`).join('')}
                            </ul>
                        </div>
                    ` : ''}
                </div>
            </div>
            
            <!-- Vue d'ensemble de l'analyse -->
            <div class="info-box success">
                <h3><i class="fas fa-chart-line"></i> Vue d'ensemble de l'analyse</h3>
                <p>Cette analyse EBIOS RM a \u00e9t\u00e9 men\u00e9e pour <strong>${contextInfo.organizationName || this.reportData.metadata?.companyName || 'l\'organisation'}</strong> afin d'\u00e9valuer les risques de cybers\u00e9curit\u00e9 et d\u00e9finir une strat\u00e9gie de traitement appropri\u00e9e. L'analyse couvre <strong>${contextInfo.scope || 'le p\u00e9rim\u00e8tre d\u00e9fini'}</strong> et s'appuie sur une approche m\u00e9thodologique rigoureuse.</p>
                <div style="margin-top: 1.5rem;">
                    <h4 style="color: var(--success-color); margin-bottom: 1rem; font-weight: 700;"><i class="fas fa-chart-pie"></i> Synth\u00e8se quantitative :</h4>
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem; font-size: 1rem;">
                        <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                            <div style="font-size: 2rem; font-weight: 800; color: var(--success-color);">${businessValuesCount}</div>
                            <div style="text-align: center;">valeurs m\u00e9tier identifi\u00e9es</div>
                        </div>
                        <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                            <div style="font-size: 2rem; font-weight: 800; color: var(--warning-color);">${dreadedEventsCount}</div>
                            <div style="text-align: center;">\u00e9v\u00e9nements redout\u00e9s analys\u00e9s</div>
                        </div>
                        <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                            <div style="font-size: 2rem; font-weight: 800; color: var(--info-color);">${riskSourcesCount}</div>
                            <div style="text-align: center;">sources de risque \u00e9valu\u00e9es</div>
                        </div>
                        <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                            <div style="font-size: 2rem; font-weight: 800; color: var(--danger-color);">${strategicScenariosCount + operationalScenariosCount}</div>
                            <div style="text-align: center;">sc\u00e9narios de risque mod\u00e9lis\u00e9s</div>
                        </div>
                        <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                            <div style="font-size: 2rem; font-weight: 800; color: var(--purple-color);">${treatmentPlansCount}</div>
                            <div style="text-align: center;">mesures de traitement d\u00e9finies</div>
                        </div>
                        <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                            <div style="font-size: 2rem; font-weight: 800; color: var(--secondary-color);">${securityControlsCount}</div>
                            <div style="text-align: center;">contr\u00f4les de s\u00e9curit\u00e9 recens\u00e9s</div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- \u00c9valuation des risques -->
            <div class="info-box ai">
                <h3><i class="fas fa-shield-virus"></i> \u00c9valuation des risques</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 2rem;">
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1.5rem; background: rgba(255,255,255,0.2); border-radius: 16px;">
                        <h4 style="margin-bottom: 1rem; font-weight: 700;">Niveau de risque global</h4>
                        <span class="status-badge ${this.getRiskLevelClass(riskAnalysis.riskAssessment?.overallRiskLevel)}" style="font-size: 1.2rem; padding: 0.75rem 1.5rem;">
                            <i class="fas fa-exclamation-triangle"></i> ${riskAnalysis.riskAssessment?.overallRiskLevel || 'Non \u00e9valu\u00e9'}
                        </span>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1.5rem; background: rgba(255,255,255,0.2); border-radius: 16px;">
                        <h4 style="margin-bottom: 1rem; font-weight: 700;">Couverture du traitement</h4>
                        <div style="position: relative; width: 120px; height: 120px;">
                            <svg viewBox="0 0 36 36" style="width: 100%; height: 100%;">
                                <path
                                    d="M18 2.0845
                                    a 15.9155 15.9155 0 0 1 0 31.831
                                    a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="#eee"
                                    stroke-width="3"
                                />
                                <path
                                    d="M18 2.0845
                                    a 15.9155 15.9155 0 0 1 0 31.831
                                    a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="var(--success-color)"
                                    stroke-width="3"
                                    stroke-dasharray="${riskAnalysis.riskAssessment?.treatmentCoverage || 0}, 100"
                                />
                                <text x="18" y="21" text-anchor="middle" fill="var(--primary-color)" font-size="8" font-weight="bold">${riskAnalysis.riskAssessment?.treatmentCoverage || 0}%</text>
                            </svg>
                        </div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1.5rem; background: rgba(255,255,255,0.2); border-radius: 16px;">
                        <h4 style="margin-bottom: 1rem; font-weight: 700;">Maturit\u00e9 s\u00e9curit\u00e9</h4>
                        <span class="status-badge info" style="font-size: 1.2rem; padding: 0.75rem 1.5rem;">
                            <i class="fas fa-lock"></i> ${summary.maturityLevel || 'Non d\u00e9fini'}
                        </span>
                    </div>
                </div>
                ${riskAnalysis.criticalBusinessValues?.length > 0 ? `
                    <div style="margin-top: 2rem;">
                        <h4 style="margin-bottom: 1rem; font-weight: 700;"><i class="fas fa-star"></i> Valeurs m\u00e9tier critiques :</h4>
                        <div style="display: flex; flex-wrap: wrap; gap: 1rem;">
                            ${riskAnalysis.criticalBusinessValues.map(bv => `
                                <span class="status-badge ${this.getCriticalityClass(bv.criticalityLevel)}" style="font-size: 1rem;">
                                    <i class="fas fa-exclamation-circle"></i> ${bv.name} (${bv.criticalityLevel})
                                </span>
                            `).join('')}
                        </div>
                    </div>
                ` : ''}
            </div>
            
            <!-- Principales Conclusions -->
            <div class="info-box success">
                <h3><i class="fas fa-lightbulb"></i> Principales Conclusions</h3>
                <ul style="margin: 0; padding-left: 1.5rem;">
                    ${(summary.keyFindings || []).map(finding => `<li style="margin-bottom: 0.75rem;"><i class="fas fa-check-circle" style="color: var(--success-color); margin-right: 0.5rem;"></i> ${finding}</li>`).join('')}
                    ${summary.keyFindings?.length === 0 ? '<li><i class="fas fa-info-circle" style="color: var(--info-color); margin-right: 0.5rem;"></i> Analyse en cours - conclusions \u00e0 venir</li>' : ''}
                </ul>
            </div>
            
            <!-- Recommandations Prioritaires -->
            <div class="info-box warning">
                <h3><i class="fas fa-tasks"></i> Recommandations Prioritaires</h3>
                <ul style="margin: 0; padding-left: 1.5rem;">
                    ${(summary.recommendations || []).map(rec => `<li style="margin-bottom: 0.75rem;"><i class="fas fa-arrow-right" style="color: var(--warning-color); margin-right: 0.5rem;"></i> ${rec}</li>`).join('')}
                    ${summary.recommendations?.length === 0 ? '<li><i class="fas fa-info-circle" style="color: var(--info-color); margin-right: 0.5rem;"></i> Recommandations en cours d\'\u00e9laboration</li>' : ''}
                </ul>

            </div>
        </div>

    </div>`;
    }

    /**
     * Generate Feared Events identification section with enhanced design
     * @returns {string} Feared Events HTML
     */
    generateDreadedEventsSection() {
        this.currentPage++;
        const workshops = this.reportData.workshops || {};
        const workshop1Data = workshops.workshop1 || {};
        const dreadedEvents = workshop1Data.dreadedEvents || [];
        const businessValues = workshop1Data.businessValues || [];

        return `
    <div class="slide" id="dreaded-events">
        <div class="slide-header">
            <div class="slide-title">Identification des \u00c9v\u00e9nements Redout\u00e9s</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Identification des \u00c9v\u00e9nements Redout\u00e9s</h1>
            
            <!-- Key Metrics -->
            <div class="kpi-grid">
                <div class="kpi-card">
                    <i class="fas fa-exclamation-triangle kpi-icon"></i>
                    <div class="kpi-value">${dreadedEvents.length}</div>
                    <div class="kpi-label">\u00c9v\u00e9nements Redout\u00e9s</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-building kpi-icon"></i>
                    <div class="kpi-value">${businessValues.length}</div>
                    <div class="kpi-label">Valeurs M\u00e9tier</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-radiation kpi-icon"></i>
                    <div class="kpi-value">${dreadedEvents.filter(de => de.severity === 'high' || de.severity === '\u00e9lev\u00e9').length}</div>
                    <div class="kpi-label">\u00c9v\u00e9nements Critiques</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-chart-line kpi-icon"></i>
                    <div class="kpi-value">${dreadedEvents.filter(de => de.likelihood === 'high' || de.likelihood === '\u00e9lev\u00e9').length}</div>
                    <div class="kpi-label">\u00c9v\u00e9nements Probables</div>
                </div>
            </div>

            <!-- R\u00e9f\u00e9rence vers Atelier 1 -->
            <div class="info-box info">
                <h3><i class="fas fa-clipboard-list"></i> Plan de Traitement</h3>
                <p style="margin: 0; font-size: 1.1rem;">Le plan de traitement d\u00e9taill\u00e9 de ces \u00e9v\u00e9nements redout\u00e9s est disponible dans la section <strong>Atelier 1 - Plan de Traitement des Risques</strong>.</p>
            </div>
            
            <!-- Synth\u00e8se et recommandations -->
            <div class="info-box ai">
                <h3><i class="fas fa-lightbulb"></i> Synth\u00e8se et Recommandations</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 2rem;">
                    <div>
                        <h4 style="margin: 0 0 1rem 0; color: var(--purple-color); font-weight: 700;"><i class="fas fa-exclamation-circle"></i> Points d'attention</h4>
                        <ul style="margin: 0; padding-left: 1.5rem; font-size: 1rem;">
                            ${dreadedEvents.filter(de => de.severity === 'high' || de.severity === '\u00e9lev\u00e9').length > 0 ?
                `<li style="margin-bottom: 0.75rem;"><i class="fas fa-arrow-right" style="color: var(--danger-color); margin-right: 0.5rem;"></i>Prioriser le traitement des ${dreadedEvents.filter(de => de.severity === 'high' || de.severity === '\u00e9lev\u00e9').length} \u00e9v\u00e9nements critiques</li>` : ''}
                            ${businessValues.filter(bv => !dreadedEvents.some(de => de.businessValueId === bv.id)).length > 0 ?
                `<li style="margin-bottom: 0.75rem;"><i class="fas fa-arrow-right" style="color: var(--warning-color); margin-right: 0.5rem;"></i>Analyser les ${businessValues.filter(bv => !dreadedEvents.some(de => de.businessValueId === bv.id)).length} valeurs m\u00e9tier sans \u00e9v\u00e9nements redout\u00e9s</li>` : ''}
                            <li style="margin-bottom: 0.75rem;"><i class="fas fa-arrow-right" style="color: var(--info-color); margin-right: 0.5rem;"></i>R\u00e9viser r\u00e9guli\u00e8rement l'\u00e9valuation des \u00e9v\u00e9nements redout\u00e9s</li>
                            <li style="margin-bottom: 0.75rem;"><i class="fas fa-arrow-right" style="color: var(--info-color); margin-right: 0.5rem;"></i>Documenter les crit\u00e8res de s\u00e9curit\u00e9 pour chaque \u00e9v\u00e9nement</li>
                        </ul>
                    </div>
                    <div>
                        <h4 style="margin: 0 0 1rem 0; color: var(--purple-color); font-weight: 700;"><i class="fas fa-tasks"></i> Actions recommand\u00e9es</h4>
                        <ul style="margin: 0; padding-left: 1.5rem; font-size: 1rem;">
                            <li style="margin-bottom: 0.75rem;"><i class="fas fa-check-circle" style="color: var(--success-color); margin-right: 0.5rem;"></i>Valider les \u00e9v\u00e9nements redout\u00e9s avec les m\u00e9tiers</li>
                            <li style="margin-bottom: 0.75rem;"><i class="fas fa-check-circle" style="color: var(--success-color); margin-right: 0.5rem;"></i>Affiner l'\u00e9valuation de la gravit\u00e9 et vraisemblance</li>
                            <li style="margin-bottom: 0.75rem;"><i class="fas fa-check-circle" style="color: var(--success-color); margin-right: 0.5rem;"></i>D\u00e9finir des indicateurs de d\u00e9tection pr\u00e9coce</li>
                            <li style="margin-bottom: 0.75rem;"><i class="fas fa-check-circle" style="color: var(--success-color); margin-right: 0.5rem;"></i>Pr\u00e9parer des plans de r\u00e9ponse aux incidents</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

    </div>`;
    }

    /**
     * Generate workshop analysis section with enhanced design
     * @returns {string} Workshop analysis HTML
     */
    async generateWorkshopAnalysis() {
        const workshops = this.reportData.workshops || {};
        let workshopContent = '';

        // Workshop Overview
        this.currentPage++;
        workshopContent += `
    <div class="slide" id="workshop-analysis">
        <div class="slide-header">
            <div class="slide-title">Analyse D\u00e9taill\u00e9e des Ateliers</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Analyse D\u00e9taill\u00e9e des Ateliers</h1>
            <p style="font-size: 1.2rem; margin-bottom: 2rem;">Cette section pr\u00e9sente l'analyse d\u00e9taill\u00e9e de chaque atelier EBIOS RM avec les donn\u00e9es collect\u00e9es et les r\u00e9sultats obtenus.</p>
            
            <div class="kpi-grid">
                <div class="kpi-card">
                    <i class="fas fa-building kpi-icon"></i>
                    <div class="kpi-value">${workshops.workshop1?.businessValues?.length || 0}</div>
                    <div class="kpi-label">Biens Supports</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-user-secret kpi-icon"></i>
                    <div class="kpi-value">${workshops.workshop2?.riskSources?.length || 0}</div>
                    <div class="kpi-label">Sources de Risque</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-project-diagram kpi-icon"></i>
                    <div class="kpi-value">${(workshops.workshop3?.strategicScenarios?.length || 0) + (workshops.workshop4?.operationalScenarios?.length || 0)}</div>
                    <div class="kpi-label">Sc\u00e9narios</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-shield-alt kpi-icon"></i>
                    <div class="kpi-value">${workshops.workshop5?.riskTreatmentPlans?.length || 0}</div>
                    <div class="kpi-label">Plans de Traitement</div>
                </div>
            </div>
            
            <h2>Synth\u00e8se des Ateliers</h2>
            <div class="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Atelier</th>
                            <th>\u00c9l\u00e9ments Cl\u00e9s</th>
                            <th>Donn\u00e9es Collect\u00e9es</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong><i class="fas fa-clipboard-list"></i> Atelier 1</strong><br>Cadrage et Socle de S\u00e9curit\u00e9</td>
                            <td>
                                <div>${workshops.workshop1?.businessValues?.length || 0} valeurs m\u00e9tier</div>
                                <div>${workshops.workshop1?.dreadedEvents?.length || 0} \u00e9v\u00e9nements redout\u00e9s</div>
                                <div>${workshops.workshop1?.securityControls?.length || 0} contr\u00f4les de s\u00e9curit\u00e9</div>
                            </td>
                            <td>
                                <span class="status-badge ${this.getCompletionStatus(workshops.workshop1)}">${this.getCompletionLabel(workshops.workshop1)}</span>
                                <div style="margin-top: 0.5rem; font-size: 0.9rem;">P\u00e9rim\u00e8tre d\u00e9fini, actifs identifi\u00e9s</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong><i class="fas fa-user-secret"></i> Atelier 2</strong><br>Sources de Risque et Objectifs Vis\u00e9s</td>
                            <td>
                                <div>${workshops.workshop2?.riskSources?.length || 0} sources de risque</div>
                                <div>${workshops.workshop2?.stakeholders?.length || 0} parties prenantes</div>
                                <div>${workshops.workshop2?.objectives?.length || 0} objectifs vis\u00e9s</div>
                            </td>
                            <td>
                                <span class="status-badge ${this.getCompletionStatus(workshops.workshop2)}">${this.getCompletionLabel(workshops.workshop2)}</span>
                                <div style="margin-top: 0.5rem; font-size: 0.9rem;">\u00c9cosyst\u00e8me de menaces analys\u00e9</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong><i class="fas fa-chess"></i> Atelier 3</strong><br>Sc\u00e9narios Strat\u00e9giques</td>
                            <td>
                                <div>${workshops.workshop3?.strategicScenarios?.length || 0} sc\u00e9narios strat\u00e9giques</div>
                                <div>${workshops.workshop3?.ecosystemMeasures?.length || 0} mesures \u00e9cosyst\u00e8me</div>
                            </td>
                            <td>
                                <span class="status-badge ${this.getCompletionStatus(workshops.workshop3)}">${this.getCompletionLabel(workshops.workshop3)}</span>
                                <div style="margin-top: 0.5rem; font-size: 0.9rem;">Chemins d'attaque identifi\u00e9s</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong><i class="fas fa-cogs"></i> Atelier 4</strong><br>Sc\u00e9narios Op\u00e9rationnels</td>
                            <td>
                                <div>${workshops.workshop4?.operationalScenarios?.length || 0} sc\u00e9narios op\u00e9rationnels</div>
                                <div>\u00c9valuation des risques d\u00e9taill\u00e9e</div>
                            </td>
                            <td>
                                <span class="status-badge ${this.getCompletionStatus(workshops.workshop4)}">${this.getCompletionLabel(workshops.workshop4)}</span>
                                <div style="margin-top: 0.5rem; font-size: 0.9rem;">Modes op\u00e9ratoires analys\u00e9s</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong><i class="fas fa-shield-alt"></i> Atelier 5</strong><br>Traitement du Risque</td>
                            <td>
                                <div>${workshops.workshop5?.riskTreatmentPlans?.length || 0} plans de traitement</div>
                                <div>${workshops.workshop5?.treatmentStrategies ? Object.keys(workshops.workshop5.treatmentStrategies).length : 0} strat\u00e9gies</div>
                            </td>
                            <td>
                                <span class="status-badge ${this.getCompletionStatus(workshops.workshop5)}">${this.getCompletionLabel(workshops.workshop5)}</span>
                                <div style="margin-top: 0.5rem; font-size: 0.9rem;">Mesures de s\u00e9curit\u00e9 d\u00e9finies</div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

    </div>`;

        // Workshop 1
        if (workshops.workshop1) {
            this.currentPage++;
            workshopContent += this.generateWorkshop1Section(workshops.workshop1);
        }

        // Workshop 2
        if (workshops.workshop2) {
            this.currentPage++;
            workshopContent += await this.generateWorkshop2Section(workshops.workshop2);
        }

        // Workshop 3
        if (workshops.workshop3) {
            this.currentPage++;
            workshopContent += await this.generateWorkshop3Section(workshops.workshop3);
        }

        // Workshop 4
        if (workshops.workshop4) {
            this.currentPage++;
            workshopContent += this.generateWorkshop4Section(workshops.workshop4);
        }

        // Workshop 5
        if (workshops.workshop5) {
            this.currentPage++;
            workshopContent += await this.generateWorkshop5Section(workshops.workshop5);
        }

        // Only add enhanced sections if there's sufficient real data
        const hasRealData = this.hasMinimumDataForEnhancedAnalysis(workshops);

        console.log('[EnhancedHtmlReportGenerator] Enhanced sections availability check:', {
            hasRealData,
            workshopDataSummary: {
                workshop1: {
                    businessValues: workshops.workshop1?.businessValues?.length || 0,
                    dreadedEvents: workshops.workshop1?.dreadedEvents?.length || 0
                },
                workshop2: {
                    riskSources: workshops.workshop2?.riskSources?.length || 0,
                    stakeholders: workshops.workshop2?.stakeholders?.length || 0
                },
                workshop3: {
                    strategicScenarios: workshops.workshop3?.strategicScenarios?.length || 0
                },
                workshop4: {
                    operationalScenarios: workshops.workshop4?.operationalScenarios?.length || 0
                },
                workshop5: {
                    riskTreatmentPlans: workshops.workshop5?.riskTreatmentPlans?.length || 0
                }
            }
        });

        if (hasRealData) {
            console.log('[EnhancedHtmlReportGenerator] Generating enhanced analysis sections');

            // Cross-Analysis Section
            this.currentPage++;
            workshopContent += this.generateCrossAnalysisSection(workshops);

            // Risk Matrix Section
            this.currentPage++;
            workshopContent += this.generateRiskMatrixSection(workshops);

            // Implementation Roadmap Section
            this.currentPage++;
            workshopContent += this.generateImplementationRoadmapSection(workshops);
        } else {
            console.log('[EnhancedHtmlReportGenerator] Insufficient data for enhanced sections, showing info section');

            // Add informational section about enhanced analysis availability
            this.currentPage++;
            workshopContent += this.generateEnhancedAnalysisInfoSection(workshops);
        }

        return workshopContent;
    }

    /**
     * Generate Workshop 1 section with enhanced design
     * @param {Object} workshop1Data - Workshop 1 data
     * @returns {string} Workshop 1 HTML
     */
    generateWorkshop1Section(workshop1Data) {
        const businessValues = workshop1Data.businessValues || [];
        const dreadedEvents = workshop1Data.dreadedEvents || [];
        const securityControls = workshop1Data.securityControls || [];
        const securityFramework = workshop1Data.securityFramework || {};
        // Extract treatment plan data - it should be in workshop1Data.treatmentPlan
        const workshop5Data = this.reportData.workshops?.workshop5 || {};
        let treatmentPlan = {};

        console.log('🔍 ===== TREATMENT PLAN DATA EXTRACTION =====');
        console.log('📊 Workshop1Data keys:', Object.keys(workshop1Data));
        console.log('📊 Workshop1Data.treatmentPlan:', workshop1Data.treatmentPlan);
        console.log('📊 Workshop5Data keys:', Object.keys(workshop5Data));
        console.log('📊 Workshop5Data.treatmentPlanDetails:', workshop5Data.treatmentPlanDetails);

        // The reportService puts the treatment plan in workshop1Data.treatmentPlan
        if (workshop1Data.treatmentPlan) {
            treatmentPlan = workshop1Data.treatmentPlan;
            console.log('✅ Found treatmentPlan in workshop1Data.treatmentPlan:', treatmentPlan);
        } else if (workshop5Data.treatmentPlanDetails) {
            treatmentPlan = workshop5Data.treatmentPlanDetails;
            console.log('✅ Found treatmentPlan in workshop5Data.treatmentPlanDetails:', treatmentPlan);
        } else if (workshop5Data.planData?.plan) {
            treatmentPlan = workshop5Data.planData.plan;
            console.log('✅ Found treatmentPlan in workshop5Data.planData.plan:', treatmentPlan);
        } else if (workshop5Data.plan) {
            treatmentPlan = workshop5Data.plan;
            console.log('✅ Found treatmentPlan in workshop5Data.plan:', treatmentPlan);
        } else if (workshop5Data.riskTreatmentPlans) {
            treatmentPlan = workshop5Data.riskTreatmentPlans;
            console.log('✅ Found treatmentPlan in workshop5Data.riskTreatmentPlans:', treatmentPlan);
        } else if (workshop1Data.currentAnalysisControlPlan) {
            treatmentPlan = workshop1Data.currentAnalysisControlPlan;
            console.log('✅ Found treatmentPlan in workshop1Data.currentAnalysisControlPlan:', treatmentPlan);
        } else if (workshop1Data.controlPlan) {
            treatmentPlan = workshop1Data.controlPlan;
            console.log('✅ Found treatmentPlan in workshop1Data.controlPlan:', treatmentPlan);
        } else if (workshop1Data.securityControlPlan) {
            treatmentPlan = workshop1Data.securityControlPlan;
            console.log('✅ Found treatmentPlan in workshop1Data.securityControlPlan:', treatmentPlan);
        } else if (workshop1Data.plan) {
            treatmentPlan = workshop1Data.plan;
            console.log('✅ Found treatmentPlan in workshop1Data.plan:', treatmentPlan);
        } else {
            // Try to find it in the root reportData or other possible locations
            console.log('🔍 Trying alternative locations for treatment plan data...');
            console.log('📊 Root reportData keys:', Object.keys(this.reportData));
            console.log('📊 analysisControls available:', !!this.reportData.analysisControls);

            if (this.reportData.analysisControls) {
                console.log('📊 analysisControls keys:', Object.keys(this.reportData.analysisControls));
                console.log('📊 analysisControls full structure:', JSON.stringify(this.reportData.analysisControls, null, 2));
            }

            // Check analysisControls first (this is where the data should be!)
            // The reportService sets: analysisControls: analysisControls ? analysisControls.planData : null
            // So reportData.analysisControls should contain the planData directly
            if (this.reportData.analysisControls?.plan) {
                treatmentPlan = this.reportData.analysisControls.plan;
                console.log('✅ Found treatmentPlan in reportData.analysisControls.plan:', treatmentPlan);
            } else if (this.reportData.analysisControls) {
                treatmentPlan = this.reportData.analysisControls;
                console.log('✅ Found treatmentPlan in reportData.analysisControls:', treatmentPlan);
            } else if (this.reportData.analysisControls?.planData?.plan) {
                treatmentPlan = this.reportData.analysisControls.planData.plan;
                console.log('✅ Found treatmentPlan in reportData.analysisControls.planData.plan:', treatmentPlan);
            } else if (this.reportData.analysisControls?.planData) {
                treatmentPlan = this.reportData.analysisControls.planData;
                console.log('✅ Found treatmentPlan in reportData.analysisControls.planData:', treatmentPlan);
            } else if (this.reportData.analysisControls?.data?.plan) {
                treatmentPlan = this.reportData.analysisControls.data.plan;
                console.log('✅ Found treatmentPlan in reportData.analysisControls.data.plan:', treatmentPlan);
            } else if (this.reportData.analysisControls?.data) {
                treatmentPlan = this.reportData.analysisControls.data;
                console.log('✅ Found treatmentPlan in reportData.analysisControls.data:', treatmentPlan);
            } else if (workshop5Data.planData) {
                treatmentPlan = workshop5Data.planData;
                console.log('✅ Found treatmentPlan in workshop5Data.planData:', treatmentPlan);
            } else if (this.reportData.planData?.plan) {
                treatmentPlan = this.reportData.planData.plan;
                console.log('✅ Found treatmentPlan in root reportData.planData.plan:', treatmentPlan);
            } else if (this.reportData.treatmentPlan) {
                treatmentPlan = this.reportData.treatmentPlan;
                console.log('✅ Found treatmentPlan in root reportData.treatmentPlan:', treatmentPlan);
            } else if (this.reportData.currentAnalysisControlPlan) {
                treatmentPlan = this.reportData.currentAnalysisControlPlan;
                console.log('✅ Found treatmentPlan in root reportData.currentAnalysisControlPlan:', treatmentPlan);
            } else if (this.reportData.controlPlan) {
                treatmentPlan = this.reportData.controlPlan;
                console.log('✅ Found treatmentPlan in root reportData.controlPlan:', treatmentPlan);
            } else {
                treatmentPlan = {};
                console.log('❌ No treatmentPlan found anywhere');
            }
        }

        console.log('🎯 FINAL treatmentPlan selected:', treatmentPlan);
        console.log('🎯 FINAL treatmentPlan type:', typeof treatmentPlan);
        console.log('🎯 FINAL treatmentPlan keys:', treatmentPlan ? Object.keys(treatmentPlan) : 'null');
        console.log('===== END TREATMENT PLAN DATA EXTRACTION =====');
        const context = workshop1Data.context || {};
        const metadata = this.reportData.metadata || {};

        console.log('[Report Generator] Workshop Data Structure:', {
            workshop1: {
                businessValues: businessValues.length,
                dreadedEvents: dreadedEvents.length,
                securityControls: securityControls.length,
                securityFramework: Object.keys(securityFramework).length,
                context: Object.keys(context).length
            },
            workshop5: {
                keys: Object.keys(workshop5Data),
                treatmentPlanDetails: !!workshop5Data.treatmentPlanDetails,
                riskTreatmentPlans: !!workshop5Data.riskTreatmentPlans,
                treatmentPlanDetailsKeys: workshop5Data.treatmentPlanDetails ? Object.keys(workshop5Data.treatmentPlanDetails) : 'none',
                riskTreatmentPlansType: Array.isArray(workshop5Data.riskTreatmentPlans) ? 'array' : typeof workshop5Data.riskTreatmentPlans
            },
            treatmentPlan: {
                keys: Object.keys(treatmentPlan),
                type: typeof treatmentPlan,
                isArray: Array.isArray(treatmentPlan),
                sample: treatmentPlan && typeof treatmentPlan === 'object' ? Object.keys(treatmentPlan).slice(0, 3) : 'not object'
            }
        });

        // DETAILED TREATMENT PLAN INSPECTION
        console.log('[Report Generator] DETAILED treatmentPlan structure:', JSON.stringify(treatmentPlan, null, 2));
        console.log('[Report Generator] Available workshop1Data keys:', Object.keys(workshop1Data));
        console.log('[Report Generator] workshop1Data.treatmentPlan:', workshop1Data.treatmentPlan);
        console.log('[Report Generator] workshop1Data.currentAnalysisControlPlan:', workshop1Data.currentAnalysisControlPlan);
        console.log('[Report Generator] workshop1Data.controlPlan:', workshop1Data.controlPlan);

        // DETAILED DATA INSPECTION
        console.log('[Report Generator] DETAILED Business Values:', businessValues.map(bv => ({
            id: bv.id,
            name: bv.name,
            description: bv.description
        })));

        console.log('[Report Generator] DETAILED Feared Events:', dreadedEvents.map(de => ({
            id: de.id,
            name: de.name,
            businessValue: de.businessValue,
            businessValueId: de.businessValueId,
            businessValueName: de.businessValueName,
            severity: de.severity,
            impacts: de.impacts,
            impact: de.impact,
            impactLabels: de.impactLabels
        })));

        // Check if business values have the expected structure
        if (businessValues.length > 0) {
            console.log('[Report Generator] Sample Business Value:', businessValues[0]);
        }

        // Check if dreaded events have the expected structure
        if (dreadedEvents.length > 0) {
            console.log('[Report Generator] Sample Feared Event:', dreadedEvents[0]);
        }

        return `
    <div class="slide" id="workshop1">
        <div class="slide-header">
            <div class="slide-title">Atelier 1 - Plan de Traitement des Risques - Synth\u00e8se</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Atelier 1 - Plan de Traitement des Risques - Synth\u00e8se</h1>
            
            <!-- Key Metrics -->
            <div class="kpi-grid">
                <div class="kpi-card">
                    <i class="fas fa-building kpi-icon"></i>
                    <div class="kpi-value">${businessValues.length}</div>
                    <div class="kpi-label">Valeurs M\u00e9tier</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-exclamation-triangle kpi-icon"></i>
                    <div class="kpi-value">${dreadedEvents.length}</div>
                    <div class="kpi-label">\u00c9v\u00e9nements Redout\u00e9s</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-shield-alt kpi-icon"></i>
                    <div class="kpi-value">${securityControls.length}</div>
                    <div class="kpi-label">Contr\u00f4les de S\u00e9curit\u00e9</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-tasks kpi-icon"></i>
                    <div class="kpi-value">${Object.keys(treatmentPlan).length}</div>
                    <div class="kpi-label">Plans de Traitement</div>
                </div>
            </div>
            
            <!-- Contexte de l'Organisation -->
            <div class="info-box info">
                <h3><i class="fas fa-building"></i> Contexte de l'Organisation</h3>
                <div style="display: grid; gap: 1.5rem;">
                    ${context.missions?.length > 0 ? `
                        <div>
                            <h4 style="margin: 0 0 0.5rem 0; color: var(--primary-color); font-weight: 700;"><i class="fas fa-tasks"></i> Missions</h4>
                            <ul style="margin: 0; padding-left: 1.5rem;">
                                ${context.missions.map(mission => `<li>${mission}</li>`).join('')}
                            </ul>
                        </div>
                    ` : ''}
                    ${context.contraintes?.length > 0 ? `
                        <div>
                            <h4 style="margin: 0 0 0.5rem 0; color: var(--primary-color); font-weight: 700;"><i class="fas fa-exclamation-triangle"></i> Contraintes</h4>
                            <ul style="margin: 0; padding-left: 1.5rem;">
                                ${context.contraintes.map(item => `<li>${item}</li>`).join('')}
                            </ul>
                        </div>
                    ` : ''}
                    ${context.hypotheses?.length > 0 ? `
                        <div>
                            <h4 style="margin: 0 0 0.5rem 0; color: var(--primary-color); font-weight: 700;"><i class="fas fa-lightbulb"></i> Hypoth\u00e8ses</h4>
                            <ul style="margin: 0; padding-left: 1.5rem;">
                                ${context.hypotheses.map(item => `<li>${item}</li>`).join('')}
                            </ul>
                        </div>
                    ` : ''}
                </div>
            </div>
            
            <!-- Contexte M\u00e9tier -->
            <div class="info-box success">
                <h3><i class="fas fa-building"></i> Contexte M\u00e9tier</h3>
                ${businessValues.length > 0 ? `
                    <div style="display: grid; gap: 2rem;">
                        ${businessValues.map(bv => `
                            <div style="background: rgba(255,255,255,0.5); border: 1px solid rgba(16, 185, 129, 0.3); border-radius: 12px; padding: 1.5rem; box-shadow: var(--shadow);">
                                <div style="display: flex; align-items: center; margin-bottom: 1.5rem;">
                                    <div style="background: rgba(16, 185, 129, 0.2); padding: 0.75rem; border-radius: 50%; margin-right: 1.5rem; display: flex; align-items: center; justify-content: center; width: 60px; height: 60px;">
                                        <i class="fas fa-building" style="color: var(--success-color); font-size: 1.5rem;"></i>
                                    </div>
                                    <div>
                                        <h4 style="margin: 0; font-weight: 700; color: var(--text-primary); font-size: 1.3rem;">${bv.name || 'Valeur m\u00e9tier non d\u00e9finie'}</h4>
                                        <p style="margin: 0.5rem 0 0 0; font-size: 1rem; color: var(--text-secondary);">${bv.description || 'Description \u00e0 compl\u00e9ter'}</p>
                                    </div>
                                </div>
                                <!-- Biens supports associ\u00e9s -->
                                <div>
                                    <h5 style="font-weight: 700; color: var(--text-primary); margin-bottom: 1rem; font-size: 1.1rem;"><i class="fas fa-server"></i> Biens supports associ\u00e9s :</h5>
                                    ${bv.supportAssets && bv.supportAssets.length > 0 ? `
                                        <div style="display: grid; gap: 1rem;">
                                            ${bv.supportAssets.map(asset => `
                                                <div style="background: rgba(59, 130, 246, 0.1); border: 1px solid rgba(59, 130, 246, 0.3); border-radius: 10px; padding: 1.25rem; box-shadow: var(--shadow);">
                                                    <div style="font-weight: 700; margin-bottom: 0.75rem; font-size: 1.1rem;">${asset.name || 'Bien support'}</div>
                                                    ${asset.description ? `<div style="font-size: 1rem; color: var(--text-secondary); margin-bottom: 1rem;">${asset.description}</div>` : ''}
                                                    <div style="display: flex; gap: 1rem; flex-wrap: wrap;">
                                                        ${asset.type ? `<span class="status-badge info"><i class="fas fa-tag"></i> Type: ${asset.type}</span>` : ''}
                                                        ${asset.location ? `<span class="status-badge secondary"><i class="fas fa-map-marker-alt"></i> Localisation: ${asset.location}</span>` : ''}
                                                        ${asset.owner ? `<span class="status-badge info"><i class="fas fa-user"></i> Propri\u00e9taire: ${asset.owner}</span>` : ''}
                                                        ${asset.vendor ? `<span class="status-badge secondary"><i class="fas fa-building"></i> Fournisseur: ${asset.vendor}</span>` : ''}
                                                    </div>
                                                </div>
                                            `).join('')}
                                        </div>
                                    ` : `
                                        <p style="font-size: 1rem; color: var(--text-secondary); font-style: italic; padding: 1rem; background: rgba(0,0,0,0.02); border-radius: 8px;">Les biens supports associ\u00e9s \u00e0 cette valeur m\u00e9tier seront identifi\u00e9s et d\u00e9taill\u00e9s dans les phases ult\u00e9rieures de l'analyse EBIOS RM.</p>
                                    `}
                                </div>
                            </div>
                        `).join('')}
                    </div>
                ` : `
                    <p style="font-size: 1rem; color: var(--text-secondary); font-style: italic; text-align: center; padding: 2rem;">Aucune valeur m\u00e9tier d\u00e9finie.</p>
                `}
            </div>
            
            <!-- Identification des Risques -->
            <div class="info-box ai">
                <h3><i class="fas fa-exclamation-triangle"></i> Identification des Risques</h3>
                <p style="font-size: 1rem; margin-bottom: 1.5rem; color: var(--text-secondary);">Liste des ${dreadedEvents.length} \u00e9v\u00e9nements redout\u00e9s identifi\u00e9s :</p>
                ${dreadedEvents.length > 0 ? `
                    <div style="overflow-x: auto; margin: 0 -1.5rem; padding: 0 1.5rem;">
                        <table style="min-width: 100%; font-size: 0.85rem; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden;">
                            <thead style="background: linear-gradient(135deg, #8b5cf6, #7c3aed); color: white;">
                                <tr>
                                    <th style="padding: 1rem; font-weight: 600;">Nom de l'\u00e9v\u00e9nement</th>
                                    <th style="padding: 1rem; font-weight: 600;">Description</th>
                                    <th style="padding: 1rem; font-weight: 600;">Valeur m\u00e9tier associ\u00e9e</th>
                                    <th style="padding: 1rem; font-weight: 600;">Impact principal</th>
                                    <th style="padding: 1rem; font-weight: 600; text-align: center;">Gravit\u00e9 initiale</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${dreadedEvents.map(de => {
            // DETAILED DEBUGGING for this specific dreaded event
            console.log(`[Report] Processing Feared Event: ${de.name}`, {
                dreadedEventId: de.id,
                businessValue: de.businessValue,
                businessValueId: de.businessValueId,
                businessValueName: de.businessValueName,
                severity: de.severity,
                impacts: de.impacts,
                impact: de.impact,
                availableBusinessValues: businessValues.map(bv => ({ id: bv.id, name: bv.name }))
            });

            // Process data exactly like the Risk Treatment component
            const linkedBv = businessValues.find(bv => String(bv.id) === String(de.businessValue));
            const businessValueName = linkedBv?.name || 'N/A';
            console.log(`[Report] Business Value Linking for ${de.name}:`, {
                searchingFor: de.businessValue,
                foundBV: linkedBv,
                resultName: businessValueName
            });

            // Get impact labels (support both old impact field and new impacts array)
            const impactArray = de.impacts || (de.impact ? [de.impact] : []);
            const impactLabels = impactArray.length > 0 ?
                impactArray.map(impactValue => {
                    // Map impact values to labels
                    const impactMap = {
                        'financial': 'Impacts financiers',
                        'operational': 'Impacts sur les missions et services de l\'organisation',
                        'legal': 'Impacts juridiques',
                        'image': 'Impacts sur l\'image et la confiance',
                        'governance': 'Impacts sur la gouvernance',
                        'human': 'Impacts humains, mat\u00e9riels ou environnementaux'
                    };
                    return impactMap[impactValue] || impactValue;
                }) : ['Non sp\u00e9cifi\u00e9'];
            console.log(`[Report] Impact Processing for ${de.name}:`, {
                rawImpacts: de.impacts,
                rawImpact: de.impact,
                impactArray: impactArray,
                impactLabels: impactLabels
            });

            const severityInfo = this.getSeverityInfo(de.severity);
            return `
                                    <tr style="border-bottom: 1px solid #e5e7eb; vertical-align: top;" onmouseover="this.style.backgroundColor='#f9fafb'" onmouseout="this.style.backgroundColor=''">
                                        <td style="padding: 1rem; font-weight: 600; color: #1f2937; font-size: 0.95rem;">${de.name || '\u00c9v\u00e9nement non nomm\u00e9'}</td>
                                        <td style="padding: 1rem; color: #374151; font-size: 0.9rem;">${de.description || 'Description \u00e0 compl\u00e9ter'}</td>
                                        <td style="padding: 1rem; color: #374151; font-size: 0.95rem;">${businessValueName}</td>
                                        <td style="padding: 1rem; color: #374151;">
                                            ${impactLabels.length > 0 && impactLabels[0] !== 'Non sp\u00e9cifi\u00e9' ? `
                                                <div style="display: flex; flex-wrap: wrap; gap: 0.5rem;">
                                                    ${impactLabels.map(label => `<span style="display: inline-flex; align-items: center; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.85rem; font-weight: 500; background-color: rgba(59, 130, 246, 0.1); color: rgb(30, 64, 175); border: 1px solid rgba(59, 130, 246, 0.2);"><i class="fas fa-bolt" style="margin-right: 0.25rem;"></i> ${label}</span>`).join('')}
                                                </div>
                                            ` : '<span style="color: #9ca3af; font-size: 0.9rem;"><i class="fas fa-question-circle"></i> Non sp\u00e9cifi\u00e9</span>'}
                                        </td>
                                        <td style="padding: 1rem; text-align: center;">
                                            <div style="display: inline-flex; flex-direction: column; align-items: center; justify-content: center; width: 4rem; height: 2.75rem; padding: 0.25rem; border-radius: 0.5rem; font-size: 0.7rem; font-weight: 600; border: 1px solid #d1d5db; ${this.getSeverityBoxStyle(severityInfo)}">
                                                <span>${severityInfo.level}</span>
                                                <span style="font-size: 0.6rem; line-height: 1.2; margin-top: 0.25rem;">${severityInfo.label}</span>
                                            </div>
                                        </td>
                                    </tr>
                                    `;
        }).join('')}
                            </tbody>
                        </table>
                    </div>
                ` : '<p style="font-size: 0.95rem; color: #6b7280; font-style: italic; text-align: center; padding: 2rem; background: rgba(0,0,0,0.02); border-radius: 12px;">Aucun \u00e9v\u00e9nement redout\u00e9 d\u00e9fini.</p>'}
            </div>
            
            <!-- Socle de S\u00e9curit\u00e9 -->
            <div class="info-box info">
                <h3><i class="fas fa-shield-alt"></i> Socle de S\u00e9curit\u00e9</h3>
                ${this.generateSecurityFrameworkSummary(securityFramework, businessValues, dreadedEvents)}
            </div>
            
            <!-- Security Controls Table -->
            <div class="info-box success">
                <h3><i class="fas fa-shield-alt"></i> Security Controls</h3>
                ${this.generateEnhancedSecurityControlsTable(treatmentPlan, securityFramework, securityControls, dreadedEvents, businessValues)}
            </div>
        </div>

    </div>`;
    }

    /**
     * Generate Workshop 2 section
     * @param {Object} workshop2Data - Workshop 2 data
     * @returns {Promise<string>} Workshop 2 HTML
     */
    async generateWorkshop2Section(workshop2Data) {
        // Extract data with multiple fallback property names for compatibility
        const riskSources = workshop2Data?.riskSources || workshop2Data?.sourcesRisque || workshop2Data?.sources || [];
        const threatCategories = workshop2Data?.threatCategories || workshop2Data?.categories || [];
        const objectives = workshop2Data?.objectives || workshop2Data?.objectifsVises || workshop2Data?.objectifs || [];
        const couplesSROV = workshop2Data?.couplesSROV || workshop2Data?.couples || workshop2Data?.mappings || [];

        return `
    <div class="slide" id="workshop2">
        <div class="slide-header">
            <div class="slide-title">Atelier 2 - Sources de Risque et \u00c9cosyst\u00e8me</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Atelier 2 - Sources de Risque et \u00c9cosyst\u00e8me</h1>
            
            <!-- Vue d'ensemble -->
            <div class="info-box info">
                <h3><i class="fas fa-chart-pie"></i> Vue d'ensemble des sources de risque</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem; font-size: 1rem; margin-top: 1rem;">
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px; min-height: 80px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--info-color); margin-bottom: 0.5rem;">${riskSources.length}</div>
                        <div style="text-align: center; line-height: 1.4; word-wrap: break-word;">sources de risque identifi\u00e9es</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px; min-height: 80px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--primary-color); margin-bottom: 0.5rem;">${this.getUniqueCategories(riskSources).length}</div>
                        <div style="text-align: center; line-height: 1.4; word-wrap: break-word;">cat\u00e9gories de menaces</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px; min-height: 80px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--warning-color); margin-bottom: 0.5rem;">${objectives.length}</div>
                        <div style="text-align: center; line-height: 1.4; word-wrap: break-word;">objectifs vis\u00e9s</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px; min-height: 80px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--danger-color); margin-bottom: 0.5rem;">${couplesSROV.length}</div>
                        <div style="text-align: center; line-height: 1.4; word-wrap: break-word;">couples SR/OV \u00e9valu\u00e9s</div>
                    </div>
                </div>
            </div>
            
            <!-- Couples SR/OV Evaluation Table (from Evaluate RS/TO couples page) -->
            ${this.generateEnhancedCouplesSROVTable(couplesSROV, riskSources, objectives)}
            
            <!-- Sources de Risque - Exact Table Copy -->
            ${riskSources.length > 0 ? `
                <div class="info-box info">
                    <h3><i class="fas fa-shield-alt"></i> Couples Sources de Risque / Objectifs Vis\u00e9s</h3>
                    <p style="margin-bottom: 1rem; font-style: italic; color: var(--text-secondary);">Cette table repr\u00e9sente exactement les m\u00eames donn\u00e9es que vous voyez dans l'interface</p>
                    <div class="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Source de risque</th>
                                    <th>Cat\u00e9gorie</th>
                                    <th>Objectifs Vis\u00e9s</th>
                                    <th>Ressources</th>
                                    <th>Activit\u00e9</th>
                                    <th>Motivation</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${riskSources.map(rs => `
                                <tr>
                                    <td>
                                        <div style="font-weight: 600; color: var(--text-primary);">${rs.name || 'Source non identifi\u00e9e'}</div>
                                        ${rs.description ? `<div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 0.25rem;">${rs.description}</div>` : ''}
                                    </td>
                                    <td>${rs.category || 'Cat\u00e9gorie \u00e0 d\u00e9finir'}</td>
                                    <td>
                                        ${rs.objectifVise ? rs.objectifVise : rs.objectives?.length > 0 ? `
                                            <ul style="margin: 0; padding-left: 1rem; font-size: 0.9rem;">
                                                ${rs.objectives.map(obj => `<li>${obj}</li>`).join('')}
                                            </ul>
                                        ` : rs.targetedObjectives?.length > 0 ? `
                                            <ul style="margin: 0; padding-left: 1rem; font-size: 0.9rem;">
                                                ${rs.targetedObjectives.map(obj => `<li>${obj}</li>`).join('')}
                                            </ul>
                                        ` : 'Objectifs \u00e0 analyser'}
                                    </td>
                                    <td><span class="status-badge ${this.getResourcesClass(rs.ressources)}">${this.getDisplayValue(rs.ressources) || 'Non \u00e9valu\u00e9'}</span></td>
                                    <td><span class="status-badge ${this.getActivityClass(rs.activite)}">${this.getDisplayValue(rs.activite) || 'Non \u00e9valu\u00e9'}</span></td>
                                    <td><span class="status-badge ${this.getMotivationClass(rs.motivation)}">${this.getDisplayValue(rs.motivation) || 'Non \u00e9valu\u00e9'}</span></td>
                                   
                                </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </div>
                </div>
            ` : ''}

            <!-- Visualisation Radar des Couples SR/OV -->
            ${couplesSROV.length > 0 ? await this.generateRadarVisualizationFromCapture(couplesSROV) : ''}

            <!-- Cat\u00e9gories de menaces d\u00e9taill\u00e9es -->
            ${threatCategories.length > 0 ? `
                <div class="info-box success">
                    <h3><i class="fas fa-tags"></i> Cat\u00e9gories de menaces identifi\u00e9es</h3>
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1rem;">
                        ${threatCategories.map(category => {
            const relatedSources = riskSources.filter(rs => rs.category === category.name);
            return `
                            <div style="background: rgba(16, 185, 129, 0.05); border: 1px solid rgba(16, 185, 129, 0.2); border-radius: 8px; padding: 1.5rem;">
                                <h4 style="margin: 0 0 0.75rem 0; color: var(--success-color);">${category.name || 'Cat\u00e9gorie'}</h4>
                                <p style="margin: 0 0 1rem 0; font-size: 0.9rem; color: var(--text-secondary);">${category.description || 'Description \u00e0 compl\u00e9ter'}</p>
                                <div style="margin-bottom: 1rem;">
                                    ${category.threatLevel ? `<span class="status-badge ${this.getThreatLevelClass(category.threatLevel)}" style="margin-right: 0.5rem;">${category.threatLevel}</span>` : ''}
                                    <span style="font-size: 0.8rem; color: var(--text-secondary);">${relatedSources.length} source${relatedSources.length > 1 ? 's' : ''} associ\u00e9e${relatedSources.length > 1 ? 's' : ''}</span>
                                </div>
                                ${relatedSources.length > 0 ? `
                                    <div style="font-size: 0.8rem;">
                                        <strong>Sources associ\u00e9es :</strong>
                                        <ul style="margin: 0.5rem 0 0 1rem; padding: 0;">
                                            ${relatedSources.map(rs => `<li>${rs.name}</li>`).join('')}
                                        </ul>
                                    </div>
                                ` : ''}
                            </div>
                            `;
        }).join('')}
                    </div>
                </div>
            ` : ''}

            <!-- Vision par objectif vis\u00e9 -->
            ${objectives.length > 0 ? `
                <h2>Vision par Objectif Vis\u00e9</h2>
                <div class="info-box warning">
                    <h3><i class="fas fa-bullseye"></i> Analyse d\u00e9taill\u00e9e par objectif</h3>
                    <div style="display: grid; gap: 1.5rem;">
                        ${objectives.map(objective => {
            const targetingSources = riskSources.filter(rs =>
                rs.objectives?.includes(objective.name) ||
                rs.objectives?.includes(objective.id) ||
                rs.targetedObjectives?.includes(objective.name)
            );
            return `
                            <div style="background: rgba(245, 158, 11, 0.05); border: 1px solid rgba(245, 158, 11, 0.2); border-radius: 8px; padding: 1.5rem;">
                                <div style="display: grid; grid-template-columns: 1fr auto; gap: 1rem; align-items: start; margin-bottom: 1rem;">
                                    <div>
                                        <h4 style="margin: 0 0 0.5rem 0; color: var(--warning-color);">${objective.name || 'Objectif'}</h4>
                                        <p style="margin: 0; font-size: 0.9rem; color: var(--text-secondary);">${objective.description || 'Description \u00e0 compl\u00e9ter'}</p>
                                        ${objective.category ? `<div style="margin-top: 0.5rem; font-size: 0.8rem; color: var(--text-secondary);"><strong>Cat\u00e9gorie:</strong> ${objective.category}</div>` : ''}
                                        ${objective.impact ? `<div style="margin-top: 0.25rem; font-size: 0.8rem; color: var(--text-secondary);"><strong>Impact:</strong> ${objective.impact}</div>` : ''}
                                    </div>
                                    <div style="text-align: center;">
                                        <div style="font-size: 1.5rem; font-weight: bold; color: var(--warning-color);">${targetingSources.length}</div>
                                        <div style="font-size: 0.8rem; color: var(--text-secondary);">source${targetingSources.length > 1 ? 's' : ''} de risque</div>
                                    </div>
                                </div>
                                ${targetingSources.length > 0 ? `
                                    <div>
                                        <h5 style="margin: 0 0 0.75rem 0; color: var(--warning-color);">Sources de risque ciblant cet objectif :</h5>
                                        <div style="display: grid; gap: 0.75rem;">
                                            ${targetingSources.map(rs => `
                                                <div style="background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.3); border-radius: 6px; padding: 1rem; display: grid; grid-template-columns: 1fr auto; gap: 1rem; align-items: center;">
                                                    <div>
                                                        <div style="font-weight: 600; margin-bottom: 0.25rem;">${rs.name || 'Source de risque'}</div>
                                                        <div style="font-size: 0.8rem; color: var(--text-secondary);">${rs.category || 'Cat\u00e9gorie non d\u00e9finie'}</div>
                                                        ${rs.motivation ? `<div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 0.25rem;"><strong>Motivation:</strong> ${rs.motivation}</div>` : ''}
                                                    </div>
                                                    <div>
                                                        <span class="status-badge ${this.getThreatLevelClass(rs.threatLevel)}">${rs.threatLevel || 'Non \u00e9valu\u00e9'}</span>
                                                    </div>
                                                </div>
                                            `).join('')}
                                        </div>
                                    </div>
                                ` : '<p style="margin: 0; font-style: italic; color: var(--text-secondary);">Aucune source de risque identifi\u00e9e pour cet objectif.</p>'}
                            </div>
                            `;
        }).join('')}
                    </div>
                </div>
            ` : ''}
            
            <!-- Vision par source de risque - Visualisation -->
            ${riskSources.length > 0 && objectives.length > 0 ? `
                <h2>Vision par Source de Risque - Visualisation</h2>
                <div class="info-box error">
                    <h3><i class="fas fa-project-diagram"></i> Cartographie des relations Sources de Risque → Objectifs</h3>
                    <!-- Chart-like visualization -->
                    <div style="background: white; border-radius: 8px; padding: 2rem; margin: 1rem 0;">
                        <div style="display: grid; grid-template-columns: 1fr 3fr; gap: 2rem; min-height: 400px;">
                            <!-- Sources de risque (left side) -->
                            <div>
                                <h4 style="text-align: center; margin-bottom: 1rem; color: var(--error-color);">Sources de Risque</h4>
                                <div style="display: flex; flex-direction: column; gap: 1rem; height: 100%;">
                                    ${riskSources.map((rs, index) => {
            const targetedObjectives = objectives.filter(obj =>
                rs.objectives?.includes(obj.name) ||
                rs.objectives?.includes(obj.id) ||
                rs.targetedObjectives?.includes(obj.name)
            );
            return `
                                        <div style="
                                            background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05));
                                            border: 2px solid rgba(239, 68, 68, 0.3);
                                            border-radius: 8px;
                                            padding: 1rem;
                                            position: relative;
                                            flex: 1;
                                            display: flex;
                                            flex-direction: column;
                                            justify-content: center;
                                        ">
                                            <div style="font-weight: 600; font-size: 0.9rem; margin-bottom: 0.5rem;">${rs.name || 'Source ' + (index + 1)}</div>
                                            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.5rem;">${rs.category || 'Cat\u00e9gorie non d\u00e9finie'}</div>
                                            <div style="display: flex; justify-content: space-between; align-items: center;">
                                                <span class="status-badge ${this.getThreatLevelClass(rs.threatLevel)}" style="font-size: 0.7rem;">${rs.threatLevel || 'Non \u00e9valu\u00e9'}</span>
                                                <div style="font-size: 0.8rem; font-weight: bold; color: var(--error-color);">${targetedObjectives.length} obj.</div>
                                            </div>
                                        </div>
                                        `;
        }).join('')}
                                </div>
                            </div>
                            <!-- Visualization area (right side) -->
                            <div style="position: relative;">
                                <h4 style="text-align: center; margin-bottom: 1rem; color: var(--warning-color);">Objectifs Vis\u00e9s</h4>
                                <!-- Objectives grid -->
                                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 1rem; height: calc(100% - 3rem);">
                                    ${objectives.map((obj, objIndex) => {
            const targetingSources = riskSources.filter(rs =>
                rs.objectives?.includes(obj.name) ||
                rs.objectives?.includes(obj.id) ||
                rs.targetedObjectives?.includes(obj.name)
            );
            const intensity = targetingSources.length;
            const maxIntensity = Math.max(...objectives.map(o =>
                riskSources.filter(rs =>
                    rs.objectives?.includes(o.name) ||
                    rs.objectives?.includes(o.id) ||
                    rs.targetedObjectives?.includes(o.name)
                ).length
            ));
            const opacity = maxIntensity > 0 ? (intensity / maxIntensity) * 0.8 + 0.2 : 0.2;
            return `
                                        <div style="
                                            background: linear-gradient(135deg, rgba(245, 158, 11, ${opacity}), rgba(245, 158, 11, ${opacity * 0.5}));
                                            border: 2px solid rgba(245, 158, 11, ${opacity + 0.2});
                                            border-radius: 8px;
                                            padding: 1rem;
                                            display: flex;
                                            flex-direction: column;
                                            justify-content: center;
                                            text-align: center;
                                            position: relative;
                                        ">
                                            <div style="font-weight: 600; font-size: 0.9rem; margin-bottom: 0.5rem;">${obj.name || 'Objectif ' + (objIndex + 1)}</div>
                                            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.5rem;">${obj.category || 'Cat\u00e9gorie non d\u00e9finie'}</div>
                                            <div style="font-size: 1.2rem; font-weight: bold; color: var(--warning-color);">${intensity}</div>
                                            <div style="font-size: 0.7rem; color: var(--text-secondary);">source${intensity > 1 ? 's' : ''}</div>
                                            <!-- Threat level indicator -->
                                            ${intensity > 0 ? `
                                                <div style="
                                                    position: absolute;
                                                    top: -8px;
                                                    right: -8px;
                                                    width: 16px;
                                                    height: 16px;
                                                    border-radius: 50%;
                                                    background: ${intensity >= 3 ? '#ef4444' : intensity >= 2 ? '#f59e0b' : '#10b981'};
                                                    border: 2px solid white;
                                                "></div>
                                            ` : ''}
                                        </div>
                                        `;
        }).join('')}
                                </div>
                                <!-- Legend -->
                                <div style="position: absolute; bottom: -2rem; left: 0; right: 0; display: flex; justify-content: center; gap: 1rem; font-size: 0.8rem;">
                                    <div style="display: flex; align-items: center; gap: 0.5rem;">
                                        <div style="width: 12px; height: 12px; border-radius: 50%; background: #10b981;"></div>
                                        <span>Faible menace (1 source)</span>
                                    </div>
                                    <div style="display: flex; align-items: center; gap: 0.5rem;">
                                        <div style="width: 12px; height: 12px; border-radius: 50%; background: #f59e0b;"></div>
                                        <span>Menace mod\u00e9r\u00e9e (2 sources)</span>
                                    </div>
                                    <div style="display: flex; align-items: center; gap: 0.5rem;">
                                        <div style="width: 12px; height: 12px; border-radius: 50%; background: #ef4444;"></div>
                                        <span>Menace \u00e9lev\u00e9e (3+ sources)</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Summary statistics -->
                    <div style="margin-top: 2rem; display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; font-size: 0.9rem;">
                        <div style="text-align: center; padding: 1rem; background: rgba(239, 68, 68, 0.1); border-radius: 6px;">
                            <div style="font-size: 1.5rem; font-weight: bold; color: var(--error-color);">${riskSources.length}</div>
                            <div>Sources de risque</div>
                        </div>
                        <div style="text-align: center; padding: 1rem; background: rgba(245, 158, 11, 0.1); border-radius: 6px;">
                            <div style="font-size: 1.5rem; font-weight: bold; color: var(--warning-color);">${objectives.length}</div>
                            <div>Objectifs identifi\u00e9s</div>
                        </div>
                        <div style="text-align: center; padding: 1rem; background: rgba(16, 185, 129, 0.1); border-radius: 6px;">
                            <div style="font-size: 1.5rem; font-weight: bold; color: var(--success-color);">${objectives.filter(obj => {
            const targetingSources = riskSources.filter(rs =>
                rs.objectives?.includes(obj.name) ||
                rs.objectives?.includes(obj.id) ||
                rs.targetedObjectives?.includes(obj.name)
            );
            return targetingSources.length >= 2;
        }).length}</div>
                            <div>Objectifs multi-menaces</div>
                        </div>
                        <div style="text-align: center; padding: 1rem; background: rgba(139, 92, 246, 0.1); border-radius: 6px;">
                            <div style="font-size: 1.5rem; font-weight: bold; color: #8b5cf6;">${riskSources.filter(rs => (rs.objectives?.length || 0) >= 2).length}</div>
                            <div>Sources multi-objectifs</div>
                        </div>
                    </div>
                </div>
            ` : ''}

        </div>

    </div>`;
    }

    /**
     * Generate Workshop 3 section
     * @param {Object} workshop3Data - Workshop 3 data
     * @returns {string} Workshop 3 HTML
     */
    async generateWorkshop3Section(workshop3Data) {
        const strategicScenarios = workshop3Data.strategicScenarios || [];
        const ecosystemMeasures = workshop3Data.ecosystemMeasures || [];
        const stakeholders = workshop3Data.stakeholders || this.reportData.workshops?.workshop2?.stakeholders || [];

        return `
    <div class="slide" id="workshop3">
        <div class="slide-header">
            <div class="slide-title">Atelier 3 - Sc\u00e9narios Strat\u00e9giques</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Atelier 3 - Sc\u00e9narios Strat\u00e9giques</h1>
            
            <!-- Analyse des Parties Prenantes -->
            <h2>Analyse des Parties Prenantes</h2>
            ${stakeholders.length > 0 ? `
                <!-- Synth\u00e8se des parties prenantes -->
                <div class="info-box ai">
                    <h3><i class="fas fa-users"></i> Synth\u00e8se de l'analyse</h3>
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; font-size: 0.9rem;">
                        <div><strong>${stakeholders.filter(sh => sh && (
            sh.threatLevel === 'high' || sh.threatLevel === '\u00e9lev\u00e9' ||
            sh.threatLevel === 'High' || sh.threatLevel === '\u00c9lev\u00e9' ||
            sh.menace === 'high' || sh.menace === '\u00e9lev\u00e9'
        )).length}</strong> parties prenantes \u00e0 risque \u00e9lev\u00e9</div>
                        <div><strong>${stakeholders.filter(sh => sh && (
            sh.retained === true || sh.retained === 'true' || sh.retained === 'Oui' ||
            sh.retenu === true || sh.retenu === 'true' || sh.retenu === 'Oui'
        )).length}</strong> parties prenantes retenues</div>
                        <div><strong>${stakeholders.filter(sh => sh && (
            sh.dependency === 'high' || sh.dependency === '\u00e9lev\u00e9' ||
            sh.dependency === 'High' || sh.dependency === '\u00c9lev\u00e9' ||
            sh.dependance === 'high' || sh.dependance === '\u00e9lev\u00e9'
        )).length}</strong> d\u00e9pendances critiques</div>
                        <div><strong>${stakeholders.filter(sh => sh && (
            sh.penetration === 'high' || sh.penetration === '\u00e9lev\u00e9' ||
            sh.penetration === 'High' || sh.penetration === '\u00c9lev\u00e9' ||
            sh.penetration === '\u00c9lev\u00e9e' || sh.p\u00e9n\u00e9tration === '\u00e9lev\u00e9'
        )).length}</strong> capacit\u00e9s de p\u00e9n\u00e9tration \u00e9lev\u00e9es</div>
                    </div>
                </div>
                <div class="table-container">
                    <table>
                        <thead style="background: linear-gradient(135deg, #8b5cf6, #7c3aed); color: white;">
                            <tr>
                                <th style="padding: 1rem; font-weight: 600;">Partie Prenante</th>
                                <th style="padding: 1rem; font-weight: 600;">Cat\u00e9gorie</th>
                                <th style="padding: 1rem; font-weight: 600;">Type/Rang</th>
                                <th style="padding: 1rem; font-weight: 600; text-align: center;">Niveau de Menace</th>
                                <th style="padding: 1rem; font-weight: 600; text-align: center;">D\u00e9pendance</th>
                                <th style="padding: 1rem; font-weight: 600; text-align: center;">P\u00e9n\u00e9tration</th>
                                <th style="padding: 1rem; font-weight: 600; text-align: center;">Maturit\u00e9 Cyber</th>
                                <th style="padding: 1rem; font-weight: 600; text-align: center;">Niveau de Confiance</th>
                                <th style="padding: 1rem; font-weight: 600; text-align: center;">Retenu</th>
                                <th style="padding: 1rem; font-weight: 600; text-align: center;">Zone de Danger</th>
                                <th style="padding: 1rem; font-weight: 600;">Expression du risque r\u00e9siduel</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${stakeholders.map(sh => {
            // Ensure all values are properly formatted for ESLint
            const threatLevel = sh.threatLevel || 'Faible';
            const dependency = sh.dependency || 'Faible';
            const penetration = sh.penetration || 'Faible';
            const cyberMaturity = sh.cyberMaturity || 'Moyen';
            const trustLevel = sh.trustLevel || '\u00c9lev\u00e9';
            const residualExpr = sh.residualRiskExpression || '';
            return `
                            <tr style="border-bottom: 1px solid var(--light-border);">
                                <td style="padding: 0.75rem; font-weight: 600; color: var(--text-primary);">
                                    <div>${sh.name || 'Partie prenante'}</div>
                                    ${sh.description ? `<div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 0.25rem;">${sh.description}</div>` : ''}
                                </td>
                                <td style="padding: 0.75rem; color: var(--text-secondary);">${sh.category || 'Cat\u00e9gorie \u00e0 d\u00e9finir'}</td>
                                <td style="padding: 0.75rem; color: var(--text-secondary);">${sh.type || sh.rank || 'Type \u00e0 pr\u00e9ciser'}</td>
                                <td style="padding: 0.75rem; text-align: center;"><span class="status-badge ${this.getThreatLevelClass(threatLevel)}" style="font-size: 0.75rem;">${threatLevel}</span></td>
                                <td style="padding: 0.75rem; text-align: center;"><span class="status-badge ${this.getDependencyClass(dependency)}" style="font-size: 0.75rem;">${dependency}</span></td>
                                <td style="padding: 0.75rem; text-align: center;"><span class="status-badge ${this.getPenetrationClass(penetration)}" style="font-size: 0.75rem;">${penetration}</span></td>
                                <td style="padding: 0.75rem; text-align: center;"><span class="status-badge ${this.getMaturityClass(cyberMaturity)}" style="font-size: 0.75rem;">${cyberMaturity}</span></td>
                                <td style="padding: 0.75rem; text-align: center;"><span class="status-badge ${this.getTrustLevelClass(trustLevel)}" style="font-size: 0.75rem;">${trustLevel}</span></td>
                                <td style="padding: 0.75rem; text-align: center;"><span class="status-badge ${sh.retained ? 'completed' : 'warning'}" style="font-size: 0.75rem;">${sh.retained ? 'Oui' : 'Non'}</span></td>
                                <td style="padding: 0.75rem; text-align: center;"><span class="status-badge ${this.getDangerZoneClass(threatLevel, dependency, penetration)}" style="font-size: 0.75rem;">${this.calculateDangerZone(threatLevel, dependency, penetration)}</span></td>
                                <td style="padding: 0.75rem; font-size: 0.85rem; color: var(--text-secondary); max-width: 200px;">${residualExpr || '<span style="color: var(--text-secondary); font-style: italic;">Non renseign\u00e9e</span>'}</td>
                            </tr>
                                `;
        }).join('')}
                        </tbody>
                    </table>
                </div>
            ` : '<div class="info-box warning">Aucune partie prenante d\u00e9finie. Les parties prenantes sont analys\u00e9es dans le cadre de l\'\u00e9cosyst\u00e8me de l\'Atelier 2.</div>'}
            
            <!-- Vue d'ensemble -->
            <div class="info-box info">
                <h3><i class="fas fa-chart-pie"></i> Vue d'ensemble</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem; font-size: 1rem;">
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--info-color);">${strategicScenarios.length}</div>
                        <div style="text-align: center;">sc\u00e9narios strat\u00e9giques</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--primary-color);">${ecosystemMeasures.length}</div>
                        <div style="text-align: center;">mesures de s\u00e9curit\u00e9</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--warning-color);">${ecosystemMeasures.filter(m => m.priority === 'high').length}</div>
                        <div style="text-align: center;">mesures prioritaires</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--success-color);">${ecosystemMeasures.filter(m => m.implementation === 'implemented').length}</div>
                        <div style="text-align: center;">mesures impl\u00e9ment\u00e9es</div>
                    </div>
                </div>
            </div>
            
            <!-- Mesures de S\u00e9curit\u00e9 \u00c9cosyst\u00e8me -->
            ${ecosystemMeasures.length > 0 ? `
            <h2>Mesures de S\u00e9curit\u00e9 de l'\u00c9cosyst\u00e8me</h2>
            <div class="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Titre</th>
                            <th>Description</th>
                            <th>Cat\u00e9gorie</th>
                            <th>Priorit\u00e9</th>
                            <th>Impl\u00e9mentation</th>
                            <th>Efficacit\u00e9</th>
                            <th>Chemins d'Attaque</th>
                            <th>Impacts</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${ecosystemMeasures.map(measure => `
                        <tr>
                            <td style="font-weight: 600; color: var(--text-primary);">${measure.title || 'Mesure sans titre'}</td>
                            <td style="font-size: 0.9rem; max-width: 300px;">${measure.description || 'Description \u00e0 compl\u00e9ter'}</td>
                            <td><span class="status-badge ${this.getCategoryClass(measure.category)}">${this.formatCategory(measure.category)}</span></td>
                            <td><span class="status-badge ${this.getPriorityClass(measure.priority)}">${this.formatPriority(measure.priority)}</span></td>
                            <td><span class="status-badge ${this.getImplementationClass(measure.implementation)}">${this.formatImplementation(measure.implementation)}</span></td>
                            <td style="text-align: center;">
                                <div style="display: flex; align-items: center; justify-content: center;">
                                    ${this.generateStarRating(measure.effectiveness || 0)}
                                    <span style="margin-left: 0.5rem; font-size: 0.8rem; color: var(--text-secondary);">(${measure.effectiveness || 0}/5)</span>
                                </div>
                            </td>
                            <td style="font-size: 0.8rem;">
                                ${measure.attackPaths && measure.attackPaths.length > 0 ?
                `<span class="status-badge info">${measure.attackPaths.length} chemin(s)</span>` :
                '<span style="color: var(--text-secondary); font-style: italic;">Aucun</span>'
            }
                            </td>
                            <td style="font-size: 0.8rem;">
                                ${measure.benefits ? measure.benefits.map(benefit => `
                                    <div style="margin-bottom: 0.25rem;">
                                        <span class="status-badge ${this.getRiskReductionClass(benefit.riskReduction)}" style="font-size: 0.7rem; margin-right: 0.25rem;">${benefit.riskReduction}</span>
                                        <span>${benefit.impactArea}</span>
                                    </div>
                                `).join('') : 'Non d\u00e9finis'}
                            </td>
                        </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
            
            <!-- Radar des Parties Prenantes -->
            ${await this.generateStakeholderRadarVisualizationFromCapture(stakeholders)}
            ` : ''}
            
            <!-- Sc\u00e9narios Strat\u00e9giques -->
            ${strategicScenarios.length > 0 ? `
            <h2>Sc\u00e9narios Strat\u00e9giques</h2>
            <div class="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Source de Risque</th>
                            <th>Objectif Vis\u00e9</th>
                            <th>\u00c9v\u00e9nement Redout\u00e9</th>
                            <th>Valeur M\u00e9tier</th>
                            <th>Mesures de Contr\u00f4le</th>
                            <th>Parties Prenantes</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${strategicScenarios.map((ss, index) => `
                        <tr>
                            <td><strong>s${String(index + 1).padStart(2, '0')}</strong></td>
                            <td>${ss.sourceRiskName || 'Source \u00e0 identifier'}</td>
                            <td>${ss.objectifVise || 'Objectif \u00e0 pr\u00e9ciser'}</td>
                            <td>${ss.dreadedEventName || '\u00c9v\u00e9nement \u00e0 d\u00e9finir'}</td>
                            <td>${ss.businessValueName || 'Valeur m\u00e9tier \u00e0 associer'}</td>
                            <td>${ss.controls?.length || 0} mesure(s)</td>
                            <td>${ss.selectedStakeholders?.length || 0} partie(s) prenante(s)</td>
                        </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
            ` : '<div class="info-box warning">Aucun sc\u00e9nario strat\u00e9gique d\u00e9fini.</div>'}
        </div>

    </div>`;
    }

    /**
     * Generate Workshop 4 section
     * @param {Object} workshop4Data - Workshop 4 data
     * @returns {string} Workshop 4 HTML
     */
    generateWorkshop4Section(workshop4Data) {
        const operationalScenarios = workshop4Data.operationalScenarios || [];

        return `
    <div class="slide" id="workshop4">
        <div class="slide-header">
            <div class="slide-title">Atelier 4 - Sc\u00e9narios Op\u00e9rationnels</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Atelier 4 - Sc\u00e9narios Op\u00e9rationnels</h1>
            
            <!-- Vue d'ensemble -->
            <div class="info-box info">
                <h3><i class="fas fa-chart-pie"></i> Vue d'ensemble</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem; font-size: 1rem;">
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--error-color);">${operationalScenarios.length}</div>
                        <div style="text-align: center;">sc\u00e9narios op\u00e9rationnels</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--warning-color);">${operationalScenarios.filter(s => s.severity === '\u00c9lev\u00e9').length}</div>
                        <div style="text-align: center;">s\u00e9v\u00e9rit\u00e9 \u00e9lev\u00e9e</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--primary-color);">${operationalScenarios.reduce((sum, s) => sum + (s.operationalSteps ? s.operationalSteps.length : 0), 0)}</div>
                        <div style="text-align: center;">\u00e9tapes totales</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--success-color);">${operationalScenarios.filter(s => s.detectionDifficulty === '\u00c9lev\u00e9').length}</div>
                        <div style="text-align: center;">d\u00e9tection difficile</div>
                    </div>
                </div>
            </div>
            
            <!-- Sc\u00e9narios Op\u00e9rationnels -->
            ${operationalScenarios.length > 0 ? `
            <h2>Sc\u00e9narios Op\u00e9rationnels</h2>
            ${operationalScenarios.map((scenario, index) => `
            <div class="info-box ai" style="margin-bottom: 2rem;">
                <h3><i class="fas fa-shield-alt"></i> ${scenario.name || `Sc\u00e9nario ${index + 1}`}</h3>
                <div style="margin-bottom: 1rem;">
                    <p style="font-size: 0.95rem; line-height: 1.5; margin-bottom: 1rem;">${scenario.description || 'Description \u00e0 compl\u00e9ter'}</p>
                    
                    <!-- M\u00e9tadonn\u00e9es du sc\u00e9nario -->
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 1rem; margin-bottom: 1.5rem;">
                        <div style="text-align: center; padding: 0.75rem; background: rgba(255,255,255,0.7); border-radius: 8px;">
                            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.25rem;">R\u00e9f\u00e9rence</div>
                            <div style="font-weight: 600;">${scenario.attackPathReference || 'N/A'}</div>
                        </div>
                        <div style="text-align: center; padding: 0.75rem; background: rgba(255,255,255,0.7); border-radius: 8px;">
                            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.25rem;">S\u00e9v\u00e9rit\u00e9</div>
                            <span class="status-badge ${this.getSeverityClass(scenario.severity)}">${scenario.severity || 'N/A'}</span>
                        </div>
                        <div style="text-align: center; padding: 0.75rem; background: rgba(255,255,255,0.7); border-radius: 8px;">
                            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.25rem;">Vraisemblance</div>
                            <span class="status-badge ${this.getLikelihoodClass(scenario.likelihood)}">${scenario.likelihood || 'N/A'}</span>
                        </div>
                        <div style="text-align: center; padding: 0.75rem; background: rgba(255,255,255,0.7); border-radius: 8px;">
                            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.25rem;">D\u00e9tection</div>
                            <span class="status-badge ${this.getDifficultyClass(scenario.detectionDifficulty)}">${scenario.detectionDifficulty || 'N/A'}</span>
                        </div>
                        <div style="text-align: center; padding: 0.75rem; background: rgba(255,255,255,0.7); border-radius: 8px;">
                            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.25rem;">Comp\u00e9tences</div>
                            <span class="status-badge ${this.getSkillsClass(scenario.requiredSkills)}">${scenario.requiredSkills || 'N/A'}</span>
                        </div>
                        <div style="text-align: center; padding: 0.75rem; background: rgba(255,255,255,0.7); border-radius: 8px;">
                            <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.25rem;">Dur\u00e9e</div>
                            <div style="font-weight: 600;">${scenario.timeline || 'N/A'}</div>
                        </div>
                    </div>
                    
                    <!-- \u00c9tapes op\u00e9rationnelles -->
                    ${scenario.operationalSteps && scenario.operationalSteps.length > 0 ? `
                    <h4 style="margin-bottom: 1rem; color: var(--text-primary);">\u00c9tapes Op\u00e9rationnelles</h4>
                    <div class="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th style="width: 8%;">#</th>
                                    <th style="width: 15%;">Phase</th>
                                    <th style="width: 20%;">Technique</th>
                                    <th style="width: 25%;">Description</th>
                                    <th style="width: 12%;">Outils</th>
                                    <th style="width: 10%;">Dur\u00e9e</th>
                                    <th style="width: 10%;">Difficult\u00e9</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${scenario.operationalSteps.map(step => `
                                <tr>
                                    <td style="text-align: center; font-weight: 600;">${step.stepNumber}</td>
                                    <td><span class="status-badge ${this.getPhaseClass(step.phase)}" style="font-size: 0.75rem;">${step.phase}</span></td>
                                    <td style="font-weight: 500;">${step.technique || 'N/A'}</td>
                                    <td style="font-size: 0.9rem;">${step.description || 'Description \u00e0 compl\u00e9ter'}</td>
                                    <td style="font-size: 0.8rem;">
                                        ${step.tools && step.tools.length > 0 ?
                step.tools.map(tool => `<span class="status-badge info" style="font-size: 0.7rem; margin: 0.1rem;">${tool}</span>`).join('') :
                '<span style="color: var(--text-secondary); font-style: italic;">Aucun</span>'
            }
                                    </td>
                                    <td style="text-align: center; font-size: 0.8rem;">${step.duration || 'N/A'}</td>
                                    <td style="text-align: center;">
                                        <span class="status-badge ${this.getDifficultyLevelClass(step.difficulty)}" style="font-size: 0.7rem;">${step.difficulty || 'N/A'}</span>
                                    </td>
                                </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </div>
                    ` : '<p style="font-style: italic; color: var(--text-secondary);">Aucune \u00e9tape op\u00e9rationnelle d\u00e9finie.</p>'}
                    
                    <!-- Synth\u00e8se professionnelle -->
                    ${scenario.professionalSynthesis ? `
                    <h4 style="margin: 1.5rem 0 1rem 0; color: var(--text-primary);">Synth\u00e8se Professionnelle</h4>
                    <div style="background: rgba(255,255,255,0.7); padding: 1rem; border-radius: 8px; border-left: 4px solid var(--primary-color);">
                        <p style="font-size: 0.9rem; line-height: 1.6; margin: 0;">${scenario.professionalSynthesis}</p>
                    </div>
                    ` : ''}
                </div>
            </div>
            `).join('')}
            ` : '<div class="info-box warning">Aucun sc\u00e9nario op\u00e9rationnel d\u00e9fini.</div>'}
        </div>

    </div>`;
    }

    /**
     * Generate Workshop 5 section with synthesis focus
     * @param {Object} workshop5Data - Workshop 5 data
     * @returns {string} Workshop 5 HTML
     */
    async generateWorkshop5Section(workshop5Data) {
        // Handle different data structures for maximum compatibility
        const synthesis = workshop5Data.synthesis || workshop5Data;
        const strategicScenarios = synthesis.strategicScenarios || workshop5Data.strategicScenarios || [];
        const operationalScenarios = synthesis.operationalScenarios || workshop5Data.operationalScenarios || [];
        const ecosystemMeasures = synthesis.ecosystemMeasures || workshop5Data.ecosystemMeasures || [];
        const treatmentPlans = synthesis.treatmentPlans || workshop5Data.treatmentPlans || workshop5Data.riskTreatmentPlans || {};
        const treatmentStrategies = synthesis.treatmentStrategies || workshop5Data.treatmentStrategies || {};
        const residualRisks = synthesis.residualRisks || workshop5Data.residualRisks || [];
        const monitoringFramework = synthesis.monitoringFramework || workshop5Data.monitoringFramework || {};
        const riskTreatmentSummary = synthesis.riskTreatmentSummary || workshop5Data.riskTreatmentSummary || {};

        // Calculate synthesis metrics
        const totalScenarios = strategicScenarios.length;
        const scenariosWithTreatment = strategicScenarios.filter(s => s.riskTreatment).length;
        const scenariosWithResidualRisk = strategicScenarios.filter(s => s.residualRisk).length;

        // Treatment strategy distribution
        const treatmentDistribution = strategicScenarios.reduce((acc, scenario) => {
            if (scenario.riskTreatment?.option) {
                acc[scenario.riskTreatment.option] = (acc[scenario.riskTreatment.option] || 0) + 1;
            }
            return acc;
        }, {});

        // Risk reduction calculations
        const riskReductions = strategicScenarios
            .filter(s => s.residualRisk?.riskReduction?.overallReduction)
            .map(s => s.residualRisk.riskReduction.overallReduction);

        const avgRiskReduction = riskReductions.length > 0
            ? Math.round(riskReductions.reduce((a, b) => a + b, 0) / riskReductions.length)
            : 0;

        // Implementation status
        const allMeasures = strategicScenarios.flatMap(s => [
            ...(s.customMeasures || []),
            ...(s.dreadedEventMeasures || []),
            ...(s.ecosystemMeasures || [])
        ]);

        const implementationStats = allMeasures.reduce((acc, measure) => {
            const status = measure.implementation || measure.decision || 'planned';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        const treatmentCoverage = totalScenarios > 0 ? Math.round((scenariosWithTreatment / totalScenarios) * 100) : 0;
        const residualRiskCoverage = totalScenarios > 0 ? Math.round((scenariosWithResidualRisk / totalScenarios) * 100) : 0;

        // Check if we have any meaningful data
        const hasData = totalScenarios > 0 || Object.keys(treatmentPlans).length > 0 || residualRisks.length > 0 || ecosystemMeasures.length > 0;

        return `
    <div class="slide" id="workshop5">
        <div class="slide-header">
            <div class="slide-title">Atelier 5 - Traitement du Risque</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Atelier 5 - Traitement du Risque</h1>
            
            ${!hasData ? `
                <div class="info-box warning">
                    <h3><i class="fas fa-exclamation-triangle"></i> Donn\u00e9es de traitement non disponibles</h3>
                    <p>Les donn\u00e9es de traitement des risques ne sont pas encore compl\u00e8tes pour cette analyse.</p>
                    <p>Cette section sera mise \u00e0 jour une fois les strat\u00e9gies de traitement d\u00e9finies dans l'interface.</p>
                </div>
            ` : `
                <!-- 5.1 Vue d'Ensemble du Traitement -->
                <h2>5.1. Vue d'Ensemble du Traitement</h2>
                
                <div class="info-box info">
                    <h3><i class="fas fa-chart-pie"></i> M\u00e9triques de Traitement</h3>
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem; font-size: 1rem;">
                        <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                            <div style="font-size: 2rem; font-weight: 800; color: var(--primary-color);">${totalScenarios}</div>
                            <div style="text-align: center;">sc\u00e9narios identifi\u00e9s</div>
                        </div>
                        <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                            <div style="font-size: 2rem; font-weight: 800; color: var(--success-color);">${scenariosWithTreatment}</div>
                            <div style="text-align: center;">sc\u00e9narios trait\u00e9s</div>
                        </div>
                        <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                            <div style="font-size: 2rem; font-weight: 800; color: var(--info-color);">${treatmentCoverage}%</div>
                            <div style="text-align: center;">couverture</div>
                        </div>
                        <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                            <div style="font-size: 2rem; font-weight: 800; color: var(--warning-color);">${allMeasures.length}</div>
                            <div style="text-align: center;">mesures identifi\u00e9es</div>
                        </div>
                    </div>
                </div>

                ${Object.keys(treatmentDistribution).length > 0 ? `
                    <div class="info-box ai">
                        <h3><i class="fas fa-balance-scale"></i> Distribution des Strat\u00e9gies de Traitement</h3>
                        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;">
                            ${Object.entries(treatmentDistribution).map(([strategy, count]) => `
                                <div style="text-align: center; padding: 1rem; background: rgba(255,255,255,0.3); border-radius: 8px;">
                                    <div style="font-size: 1.5rem; font-weight: 700; color: var(--primary-color);">${count}</div>
                                    <div style="font-size: 0.9rem; color: var(--text-secondary);">${this.formatTreatmentStrategy(strategy)}</div>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                ` : ''}

                <!-- 5.2 Strat\u00e9gies de Traitement par Sc\u00e9nario -->
                <h2>5.2. Strat\u00e9gies de Traitement par Sc\u00e9nario</h2>
                
                ${strategicScenarios.filter(s => s.riskTreatment).length > 0 ? `
                    <div class="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Sc\u00e9nario</th>
                                    <th>Risque Initial</th>
                                    <th>Strat\u00e9gie</th>
                                    <th>Justification</th>
                                    <th>R\u00e9duction</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${strategicScenarios.filter(s => s.riskTreatment).map(scenario => {
            const initialRisk = scenario.residualRisk?.initialRisk;
            const riskScore = initialRisk ? (initialRisk.gravity * initialRisk.likelihood) : 'N/A';
            const reduction = scenario.residualRisk?.riskReduction?.overallReduction || 0;
            return `
                                        <tr>
                                            <td>
                                                <div style="font-weight: 600; color: var(--text-primary);">${scenario.name || 'Sc\u00e9nario sans nom'}</div>
                                                <div style="font-size: 0.8rem; color: var(--text-secondary);">${scenario.referenceCode || ''}</div>
                                            </td>
                                            <td style="text-align: center; font-weight: 600;">${riskScore}/20</td>
                                            <td style="text-align: center;">
                                                <span class="status-badge ${this.getTreatmentStrategyClass(scenario.riskTreatment.option)}">${this.formatTreatmentStrategy(scenario.riskTreatment.option)}</span>
                                            </td>
                                            <td style="font-size: 0.9rem;">${scenario.riskTreatment.justification || 'Justification non fournie'}</td>
                                            <td style="text-align: center; font-weight: 600; color: var(--success-color);">${reduction}%</td>
                                        </tr>
                                    `;
        }).join('')}
                            </tbody>
                        </table>
                    </div>
                ` : `
                    <div class="info-box warning">
                        <h3><i class="fas fa-exclamation-triangle"></i> Aucune strat\u00e9gie de traitement d\u00e9finie</h3>
                        <p>Les strat\u00e9gies de traitement des risques n'ont pas encore \u00e9t\u00e9 d\u00e9finies pour les sc\u00e9narios identifi\u00e9s.</p>
                    </div>
                `}

                <!-- 5.3 Mesures de S\u00e9curit\u00e9 Identifi\u00e9es -->
                <h2>5.3. Mesures de S\u00e9curit\u00e9 Identifi\u00e9es</h2>
                
                ${allMeasures.length > 0 ? `
                    <div class="info-box success">
                        <h3><i class="fas fa-shield-alt"></i> R\u00e9partition des Mesures par Statut</h3>
                        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 1rem;">
                            ${Object.entries(implementationStats).map(([status, count]) => `
                                <div style="text-align: center; padding: 1rem; background: rgba(255,255,255,0.3); border-radius: 8px;">
                                    <div style="font-size: 1.5rem; font-weight: 700; color: var(--primary-color);">${count}</div>
                                    <div style="font-size: 0.9rem; color: var(--text-secondary);">${this.formatImplementationStatus(status)}</div>
                                </div>
                            `).join('')}
                        </div>
                    </div>

                    <div class="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Mesure</th>
                                    <th>Cat\u00e9gorie</th>
                                    <th>Priorit\u00e9</th>
                                    <th>Statut</th>
                                    <th>Responsable</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${allMeasures.slice(0, 20).map(measure => `
                                    <tr>
                                        <td>
                                            <div style="font-weight: 600; color: var(--text-primary);">${measure.name || 'Mesure sans nom'}</div>
                                            <div style="font-size: 0.8rem; color: var(--text-secondary);">${measure.description || ''}</div>
                                        </td>
                                        <td>${measure.category || 'Non d\u00e9finie'}</td>
                                        <td style="text-align: center;">
                                            <span class="status-badge ${this.getPriorityClass(measure.priority)}">${measure.priority || 'Medium'}</span>
                                        </td>
                                        <td style="text-align: center;">
                                            <span class="status-badge ${this.getImplementationClass(measure.implementation || measure.decision)}">${this.formatImplementationStatus(measure.implementation || measure.decision || 'planned')}</span>
                                        </td>
                                        <td>${measure.responsiblePerson || 'Non assign\u00e9'}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </div>
                ` : `
                    <div class="info-box warning">
                        <h3><i class="fas fa-exclamation-triangle"></i> Aucune mesure de s\u00e9curit\u00e9 d\u00e9finie</h3>
                        <p>Les mesures de s\u00e9curit\u00e9 n'ont pas encore \u00e9t\u00e9 identifi\u00e9es pour les sc\u00e9narios de risque.</p>
                    </div>
                `}

                <!-- 5.4 \u00c9valuation des Risques R\u00e9siduels -->
                <h2>5.4. \u00c9valuation des Risques R\u00e9siduels</h2>
                
                ${riskReductions.length > 0 ? `
                    <div class="info-box info">
                        <h3><i class="fas fa-chart-line"></i> Performance de R\u00e9duction des Risques</h3>
                        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem;">
                            <div style="text-align: center; padding: 1rem; background: rgba(255,255,255,0.3); border-radius: 8px;">
                                <div style="font-size: 1.8rem; font-weight: 700; color: var(--success-color);">${avgRiskReduction}%</div>
                                <div style="font-size: 0.9rem; color: var(--text-secondary);">R\u00e9duction moyenne</div>
                            </div>
                            <div style="text-align: center; padding: 1rem; background: rgba(255,255,255,0.3); border-radius: 8px;">
                                <div style="font-size: 1.8rem; font-weight: 700; color: var(--info-color);">${Math.max(...riskReductions)}%</div>
                                <div style="font-size: 0.9rem; color: var(--text-secondary);">R\u00e9duction maximale</div>
                            </div>
                            <div style="text-align: center; padding: 1rem; background: rgba(255,255,255,0.3); border-radius: 8px;">
                                <div style="font-size: 1.8rem; font-weight: 700; color: var(--warning-color);">${Math.min(...riskReductions)}%</div>
                                <div style="font-size: 0.9rem; color: var(--text-secondary);">R\u00e9duction minimale</div>
                            </div>
                        </div>
                    </div>

                    <div class="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Sc\u00e9nario</th>
                                    <th>Risque Initial</th>
                                    <th>Risque R\u00e9siduel</th>
                                    <th>R\u00e9duction</th>
                                    <th>Statut</th>
                                    <th>Expression du risque r\u00e9siduel</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${strategicScenarios.filter(s => s.residualRisk).map(scenario => {
            const initial = scenario.residualRisk.initialRisk;
            const residual = scenario.residualRisk.residualRisk;
            const reduction = scenario.residualRisk.riskReduction?.overallReduction || 0;
            const initialScore = initial ? (initial.gravity * initial.likelihood) : 0;
            const residualScore = residual ? (residual.gravity * residual.likelihood) : 0;
            const residualExpr = scenario.residualRisk?.residualRisk?.residualRiskExpression || '';
            return `
                                        <tr>
                                            <td>
                                                <div style="font-weight: 600; color: var(--text-primary);">${scenario.name || 'Sc\u00e9nario sans nom'}</div>
                                            </td>
                                            <td style="text-align: center; font-weight: 600;">${initialScore}/20</td>
                                            <td style="text-align: center; font-weight: 600;">${residualScore}/20</td>
                                            <td style="text-align: center; font-weight: 600; color: var(--success-color);">${reduction}%</td>
                                            <td style="text-align: center;">
                                                <span class="status-badge ${residualScore <= 8 ? 'completed' : residualScore <= 12 ? 'in-progress' : 'not-started'}">
                                                    ${residualScore <= 8 ? 'Acceptable' : residualScore <= 12 ? 'Mod\u00e9r\u00e9' : '\u00c9lev\u00e9'}
                                                </span>
                                            </td>
                                            <td style="font-size: 0.85rem; color: var(--text-secondary); max-width: 200px;">${residualExpr || '<span style="color: var(--text-secondary); font-style: italic;">Non renseign\u00e9e</span>'}</td>
                                        </tr>
                                    `;
        }).join('')}
                            </tbody>
                        </table>
                    </div>
                ` : `
                    <div class="info-box warning">
                        <h3><i class="fas fa-exclamation-triangle"></i> \u00c9valuation des risques r\u00e9siduels non disponible</h3>
                        <p>L'\u00e9valuation des risques r\u00e9siduels n'a pas encore \u00e9t\u00e9 effectu\u00e9e.</p>
                    </div>
                `}

                <!-- 5.5 Acceptation des Risques R\u00e9siduels -->
                <h2>5.5. Acceptation des Risques R\u00e9siduels</h2>
                
                ${this.generateRiskAcceptanceSection(strategicScenarios)}
            `}

        </div>
    </div>`;
    }

    /**
     * Generate risk acceptance section for Workshop 5
     * @param {Array} strategicScenarios - Strategic scenarios with risk acceptance data
     * @returns {string} Risk acceptance HTML
     */
    generateRiskAcceptanceSection(strategicScenarios) {
        // Filter scenarios with residual risk data
        const scenariosWithResidualRisk = strategicScenarios.filter(s =>
            s.residualRisk?.residualRisk?.riskScore !== undefined
        );

        // Filter scenarios with risk acceptance decisions
        const scenariosWithAcceptance = scenariosWithResidualRisk.filter(s =>
            s.residualRisk?.riskAcceptance
        );

        // Calculate acceptance statistics
        const acceptanceStats = scenariosWithAcceptance.reduce((acc, scenario) => {
            const status = scenario.residualRisk.riskAcceptance.acceptanceStatus || 'pending';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        // Risk level distribution
        const riskLevelStats = scenariosWithResidualRisk.reduce((acc, scenario) => {
            const riskScore = scenario.residualRisk.residualRisk.riskScore;
            let level = 'low';
            if (riskScore > 12) level = 'critical';
            else if (riskScore > 8) level = 'high';
            else if (riskScore > 4) level = 'medium';

            acc[level] = (acc[level] || 0) + 1;
            return acc;
        }, {});

        const formatAcceptanceStatus = (status) => {
            const statusMap = {
                'pending': 'En attente',
                'auto-accepted': 'Accept\u00e9 auto',
                'manually-accepted': 'Accept\u00e9',
                'rejected': 'Refus\u00e9',
                'escalated': 'Escalad\u00e9',
                'manual-review': 'R\u00e9vision requise'
            };
            return statusMap[status] || status;
        };

        const getStatusClass = (status) => {
            const classMap = {
                'pending': 'warning',
                'auto-accepted': 'completed',
                'manually-accepted': 'completed',
                'rejected': 'not-started',
                'escalated': 'in-progress',
                'manual-review': 'warning'
            };
            return classMap[status] || 'warning';
        };

        const getRiskLevelClass = (level) => {
            const classMap = {
                'low': 'completed',
                'medium': 'warning',
                'high': 'in-progress',
                'critical': 'not-started'
            };
            return classMap[level] || 'warning';
        };

        if (scenariosWithResidualRisk.length === 0) {
            return `
                <div class="info-box warning">
                    <h3><i class="fas fa-exclamation-triangle"></i> Aucun sc\u00e9nario avec \u00e9valuation de risque r\u00e9siduel</h3>
                    <p>Pour utiliser la gestion d'acceptation des risques, vous devez d'abord \u00e9valuer les risques r\u00e9siduels.</p>
                </div>
            `;
        }

        return `
            <div class="info-box info">
                <h3><i class="fas fa-chart-pie"></i> Vue d'Ensemble de l'Acceptation des Risques</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem; font-size: 1rem;">
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--primary-color);">${scenariosWithResidualRisk.length}</div>
                        <div style="text-align: center;">sc\u00e9narios \u00e9valu\u00e9s</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--success-color);">${scenariosWithAcceptance.length}</div>
                        <div style="text-align: center;">d\u00e9cisions prises</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--info-color);">${scenariosWithResidualRisk.length - scenariosWithAcceptance.length}</div>
                        <div style="text-align: center;">en attente</div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: center; padding: 1rem; background: rgba(255,255,255,0.5); border-radius: 12px;">
                        <div style="font-size: 2rem; font-weight: 800; color: var(--warning-color);">${Math.round((scenariosWithAcceptance.length / scenariosWithResidualRisk.length) * 100) || 0}%</div>
                        <div style="text-align: center;">taux de d\u00e9cision</div>
                    </div>
                </div>
            </div>

            ${Object.keys(acceptanceStats).length > 0 ? `
                <div class="info-box success">
                    <h3><i class="fas fa-check-circle"></i> R\u00e9partition des D\u00e9cisions d'Acceptation</h3>
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 1rem;">
                        ${Object.entries(acceptanceStats).map(([status, count]) => `
                            <div style="text-align: center; padding: 1rem; background: rgba(255,255,255,0.3); border-radius: 8px;">
                                <div style="font-size: 1.5rem; font-weight: 700; color: var(--primary-color);">${count}</div>
                                <div style="font-size: 0.9rem; color: var(--text-secondary);">${formatAcceptanceStatus(status)}</div>
                            </div>
                        `).join('')}
                    </div>
                </div>
            ` : ''}

            <div class="info-box ai">
                <h3><i class="fas fa-exclamation-triangle"></i> R\u00e9partition par Niveau de Risque</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 1rem;">
                    ${Object.entries(riskLevelStats).map(([level, count]) => {
            const levelText = level === 'low' ? 'Faible (1-4)' :
                level === 'medium' ? 'Moyen (5-8)' :
                    level === 'high' ? '\u00c9lev\u00e9 (9-12)' : 'Critique (13-20)';
            return `
                            <div style="text-align: center; padding: 1rem; background: rgba(255,255,255,0.3); border-radius: 8px;">
                                <div style="font-size: 1.5rem; font-weight: 700; color: var(--primary-color);">${count}</div>
                                <div style="font-size: 0.9rem; color: var(--text-secondary);">${levelText}</div>
                            </div>
                        `;
        }).join('')}
                </div>
            </div>

            <div class="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Sc\u00e9nario</th>
                            <th>Score R\u00e9siduel</th>
                            <th>Niveau de Risque</th>
                            <th>Statut d'Acceptation</th>
                            <th>Propri\u00e9taire</th>
                            <th>Justification</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${scenariosWithResidualRisk.map(scenario => {
            const riskScore = scenario.residualRisk.residualRisk.riskScore;
            const riskLevel = riskScore <= 4 ? 'low' : riskScore <= 8 ? 'medium' : riskScore <= 12 ? 'high' : 'critical';
            const riskLevelText = riskLevel === 'low' ? 'Faible' : riskLevel === 'medium' ? 'Moyen' : riskLevel === 'high' ? '\u00c9lev\u00e9' : 'Critique';
            const acceptance = scenario.residualRisk.riskAcceptance;
            const status = acceptance?.acceptanceStatus || 'pending';
            const owner = acceptance?.riskOwner || 'Non assign\u00e9';
            const justification = acceptance?.justification || acceptance?.acceptanceJustification || 'Non fournie';

            return `
                                <tr>
                                    <td>
                                        <div style="font-weight: 600; color: var(--text-primary);">${scenario.name || 'Sc\u00e9nario sans nom'}</div>
                                        <div style="font-size: 0.8rem; color: var(--text-secondary);">${scenario.referenceCode || ''}</div>
                                    </td>
                                    <td style="text-align: center; font-weight: 600;">${riskScore}/20</td>
                                    <td style="text-align: center;">
                                        <span class="status-badge ${getRiskLevelClass(riskLevel)}">${riskLevelText}</span>
                                    </td>
                                    <td style="text-align: center;">
                                        <span class="status-badge ${getStatusClass(status)}">${formatAcceptanceStatus(status)}</span>
                                    </td>
                                    <td>${owner}</td>
                                    <td style="font-size: 0.9rem; max-width: 300px;">${justification}</td>
                                </tr>
                            `;
        }).join('')}
                    </tbody>
                </table>
            </div>

            <div class="info-box warning">
                <h3><i class="fas fa-info-circle"></i> Crit\u00e8res d'Acceptation</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1rem; margin-top: 1rem;">
                    <div style="padding: 1rem; background: rgba(16, 185, 129, 0.1); border-left: 4px solid #10b981; border-radius: 8px;">
                        <div style="font-weight: 600; color: #065f46;">≤ 4/20: Acceptation automatique</div>
                        <div style="font-size: 0.9rem; color: #047857;">Risques faibles accept\u00e9s automatiquement</div>
                    </div>
                    <div style="padding: 1rem; background: rgba(245, 158, 11, 0.1); border-left: 4px solid #f59e0b; border-radius: 8px;">
                        <div style="font-weight: 600; color: #92400e;">5-8/20: R\u00e9vision manuelle requise</div>
                        <div style="font-size: 0.9rem; color: #b45309;">D\u00e9cision manag\u00e9riale n\u00e9cessaire</div>
                    </div>
                    <div style="padding: 1rem; background: rgba(239, 68, 68, 0.1); border-left: 4px solid #ef4444; border-radius: 8px;">
                        <div style="font-weight: 600; color: #991b1b;">> 8/20: Escalade obligatoire</div>
                        <div style="font-size: 0.9rem; color: #b91c1c;">Validation direction requise</div>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Generate appendices section
     * @returns {string} Appendices HTML
     */
    generateAppendices() {
        this.currentPage++;
        const appendices = this.reportData.appendices || {};

        return `
            <div class="slide" id="appendices">
        <div class="slide-header">
            <div class="slide-title">Annexes</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Annexes</h1>
            
            <h2>A. M\u00e9thodologie EBIOS RM</h2>
            <p>EBIOS RM (Expression des Besoins et Identification des Objectifs de S\u00e9curit\u00e9 - Risk Manager) est une m\u00e9thode d'analyse et de gestion des risques num\u00e9riques d\u00e9velopp\u00e9e par l'ANSSI.</p>
            
            <h3>Les 5 Ateliers</h3>
            <ul>
                <li><strong>Atelier 1:</strong> Cadrage et socle de s\u00e9curit\u00e9</li>
                <li><strong>Atelier 2:</strong> Sources de risque</li>
                <li><strong>Atelier 3:</strong> Sc\u00e9narios strat\u00e9giques</li>
                <li><strong>Atelier 4:</strong> Sc\u00e9narios op\u00e9rationnels</li>
                <li><strong>Atelier 5:</strong> Traitement du risque</li>
            </ul>
            
            <h2>B. Glossaire</h2>
            <div class="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Terme</th>
                            <th>D\u00e9finition</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Bien support</td>
                            <td>\u00c9l\u00e9ment du syst\u00e8me d'information qui porte ou contribue \u00e0 une mission ou \u00e0 un processus m\u00e9tier</td>
                        </tr>
                        <tr>
                            <td>\u00c9v\u00e9nement redout\u00e9</td>
                            <td>\u00c9v\u00e9nement que l'on souhaite \u00e9viter et qui, s'il survenait, aurait un impact sur les missions de l'organisme</td>
                        </tr>
                        <tr>
                            <td>Source de risque</td>
                            <td>\u00c9l\u00e9ment qui, seul ou en combinaison avec d'autres, pr\u00e9sente un potentiel intrins\u00e8que d'engendrer un risque</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            
            <h2>C. R\u00e9f\u00e9rences</h2>
            <ul>
                <li>ANSSI - Guide EBIOS Risk Manager</li>
                <li>MITRE ATT&CK Framework</li>
                <li>NIST Cybersecurity Framework</li>
                <li>ENISA Threat Landscape</li>
            </ul>
        </div>

    </div > `;
    }

    /**
     * Generate JavaScript for charts
     * @returns {string} JavaScript code
     */
    generateScripts() {
        return `< script >
            document.addEventListener('DOMContentLoaded', function () {
                console.log('Report initialized successfully');
            });

        // Print functionality
        function printReport() {
            window.print();
        }

        // Export functionality
        function exportReport() {
            const element = document.querySelector('.report-container');
            const opt = {
                margin: 1,
                filename: 'rapport-ebios-rm.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
            };
            // This would require html2pdf library to be included
            // html2pdf().set(opt).from(element).save();
        }
    </script > `;
    }

    // Helper methods for CSS classes
    getStatusClass(status) {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'termin\u00e9':
                return 'completed';
            case 'in-progress':
            case 'en cours':
                return 'in-progress';
            default:
                return 'not-started';
        }
    }

    getCriticalityClass(level) {
        if (!level || typeof level !== 'string') {
            return 'info';
        }

        switch (level.toLowerCase()) {
            case 'critical':
            case 'critique':
                return 'error';
            case 'high':
            case '\u00e9lev\u00e9':
                return 'warning';
            case 'medium':
            case 'moyen':
                return 'info';
            default:
                return 'success';
        }
    }

    getSeverityClass(severity) {
        return this.getCriticalityClass(severity);
    }

    getLikelihoodClass(likelihood) {
        if (!likelihood || typeof likelihood !== 'string') {
            return 'info';
        }

        switch (likelihood.toLowerCase()) {
            case 'very high':
            case 'tr\u00e8s \u00e9lev\u00e9':
            case 'quasi certain':
            case 'almost certain':
                return 'error';
            case 'high':
            case '\u00e9lev\u00e9':
            case 'tr\u00e8s vraisemblable':
            case 'very likely':
                return 'warning';
            case 'medium':
            case 'moyen':
            case 'vraisemblable':
            case 'likely':
                return 'info';
            default:
                return 'success';
        }
    }

    getRiskLevelClass(riskLevel) {
        return this.getCriticalityClass(riskLevel);
    }

    getThreatLevelClass(threatLevel) {
        return this.getCriticalityClass(threatLevel);
    }

    getImplementationClass(status) {
        if (!status || typeof status !== 'string') {
            return 'not-started';
        }

        switch (status.toLowerCase()) {
            case 'implemented':
            case 'impl\u00e9ment\u00e9':
                return 'completed';
            case 'planned':
            case 'planifi\u00e9':
                return 'in-progress';
            default:
                return 'not-started';
        }
    }

    getPriorityClass(priority) {
        if (!priority || typeof priority !== 'string') {
            return 'info';
        }

        switch (priority.toLowerCase()) {
            case 'high':
            case '\u00e9lev\u00e9':
                return 'error';
            case 'medium':
            case 'moyen':
                return 'warning';
            default:
                return 'info';
        }
    }

    // Enhanced KPI calculation methods
    calculateTotalRiskScenarios() {
        const workshops = this.reportData.workshops || {};
        const strategicScenarios = workshops.workshop3?.strategicScenarios?.length || 0;
        const operationalScenarios = workshops.workshop4?.operationalScenarios?.length || 0;
        return strategicScenarios + operationalScenarios;
    }

    calculateHighRiskScenarios() {
        const workshops = this.reportData.workshops || {};
        let highRiskCount = 0;

        // Count high-risk strategic scenarios
        const strategicScenarios = workshops.workshop3?.strategicScenarios || [];
        highRiskCount += strategicScenarios.filter(scenario =>
            scenario.gravity >= 4 || scenario.severity === '\u00c9lev\u00e9' || scenario.severity === 'Tr\u00e8s \u00e9lev\u00e9'
        ).length;

        // Count high-risk operational scenarios
        const operationalScenarios = workshops.workshop4?.operationalScenarios || [];
        highRiskCount += operationalScenarios.filter(scenario =>
            scenario.severity === '\u00c9lev\u00e9' || scenario.severity === 'Tr\u00e8s \u00e9lev\u00e9' ||
            scenario.likelihood === '\u00c9lev\u00e9' || scenario.likelihood === 'Tr\u00e8s \u00e9lev\u00e9'
        ).length;

        return highRiskCount;
    }

    calculateCriticalRiskScenarios() {
        const workshops = this.reportData.workshops || {};
        let criticalCount = 0;

        // Count critical strategic scenarios
        const strategicScenarios = workshops.workshop3?.strategicScenarios || [];
        criticalCount += strategicScenarios.filter(scenario =>
            scenario.gravity >= 5 || scenario.severity === 'Critique' || scenario.severity === 'Catastrophique'
        ).length;

        // Count critical operational scenarios
        const operationalScenarios = workshops.workshop4?.operationalScenarios || [];
        criticalCount += operationalScenarios.filter(scenario =>
            scenario.severity === 'Critique' || scenario.severity === 'Catastrophique'
        ).length;

        return criticalCount;
    }

    calculatePlannedControls() {
        const workshops = this.reportData.workshops || {};
        const securityControls = workshops.workshop1?.securityControls || [];
        const treatmentPlans = workshops.workshop5?.riskTreatmentPlans || [];

        let plannedCount = securityControls.filter(control =>
            control.implementationStatus === 'planned' || control.decision === 'implement'
        ).length;

        treatmentPlans.forEach(plan => {
            if (plan.controls) {
                plannedCount += plan.controls.filter(control =>
                    control.decision === 'implement' || control.decision === 'plan'
                ).length;
            }
        });

        return plannedCount;
    }

    calculateImplementedControls() {
        const workshops = this.reportData.workshops || {};
        const securityControls = workshops.workshop1?.securityControls || [];
        const treatmentPlans = workshops.workshop5?.riskTreatmentPlans || [];

        let implementedCount = securityControls.filter(control =>
            control.implementationStatus === 'implemented'
        ).length;

        treatmentPlans.forEach(plan => {
            if (plan.controls) {
                implementedCount += plan.controls.filter(control =>
                    control.decision === 'implement' && control.implementationStatus === 'implemented'
                ).length;
            }
        });

        return implementedCount;
    }

    calculateRiskReduction() {
        const totalControls = this.calculatePlannedControls() + this.calculateImplementedControls();
        const implementedControls = this.calculateImplementedControls();

        if (totalControls === 0) return 0;
        return Math.round((implementedControls / totalControls) * 100);
    }

    calculateControlCoverage() {
        const totalRisks = this.calculateTotalRiskScenarios();
        const controlledRisks = this.calculatePlannedControls();

        if (totalRisks === 0) return 0;
        return Math.round((controlledRisks / totalRisks) * 100);
    }

    calculateControlEffectiveness() {
        const workshops = this.reportData.workshops || {};
        const securityControls = workshops.workshop1?.securityControls || [];

        if (securityControls.length === 0) return 0;

        const effectivenessScores = securityControls
            .map(control => this.mapEffectivenessToScore(control.effectiveness))
            .filter(score => score > 0);

        if (effectivenessScores.length === 0) return 0;

        const averageScore = effectivenessScores.reduce((sum, score) => sum + score, 0) / effectivenessScores.length;
        return Math.round(averageScore);
    }

    calculateAverageRiskSeverity() {
        const workshops = this.reportData.workshops || {};
        const allScenarios = [
            ...(workshops.workshop3?.strategicScenarios || []),
            ...(workshops.workshop4?.operationalScenarios || [])
        ];

        if (allScenarios.length === 0) return 'N/A';

        const severityScores = allScenarios
            .map(scenario => this.mapSeverityToScore(scenario.severity || scenario.gravity))
            .filter(score => score > 0);

        if (severityScores.length === 0) return 'N/A';

        const averageScore = severityScores.reduce((sum, score) => sum + score, 0) / severityScores.length;
        return this.mapScoreToSeverity(averageScore);
    }

    // Helper methods for data mapping and business logic
    mapCriticalityToPillars(criticality) {
        const mapping = {
            'high': 'Confidentialit\u00e9, Int\u00e9grit\u00e9, Disponibilit\u00e9',
            'medium': 'Int\u00e9grit\u00e9, Disponibilit\u00e9',
            'low': 'Disponibilit\u00e9',
            'critical': 'Confidentialit\u00e9, Int\u00e9grit\u00e9, Disponibilit\u00e9, Tra\u00e7abilit\u00e9'
        };
        return mapping[criticality?.toLowerCase()] || '\u00c0 d\u00e9finir';
    }

    getBusinessValueName(businessValueId) {
        const workshops = this.reportData.workshops || {};
        const businessValues = workshops.workshop1?.businessValues || [];
        const businessValue = businessValues.find(bv => bv.id === businessValueId);
        return businessValue?.name || null;
    }

    getAssociatedDreadedEvents(businessValueId, dreadedEvents) {
        console.log(`[getAssociatedDreadedEvents] Looking for events for business value ID: ${businessValueId} `);
        console.log(`[getAssociatedDreadedEvents] Available dreaded events: `, dreadedEvents.map(de => ({
            name: de.name,
            businessValueId: de.businessValueId,
            businessValue: de.businessValue,
            relatedBusinessValue: de.relatedBusinessValue,
            businessValues: de.businessValues
        })));
        const associatedEvents = dreadedEvents.filter(de => {
            // Check multiple possible ways the business value might be linked
            const isLinked =
                de.businessValueId === businessValueId ||
                de.businessValueId === businessValueId?.toString() ||
                de.businessValue === businessValueId ||
                de.relatedBusinessValue === businessValueId ||
                (Array.isArray(de.businessValues) && de.businessValues.includes(businessValueId)) ||
                (Array.isArray(de.businessValueIds) && de.businessValueIds.includes(businessValueId));
            if (isLinked) {
                console.log(`[getAssociatedDreadedEvents] Found linked event: ${de.name} for business value ${businessValueId} `);
            }
            return isLinked;
        });
        console.log(`[getAssociatedDreadedEvents] Found ${associatedEvents.length} associated events for business value ${businessValueId} `);
        return associatedEvents;
    }

    generateDetailedTreatmentPlan(treatmentPlan, securityFramework, securityControls, dreadedEvents, businessValues) {
        // Use the treatment plan directly from backend if available
        let finalTreatmentPlan = treatmentPlan || {};

        // If no treatment plan from backend, try to reconstruct from securityFramework
        if (Object.keys(finalTreatmentPlan).length === 0 && securityFramework && securityFramework.selectedRules) {
            console.log('[Report Generator] No treatment plan from backend, reconstructing from securityFramework');
            console.log('[Report Generator] SecurityFramework structure:', Object.keys(securityFramework));
            console.log('[Report Generator] SelectedRules:', Object.keys(securityFramework.selectedRules || {}));
            console.log('[Report Generator] RuleDreadedEvents:', Object.keys(securityFramework.ruleDreadedEvents || {}));

            // Create a basic treatment plan structure from available data
            const ruleDreadedEvents = securityFramework.ruleDreadedEvents || {};
            Object.entries(ruleDreadedEvents).forEach(([ruleId, dreadedEventIds]) => {
                if (Array.isArray(dreadedEventIds)) {
                    dreadedEventIds.forEach(deId => {
                        const key = `${ruleId}_${deId} `;
                        finalTreatmentPlan[key] = {
                            ruleId: ruleId,
                            dreadedEventId: deId,
                            residualSeverity: '\u00c0 \u00e9valuer',
                            controls: []
                        };
                    });
                }
            });
        }

        const planEntries = Object.entries(finalTreatmentPlan || {});
        console.log('[Report Generator] Treatment plan entries found:', planEntries.length);

        if (planEntries.length === 0) {
            return `< p style = "font-size: 0.9rem; color: var(--text-secondary); font-style: italic; text-align: center; padding: 2rem;" >
            Aucun plan de traitement d\u00e9taill\u00e9 disponible(v\u00e9rifiez l'\u00e9tape 'Analyse et Association des Contr\u00f4les').
                < br > <small>Structure de donn\u00e9es re\u00e7ue: SecurityFramework(${Object.keys(securityFramework || {}).length} keys), TreatmentPlan(${Object.keys(treatmentPlan || {}).length} entries)</small>
            </p > `;
        }

        // Create linked pairs similar to SecurityControls component
        const linkedPairs = [];
        const allRules = securityFramework?.selectedRules ? Object.values(securityFramework.selectedRules).flat() : [];

        planEntries.forEach(([key, entry]) => {
            const rule = allRules.find(r => r.id === entry.ruleId || r.rule_id === entry.ruleId);
            let dreadedEvent = dreadedEvents.find(de => de.id === entry.dreadedEventId);
            if (!dreadedEvent) dreadedEvent = { id: entry.dreadedEventId, name: '' };

            if (rule && dreadedEvent) {
                linkedPairs.push({
                    key,
                    rule,
                    dreadedEvent,
                    entry
                });
            }
        });

        // Sort by rule name, then by dreaded event name
        linkedPairs.sort((a, b) => {
            const ruleNameA = (a.rule.name || a.rule.title || '').toLowerCase();
            const ruleNameB = (b.rule.name || b.rule.title || '').toLowerCase();
            if (ruleNameA !== ruleNameB) {
                return ruleNameA.localeCompare(ruleNameB);
            }
            return (a.dreadedEvent.name || '').localeCompare(b.dreadedEvent.name || '');
        });

        return `
                <div style="overflow-x: auto; margin: 1rem -1rem;">
                <table class="treatment-plan-table" style="width: 100%; border-collapse: collapse; border: 1px solid var(--light-border);">
                    <thead style="background-color: #f9fafb;">
                        <tr>
                            <th style="border: 1px solid var(--light-border); padding: 0.75rem; text-align: left; font-weight: 600; font-size: 0.75rem; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; width: 20%;">R\u00e8gle</th>
                            <th style="border: 1px solid var(--light-border); padding: 0.75rem; text-align: left; font-weight: 600; font-size: 0.75rem; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; width: 15%;">\u00c9v\u00e9nement Redout\u00e9</th>
                            <th style="border: 1px solid var(--light-border); padding: 0.75rem; text-align: center; font-weight: 600; font-size: 0.75rem; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; width: 10%;">Gravit\u00e9</th>
                            <th style="border: 1px solid var(--light-border); padding: 0.75rem; text-align: left; font-weight: 600; font-size: 0.75rem; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; width: 35%;" colspan="3">Contr\u00f4les (Contr\u00f4le / Responsable / D\u00e9cision)</th>
                            <th style="border: 1px solid var(--light-border); padding: 0.75rem; text-align: center; font-weight: 600; font-size: 0.75rem; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; width: 10%;">Gravit\u00e9 R\u00e9siduelle</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${linkedPairs.map((pair, index) => {
            const { key, rule, dreadedEvent, entry } = pair;

            // Check if it's the start of a new rule group
            const isNewRuleGroup = index === 0 || (rule.id && linkedPairs[index - 1]?.rule?.id !== rule.id);

            // Calculate rowSpan if it's a new group
            let ruleRowSpan = 1;
            if (isNewRuleGroup && rule.id) {
                for (let i = index + 1; i < linkedPairs.length; i++) {
                    if (linkedPairs[i]?.rule?.id === rule.id) {
                        ruleRowSpan++;
                    } else {
                        break;
                    }
                }
            }

            // Get severity info
            const initialSeverityInfo = this.getSeverityInfo(dreadedEvent.severity);
            const residualSeverityInfo = this.getSeverityInfo(entry.residualSeverity);

            // Find linked Business Value
            const linkedBv = businessValues.find(bv => String(bv.id) === String(dreadedEvent.businessValue));
            const linkedBvName = linkedBv?.name;

            // Determine row background based on severity change
            let rowBgStyle = 'background-color: white;';
            const initialSevValue = this.getSeverityValue(dreadedEvent.severity);
            const residualSevValue = this.getSeverityValue(entry.residualSeverity);

            if (initialSevValue && residualSevValue) {
                if (residualSevValue < initialSevValue) {
                    rowBgStyle = 'background-color: rgba(16, 185, 129, 0.05);'; // Green tint for improvement
                } else if (residualSevValue > initialSevValue) {
                    rowBgStyle = 'background-color: rgba(239, 68, 68, 0.05);'; // Red tint for worsening
                } else if (residualSevValue === initialSevValue) {
                    rowBgStyle = 'background-color: rgba(245, 158, 11, 0.05);'; // Yellow tint for no change
                }
            }

            return `
                                <tr style="${rowBgStyle}">
                                    ${isNewRuleGroup ? `
                                        <td style="border: 1px solid var(--light-border); padding: 1rem 0.75rem; vertical-align: top;" rowspan="${ruleRowSpan}">
                                            <span style="font-weight: 700; font-size: 0.85rem; color: var(--text-primary);" title="${rule.description || ''}">${rule.name || rule.title || 'R\u00e8gle inconnue'}</span>
                                        </td>
                                    ` : ''}
                                    <td style="border: 1px solid var(--light-border); padding: 1rem 0.75rem; vertical-align: top;">
                                        <span style="font-size: 0.85rem; color: var(--text-primary);">${dreadedEvent.name}</span>
                                        ${linkedBvName ? `
                                            <div style="font-size: 0.75rem; color: var(--secondary-color); margin-top: 0.25rem;">
                                                (Valeur: ${linkedBvName})
                                            </div>
                                        ` : ''}
                                    </td>
                                    <td style="border: 1px solid var(--light-border); padding: 1rem 0.75rem; text-align: center; vertical-align: middle; ${initialSeverityInfo.bgStyle}">
                                        <span style="font-size: 0.75rem; font-weight: 600; ${initialSeverityInfo.textStyle}">
                                            ${initialSeverityInfo.label} (${initialSeverityInfo.level})
                                        </span>
                                    </td>
                                    <td style="border: 1px solid var(--light-border); padding: 0.25rem; vertical-align: top;" colspan="3">
                                        ${entry.controls && entry.controls.length > 0 ? `
                                            <table style="width: 100%; font-size: 0.75rem; table-layout: fixed;">
                                                <thead>
                                                    <tr>
                                                        <th style="width: 40%; text-align: left; font-weight: 500; color: #6b7280; text-transform: uppercase; font-size: 0.75rem; padding-bottom: 0.25rem;">Contr\u00f4le</th>
                                                        <th style="width: 30%; text-align: left; font-weight: 500; color: #6b7280; text-transform: uppercase; font-size: 0.75rem; padding-bottom: 0.25rem;">Responsable</th>
                                                        <th style="width: 30%; text-align: left; font-weight: 500; color: #6b7280; text-transform: uppercase; font-size: 0.75rem; padding-bottom: 0.25rem;">D\u00e9cision</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    ${entry.controls.map((ctrl, ctrlIndex) => `
                                                        <tr style="${ctrlIndex > 0 ? 'border-top: 1px solid #f3f4f6;' : ''}">
                                                            <td style="padding: 0.25rem 0.25rem 0.25rem 0; vertical-align: top;">
                                                                <div style="display: flex; flex-direction: column; gap: 0.25rem;">
                                                                    <span style="font-size: 0.75rem; ${ctrl.isNewSuggestion ? 'font-style: italic; color: #6366f1;' : 'color: #374151;'}">${ctrl.name || ctrl.controlId || 'Contr\u00f4le inconnu'}</span>
                                                                    ${ctrl.measure_type ? `
                                                                        <span style="display: inline-flex; align-items: center; padding: 0.125rem 0.5rem; border-radius: 9999px; font-size: 0.65rem; font-weight: 500; border: 1px solid; ${this.getMeasureTypeBadgeStyle(ctrl.measure_type)}">
                                                                            ${this.formatMeasureType(ctrl.measure_type)}
                                                                        </span>
                                                                    ` : ''}
                                                                </div>
                                                            </td>
                                                            <td style="padding: 0.25rem; vertical-align: top; font-size: 0.75rem;">
                                                                ${ctrl.responsiblePerson || '-'}
                                                            </td>
                                                            <td style="padding: 0.25rem 0 0.25rem 0.25rem; vertical-align: top;">
                                                                <div style="display: flex; flex-direction: column; gap: 0.125rem;">
                                                                    <span style="font-size: 0.75rem;">
                                                                        ${this.formatDecision(ctrl.decision, ctrl.decisionDate)}
                                                                    </span>
                                                                    ${ctrl.decision === 'apply_before' && ctrl.decisionDate ? `
                                                                        <span style="font-size: 0.65rem; color: var(--text-secondary);">
                                                                            (${ctrl.decisionDate})
                                                                        </span>
                                                                    ` : ''}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    `).join('')}
                                                </tbody>
                                            </table>
                                        ` : `
                                            <div style="text-align: center; font-size: 0.75rem; color: var(--text-secondary); font-style: italic; padding: 0.25rem;">
                                                Aucun contr\u00f4le s\u00e9lectionn\u00e9
                                            </div>
                                        `}
                                    </td>
                                    <td style="border: 1px solid var(--light-border); padding: 1rem 0.75rem; text-align: center; vertical-align: middle; ${residualSeverityInfo.bgStyle}">
                                        <span style="font-size: 0.75rem; font-weight: 600; ${residualSeverityInfo.textStyle}">
                                            ${residualSeverityInfo.label} (${residualSeverityInfo.level})
                                        </span>
                                    </td>
                                </tr>
                            `;
        }).join('')}
                    </tbody>
                </table>
            </div >
                `;
    }

    getDreadedEventName(dreadedEventId) {
        const workshops = this.reportData.workshops || {};
        const dreadedEvents = workshops.workshop1?.dreadedEvents || [];
        const dreadedEvent = dreadedEvents.find(de => de.id === dreadedEventId);
        return dreadedEvent?.name || null;
    }

    generateSecurityFrameworkSummary(securityFramework, businessValues, dreadedEvents) {
        if (!securityFramework || !securityFramework.selectedRules) {
            return `< p style = "font-size: 0.9rem; color: var(--text-secondary); font-style: italic; text-align: center; padding: 2rem;" >
            Aucun socle de s\u00e9curit\u00e9 d\u00e9fini.
            </p > `;
        }

        const allRules = Object.values(securityFramework.selectedRules).flat();
        const ruleDreadedEvents = securityFramework.ruleDreadedEvents || {};

        if (allRules.length === 0) {
            return `< p style = "font-size: 0.9rem; color: var(--text-secondary); font-style: italic; text-align: center; padding: 2rem;" >
            Aucune r\u00e8gle de s\u00e9curit\u00e9 s\u00e9lectionn\u00e9e.
            </p > `;
        }

        // Group rules by framework
        const groupedRules = new Map();
        allRules.forEach(rule => {
            const frameworkName = rule.frameworkName || 'Framework inconnu';
            if (!groupedRules.has(frameworkName)) {
                groupedRules.set(frameworkName, []);
            }
            groupedRules.get(frameworkName).push(rule);
        });

        return `
                <div style="overflow-x: auto; margin: 1rem -1rem;">
                <table style="width: 100%; border-collapse: collapse; border: 1px solid #d1d5db;">
                    <thead style="background-color: #f9fafb;">
                        <tr>
                            <th style="border: 1px solid #d1d5db; padding: 0.75rem; text-align: left; font-weight: 600; font-size: 0.75rem; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; width: 20%;">Framework</th>
                            <th style="border: 1px solid #d1d5db; padding: 0.75rem; text-align: left; font-weight: 600; font-size: 0.75rem; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; width: 25%;">R\u00e8gle</th>
                            <th style="border: 1px solid #d1d5db; padding: 0.75rem; text-align: left; font-weight: 600; font-size: 0.75rem; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; width: 15%;">Statut</th>
                            <th style="border: 1px solid #d1d5db; padding: 0.75rem; text-align: left; font-weight: 600; font-size: 0.75rem; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; width: 20%;">Justification</th>
                            <th style="border: 1px solid #d1d5db; padding: 0.75rem; text-align: left; font-weight: 600; font-size: 0.75rem; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; width: 20%;">\u00c9v\u00e9nements/Valeurs/Biens</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${Array.from(groupedRules.entries()).map(([frameworkName, rules]) => {
            return rules.map((rule, ruleIndex) => {
                const isFirstRuleInFramework = ruleIndex === 0;
                const frameworkRowSpan = rules.length;

                // Get linked dreaded events for this rule
                const linkedDreadedEventIds = ruleDreadedEvents[rule.id] || [];
                const linkedDreadedEvents = linkedDreadedEventIds.map(deId =>
                    dreadedEvents.find(de => de.id === deId)
                ).filter(Boolean);

                // Get linked business values through dreaded events
                const linkedBusinessValues = new Set();
                linkedDreadedEvents.forEach(de => {
                    const bv = businessValues.find(bv => String(bv.id) === String(de.businessValue));
                    if (bv) linkedBusinessValues.add(bv);
                });

                const statusInfo = this.getStatusInfo(rule.status);

                return `
                                    <tr style="border-bottom: 1px solid #e5e7eb;">
                                        ${isFirstRuleInFramework ? `
                                            <td style="border: 1px solid #d1d5db; padding: 0.75rem; vertical-align: top; font-weight: 600; color: #1f2937;" rowspan="${frameworkRowSpan}">
                                                ${frameworkName}
                                            </td>
                                        ` : ''}
                                        <td style="border: 1px solid #d1d5db; padding: 0.75rem; vertical-align: top;">
                                            <div style="font-weight: 600; color: #1f2937; margin-bottom: 0.25rem;">${rule.name || rule.title || 'R\u00e8gle sans nom'}</div>
                                            ${rule.description ? `<div style="font-size: 0.85rem; color: #6b7280;">${rule.description}</div>` : ''}
                                        </td>
                                        <td style="border: 1px solid #d1d5db; padding: 0.75rem; vertical-align: top;">
                                            <span style="display: inline-flex; align-items: center; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 600; ${statusInfo.style}">
                                                <i class="${statusInfo.icon}" style="margin-right: 0.25rem;"></i>
                                                ${statusInfo.label}
                                            </span>
                                        </td>
                                        <td style="border: 1px solid #d1d5db; padding: 0.75rem; vertical-align: top; font-size: 0.85rem; color: #374151;">
                                            ${rule.justification || 'Aucune justification fournie'}
                                        </td>
                                        <td style="border: 1px solid #d1d5db; padding: 0.75rem; vertical-align: top;">
                                            ${linkedDreadedEvents.length > 0 ? `
                                                <div style="margin-bottom: 0.5rem;">
                                                    <strong style="font-size: 0.75rem; color: #4b5563; text-transform: uppercase;">\u00c9v\u00e9nements redout\u00e9s:</strong>
                                                    <div style="margin-top: 0.25rem;">
                                                        ${linkedDreadedEvents.map(de => `
                                                            <span style="display: inline-block; margin: 0.125rem 0.25rem 0.125rem 0; padding: 0.125rem 0.5rem; background-color: rgba(239, 68, 68, 0.1); color: #dc2626; border-radius: 0.25rem; font-size: 0.75rem;">
                                                                ${de.name}
                                                            </span>
                                                        `).join('')}
                                                    </div>
                                                </div>
                                            ` : ''}
                                            ${linkedBusinessValues.size > 0 ? `
                                                <div style="margin-bottom: 0.5rem;">
                                                    <strong style="font-size: 0.75rem; color: #4b5563; text-transform: uppercase;">Valeurs m\u00e9tier:</strong>
                                                    <div style="margin-top: 0.25rem;">
                                                        ${Array.from(linkedBusinessValues).map(bv => `
                                                            <span style="display: inline-block; margin: 0.125rem 0.25rem 0.125rem 0; padding: 0.125rem 0.5rem; background-color: rgba(16, 185, 129, 0.1); color: #059669; border-radius: 0.25rem; font-size: 0.75rem;">
                                                                ${bv.name}
                                                            </span>
                                                        `).join('')}
                                                    </div>
                                                </div>
                                            ` : ''}
                                            ${Array.from(linkedBusinessValues).some(bv => bv.supportAssets && bv.supportAssets.length > 0) ? `
                                                <div>
                                                    <strong style="font-size: 0.75rem; color: #4b5563; text-transform: uppercase;">Biens supports:</strong>
                                                    <div style="margin-top: 0.25rem;">
                                                        ${Array.from(linkedBusinessValues).flatMap(bv => bv.supportAssets || []).map(asset => `
                                                            <span style="display: inline-block; margin: 0.125rem 0.25rem 0.125rem 0; padding: 0.125rem 0.5rem; background-color: rgba(59, 130, 246, 0.1); color: #2563eb; border-radius: 0.25rem; font-size: 0.75rem;">
                                                                ${asset.name}
                                                            </span>
                                                        `).join('')}
                                                    </div>
                                                </div>
                                            ` : ''}
                                            ${linkedDreadedEvents.length === 0 && linkedBusinessValues.size === 0 ? `
                                                <span style="color: #9ca3af; font-size: 0.85rem; font-style: italic;">Aucune liaison d\u00e9finie</span>
                                            ` : ''}
                                        </td>
                                    </tr>
                                `;
            }).join('');
        }).join('')}
                    </tbody>
                </table>
            </div >
                `;
    }

    getStatusInfo(status) {
        const statusMap = {
            'applied': {
                label: 'Appliqu\u00e9',
                style: 'background-color: rgba(16, 185, 129, 0.15); color: #065f46;',
                icon: 'fas fa-check-circle'
            },
            'partially-applied': {
                label: 'Partiellement appliqu\u00e9',
                style: 'background-color: rgba(245, 158, 11, 0.15); color: #92400e;',
                icon: 'fas fa-exclamation-triangle'
            },
            'not-applied': {
                label: 'Non appliqu\u00e9',
                style: 'background-color: rgba(239, 68, 68, 0.15); color: #991b1b;',
                icon: 'fas fa-times-circle'
            }
        };
        return statusMap[status] || {
            label: 'Statut inconnu',
            style: 'background-color: rgba(107, 114, 128, 0.15); color: #374151;',
            icon: 'fas fa-question-circle'
        };
    }

    generateSecurityControlsTable(treatmentPlan, securityFramework, securityControls, dreadedEvents, businessValues) {
        console.log('[SecurityControls Report] Input data:', {
            treatmentPlan: treatmentPlan ? 'present' : 'missing',
            securityFramework: securityFramework ? 'present' : 'missing',
            securityControls: Array.isArray(securityControls) ? securityControls.length : 'not array',
            dreadedEvents: Array.isArray(dreadedEvents) ? dreadedEvents.length : 'not array'
        });

        // Extract the actual plan data - try multiple possible structures
        let planData = {};
        if (treatmentPlan?.plan && typeof treatmentPlan.plan === 'object') {
            planData = treatmentPlan.plan;
        } else if (treatmentPlan?.planData?.plan && typeof treatmentPlan.planData.plan === 'object') {
            planData = treatmentPlan.planData.plan;
        } else if (treatmentPlan && typeof treatmentPlan === 'object' && Object.keys(treatmentPlan).length > 0) {
            const keys = Object.keys(treatmentPlan);
            if (keys.some(key => key.includes('_'))) {
                planData = treatmentPlan;
            }
        }

        // Get rule-event pairs from security framework
        const linkedPairs = [];
        const ruleDreadedEvents = securityFramework?.ruleDreadedEvents || {};
        const allRules = securityFramework?.selectedRules ? Object.values(securityFramework.selectedRules).flat() : [];
        const safeDreadedEvents = Array.isArray(dreadedEvents) ? dreadedEvents : [];

        // Create pairs from framework links
        Object.entries(ruleDreadedEvents).forEach(([ruleId, deIds]) => {
            if (Array.isArray(deIds)) {
                deIds.forEach(deId => {
                    const rule = allRules.find(r => String(r.id) === String(ruleId));
                    let de = safeDreadedEvents.find(d => String(d.id) === String(deId));
                    if (!de) de = { id: deId, name: '' };

                    if (rule && de) {
                        linkedPairs.push({
                            rule,
                            de,
                            key: `${ruleId}_${deId}`,
                            planEntry: planData[`${ruleId}_${deId}`] || {}
                        });
                    }
                });
            }
        });

        // Also add any pairs that exist in plan data but not in framework
        Object.keys(planData).forEach(key => {
            if (!linkedPairs.find(p => p.key === key)) {
                const [ruleId, deId] = key.split('_');
                const rule = allRules.find(r => String(r.id) === String(ruleId)) || { id: ruleId, name: `Rule ${ruleId}` };
                const de = safeDreadedEvents.find(d => String(d.id) === String(deId)) || { id: deId, name: '' };

                linkedPairs.push({
                    rule,
                    de,
                    key,
                    planEntry: planData[key] || {}
                });
            }
        });

        if (linkedPairs.length === 0) {
            return `
            <div style="text-align: center; padding: 3rem; background: #f8fafc; border-radius: 12px; border: 2px dashed #cbd5e1;">
                    <div style="color: #64748b; font-size: 1.1rem; margin-bottom: 1rem;">
                        <i class="fas fa-shield-alt" style="font-size: 2rem; margin-bottom: 1rem; display: block; opacity: 0.5;"></i>
                        No security control associations found
                    </div>
                    <p style="font-size: 0.9rem; color: #94a3b8; margin: 0;">
                        Configure rule-event associations in Workshop 1 → Security Framework
                    </p>
                </div >
                `;
        }

        // Sort pairs by rule name
        linkedPairs.sort((a, b) => {
            const nameA = a.rule?.name?.toLowerCase() || String(a.rule?.id);
            const nameB = b.rule?.name?.toLowerCase() || String(b.rule?.id);
            return nameA.localeCompare(nameB);
        });

        // Calculate comprehensive stats
        const totalControls = linkedPairs.reduce((sum, pair) =>
            sum + (pair.planEntry.controls?.length || 0), 0);
        const pairsWithControls = linkedPairs.filter(pair =>
            pair.planEntry.controls && pair.planEntry.controls.length > 0).length;
        const implementedControls = linkedPairs.reduce((sum, pair) => {
            if (!pair.planEntry.controls) return sum;
            return sum + pair.planEntry.controls.filter(ctrl => ctrl.decision === 'implemented').length;
        }, 0);
        const plannedControls = linkedPairs.reduce((sum, pair) => {
            if (!pair.planEntry.controls) return sum;
            return sum + pair.planEntry.controls.filter(ctrl => ctrl.decision === 'planned').length;
        }, 0);

        return `
                <div style="margin-bottom: 2rem;">
                <!-- Header -->
                <div style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); color: white; padding: 1.5rem; border-radius: 12px 12px 0 0; margin-bottom: 0;">
                    <h3 style="margin: 0; font-size: 1.4rem; font-weight: 700; display: flex; align-items: center;">
                        <i class="fas fa-shield-check" style="margin-right: 0.75rem; font-size: 1.2rem;"></i>
                        Security Controls Treatment Plan
                    </h3>
                    <p style="margin: 0.5rem 0 0 0; opacity: 0.9; font-size: 0.95rem;">
                        Comprehensive overview of security controls assigned to rule-event associations
                    </p>
                </div>
                
                <!--Enhanced Stats Dashboard-- >
                <div style="background: white; border: 1px solid #e2e8f0; border-top: none; padding: 1.5rem;">
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem; margin-bottom: 2rem;">
                        <div style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border: 1px solid #0ea5e9; border-radius: 10px; padding: 1.25rem; text-align: center; position: relative; overflow: hidden;">
                            <div style="position: absolute; top: -10px; right: -10px; width: 40px; height: 40px; background: rgba(14, 165, 233, 0.1); border-radius: 50%;"></div>
                            <div style="font-size: 2rem; font-weight: 800; color: #0369a1; margin-bottom: 0.5rem;">${linkedPairs.length}</div>
                            <div style="font-size: 0.85rem; color: #0369a1; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Rule-Event Pairs</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 1px solid #22c55e; border-radius: 10px; padding: 1.25rem; text-align: center; position: relative; overflow: hidden;">
                            <div style="position: absolute; top: -10px; right: -10px; width: 40px; height: 40px; background: rgba(34, 197, 94, 0.1); border-radius: 50%;"></div>
                            <div style="font-size: 2rem; font-weight: 800; color: #15803d; margin-bottom: 0.5rem;">${totalControls}</div>
                            <div style="font-size: 0.85rem; color: #15803d; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Total Controls</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #fefce8 0%, #fef3c7 100%); border: 1px solid #eab308; border-radius: 10px; padding: 1.25rem; text-align: center; position: relative; overflow: hidden;">
                            <div style="position: absolute; top: -10px; right: -10px; width: 40px; height: 40px; background: rgba(234, 179, 8, 0.1); border-radius: 50%;"></div>
                            <div style="font-size: 2rem; font-weight: 800; color: #a16207; margin-bottom: 0.5rem;">${pairsWithControls}</div>
                            <div style="font-size: 0.85rem; color: #a16207; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">With Controls</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 1px solid #10b981; border-radius: 10px; padding: 1.25rem; text-align: center; position: relative; overflow: hidden;">
                            <div style="position: absolute; top: -10px; right: -10px; width: 40px; height: 40px; background: rgba(16, 185, 129, 0.1); border-radius: 50%;"></div>
                            <div style="font-size: 2rem; font-weight: 800; color: #047857; margin-bottom: 0.5rem;">${implementedControls}</div>
                            <div style="font-size: 0.85rem; color: #047857; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Implemented</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border: 1px solid #3b82f6; border-radius: 10px; padding: 1.25rem; text-align: center; position: relative; overflow: hidden;">
                            <div style="position: absolute; top: -10px; right: -10px; width: 40px; height: 40px; background: rgba(59, 130, 246, 0.1); border-radius: 50%;"></div>
                            <div style="font-size: 2rem; font-weight: 800; color: #1d4ed8; margin-bottom: 0.5rem;">${plannedControls}</div>
                            <div style="font-size: 0.85rem; color: #1d4ed8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Planned</div>
                        </div>
                    </div>
                </div>

                <!--Enhanced Table-- >
            <div style="background: white; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 12px 12px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                <div style="overflow-x: auto;">
                    <table style="width: 100%; border-collapse: collapse; font-size: 0.9rem;">
                        <thead>
                            <tr style="background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%);">
                                <th style="border-bottom: 2px solid #e2e8f0; padding: 1rem 0.75rem; text-align: left; font-weight: 700; color: #1e293b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; width: 18%;">
                                    <i class="fas fa-gavel" style="margin-right: 0.5rem; color: #64748b;"></i>Rule
                                </th>
                                <th style="border-bottom: 2px solid #e2e8f0; padding: 1rem 0.75rem; text-align: left; font-weight: 700; color: #1e293b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; width: 18%;">
                                    <i class="fas fa-exclamation-triangle" style="margin-right: 0.5rem; color: #64748b;"></i>Feared Event
                                </th>
                                <th style="border-bottom: 2px solid #e2e8f0; padding: 1rem 0.75rem; text-align: center; font-weight: 700; color: #1e293b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; width: 10%;">
                                    <i class="fas fa-thermometer-half" style="margin-right: 0.5rem; color: #64748b;"></i>Severity
                                </th>
                                <th style="border-bottom: 2px solid #e2e8f0; padding: 1rem 0.75rem; text-align: left; font-weight: 700; color: #1e293b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; width: 32%;">
                                    <i class="fas fa-shield-alt" style="margin-right: 0.5rem; color: #64748b;"></i>Security Controls
                                </th>
                                <th style="border-bottom: 2px solid #e2e8f0; padding: 1rem 0.75rem; text-align: center; font-weight: 700; color: #1e293b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; width: 10%;">
                                    <i class="fas fa-chart-line" style="margin-right: 0.5rem; color: #64748b;"></i>Residual
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            ${linkedPairs.map((pair, index) => {
            const controls = pair.planEntry.controls || [];
            const residualSeverity = pair.planEntry.residualSeverity;

            return `
                                        <tr style="border-bottom: 1px solid #f1f5f9; ${index % 2 === 0 ? 'background-color: #ffffff;' : 'background-color: #fafbfc;'} transition: background-color 0.2s ease;">
                                            <td style="padding: 1rem 0.75rem; vertical-align: top; border-right: 1px solid #f1f5f9;">
                                                <div style="font-weight: 600; color: #1e293b; margin-bottom: 0.25rem; line-height: 1.4;">
                                                    ${pair.rule.name || `Rule ${pair.rule.id}`}
                                                </div>

                                            </td>
                                            <td style="padding: 1rem 0.75rem; vertical-align: top; border-right: 1px solid #f1f5f9;">
                                                <div style="font-weight: 600; color: #1e293b; margin-bottom: 0.25rem; line-height: 1.4;">
                                                    ${pair.de.name || `Event ${pair.de.id}`}
                                                </div>

                                            </td>
                                            <td style="padding: 1rem 0.75rem; vertical-align: top; text-align: center; border-right: 1px solid #f1f5f9;">
                                                ${pair.de.severity ? this.getSeverityBadge(pair.de.severity) : '<span style="color: #94a3b8; font-style: italic;">Not defined</span>'}
                                            </td>
                                            <td style="padding: 1rem 0.75rem; vertical-align: top; border-right: 1px solid #f1f5f9;">
                                                ${controls.length > 0 ? `
                                                    <div style="display: flex; flex-direction: column; gap: 0.75rem;">
                                                        ${controls.map((ctrl, ctrlIndex) => {
                // Enhanced control data extraction with fallbacks
                const controlName = ctrl.name || ctrl.controlId || ctrl.measure_description || 'Unnamed Control';
                const measureType = ctrl.measure_type || null;
                const isAI = ctrl.isNewSuggestion || false;
                const decision = ctrl.decision || null;
                const responsible = ctrl.responsiblePerson || null;
                const decisionDate = ctrl.decisionDate || null;

                console.log('[Enhanced SecurityControls Report] Control data:', {
                    name: controlName,
                    measureType,
                    isAI,
                    decision,
                    responsible,
                    decisionDate,
                    originalCtrl: ctrl
                });

                return `
                                                            <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border: 1px solid #e2e8f0; border-left: 4px solid ${this.getControlBorderColor(ctrl)}; border-radius: 8px; padding: 0.75rem; position: relative; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);">
                                                                <!-- Control Header -->
                                                                <div style="display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 0.5rem;">
                                                                    <div style="flex: 1;">
                                                                        <div style="font-weight: 700; color: #1e293b; margin-bottom: 0.25rem; line-height: 1.3; font-size: 0.9rem;">
                                                                            ${isAI ? '<i class="fas fa-robot" style="color: #8b5cf6; margin-right: 0.5rem;"></i>' : '<i class="fas fa-shield-check" style="color: #059669; margin-right: 0.5rem;"></i>'}
                                                                            ${controlName}
                                                                        </div>
                                                                        ${measureType ? `
                                                                            <span style="display: inline-flex; align-items: center; padding: 0.25rem 0.5rem; border-radius: 6px; font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.5rem; ${this.getMeasureTypeBadgeStyle(measureType)}">
                                                                                <i class="fas ${this.getMeasureTypeIcon(measureType)}" style="margin-right: 0.375rem; font-size: 0.6rem;"></i>
                                                                                ${this.formatMeasureType(measureType)}
                                                                            </span>
                                                                        ` : ''}
                                                                    </div>
                                                                    ${decision ? `
                                                                        <span style="display: inline-flex; align-items: center; padding: 0.25rem 0.5rem; border-radius: 6px; font-size: 0.7rem; font-weight: 600; ${this.getDecisionStatusInfo(decision).style}">
                                                                            <i class="${this.getDecisionStatusInfo(decision).icon}" style="margin-right: 0.375rem; font-size: 0.6rem;"></i>
                                                                            ${this.getDecisionStatusInfo(decision).label}
                                                                        </span>
                                                                    ` : `
                                                                        <span style="display: inline-flex; align-items: center; padding: 0.25rem 0.5rem; border-radius: 6px; font-size: 0.7rem; font-weight: 600; background-color: rgba(107, 114, 128, 0.15); color: #374151;">
                                                                            <i class="fas fa-question-circle" style="margin-right: 0.375rem; font-size: 0.6rem;"></i>
                                                                            Pending
                                                                        </span>
                                                                    `}
                                                                </div>
                                                                
                                                                <!-- Control Details -->
                                                                <div style="display: grid; grid-template-columns: 1fr; gap: 0.375rem; font-size: 0.8rem;">
                                                                    ${responsible ? `
                                                                        <div style="display: flex; align-items: center; color: #475569;">
                                                                            <i class="fas fa-user" style="margin-right: 0.5rem; color: #64748b; width: 12px;"></i>
                                                                            <span style="font-weight: 600; margin-right: 0.5rem;">Responsible:</span>
                                                                            <span style="background: #e0f2fe; color: #0369a1; padding: 0.125rem 0.375rem; border-radius: 4px; font-weight: 500;">${responsible}</span>
                                                                        </div>
                                                                    ` : `
                                                                        <div style="display: flex; align-items: center; color: #94a3b8;">
                                                                            <i class="fas fa-user" style="margin-right: 0.5rem; color: #cbd5e1; width: 12px;"></i>
                                                                            <span style="font-weight: 600; margin-right: 0.5rem;">Responsible:</span>
                                                                            <span style="font-style: italic;">Not assigned</span>
                                                                        </div>
                                                                    `}
                                                                    ${decisionDate ? `
                                                                        <div style="display: flex; align-items: center; color: #475569;">
                                                                            <i class="fas fa-calendar" style="margin-right: 0.5rem; color: #64748b; width: 12px;"></i>
                                                                            <span style="font-weight: 600; margin-right: 0.5rem;">Date:</span>
                                                                            <span style="font-family: 'Courier New', monospace; background: #f1f5f9; padding: 0.125rem 0.375rem; border-radius: 4px;">${new Date(decisionDate).toLocaleDateString('fr-FR')}</span>
                                                                        </div>
                                                                    ` : ''}

                                                                    <!-- Control ID for debugging -->
                                                                    ${ctrl.controlId || ctrl.id ? `

                                                                    ` : ''}
                                                                </div>
                                                            </div>
                                                        `;
            }).join('')}
                                                    </div>
                                                ` : `
                                                    <div style="text-align: center; padding: 1.5rem; color: #94a3b8; font-style: italic; background: #f8fafc; border: 2px dashed #e2e8f0; border-radius: 8px;">
                                                        <i class="fas fa-shield-alt" style="font-size: 1.5rem; margin-bottom: 0.5rem; display: block; opacity: 0.5;"></i>
                                                        No controls assigned
                                                        <div style="font-size: 0.8rem; margin-top: 0.25rem; color: #64748b;">
                                                            Add controls in the Security Controls component
                                                        </div>
                                                    </div>
                                                `}
                                            </td>
                                            <td style="padding: 1rem 0.75rem; vertical-align: top; text-align: center;">
                                                ${residualSeverity ? this.getSeverityBadge(residualSeverity) : '<span style="color: #94a3b8; font-style: italic;">Not defined</span>'}
                                            </td>
                                        </tr>
                                    `;
        }).join('')}
                        </tbody>
                    </table>
                </div>
            </div>
            </div >
                `;
    }

    /**
     * Generate enhanced security controls table with structured data
     * This method creates a comprehensive table similar to the SecurityControls component
     */
    generateEnhancedSecurityControlsTable(treatmentPlan, securityFramework, securityControls, dreadedEvents, businessValues) {
        console.log('🔥 ===== ENHANCED SECURITY CONTROLS TABLE GENERATION =====');
        console.log('📋 [Enhanced SecurityControls Report] Input parameters received:');
        console.log('  🎯 treatmentPlan:', treatmentPlan);
        console.log('  🛡️ securityFramework:', securityFramework);
        console.log('  🔒 securityControls:', securityControls);
        console.log('  ⚠️ dreadedEvents:', dreadedEvents);
        console.log('  💼 businessValues:', businessValues);

        // Deep analysis of treatmentPlan structure
        if (treatmentPlan) {
            console.log('🔍 [Enhanced SecurityControls Report] TreatmentPlan Deep Analysis:');
            console.log('  📊 Type:', typeof treatmentPlan);
            console.log('  🔑 Keys:', Object.keys(treatmentPlan));
            console.log('  📝 Full JSON Structure:', JSON.stringify(treatmentPlan, null, 2));

            // Check each plan entry
            Object.entries(treatmentPlan).forEach(([planKey, planData]) => {
                console.log(`  📋 Plan Entry "${planKey}": `, {
                    type: typeof planData,
                    keys: planData ? Object.keys(planData) : 'null',
                    hasControls: !!(planData?.controls),
                    controlsCount: planData?.controls?.length || 0,
                    residualSeverity: planData?.residualSeverity,
                    controls: planData?.controls,
                    fullPlanData: planData
                });

                // Log each control in detail
                if (planData?.controls && Array.isArray(planData.controls)) {
                    planData.controls.forEach((control, index) => {
                        console.log(`    🔒 Control ${index + 1}:`, {
                            controlId: control.controlId,
                            name: control.name,
                            measure_type: control.measure_type,
                            responsiblePerson: control.responsiblePerson,
                            decision: control.decision,
                            decisionDate: control.decisionDate,
                            isNewSuggestion: control.isNewSuggestion,
                            fullControl: control
                        });
                    });
                }
            });
        } else {
            console.log('❌ [Enhanced SecurityControls Report] treatmentPlan is null/undefined');
        }

        console.log('🔍 [Enhanced SecurityControls Report] Other parameters analysis:');
        console.log('  🛡️ securityFramework type:', typeof securityFramework, 'keys:', securityFramework ? Object.keys(securityFramework) : 'null');
        console.log('  🔒 securityControls type:', typeof securityControls, 'length:', Array.isArray(securityControls) ? securityControls.length : 'not array');
        console.log('  ⚠️ dreadedEvents type:', typeof dreadedEvents, 'length:', Array.isArray(dreadedEvents) ? dreadedEvents.length : 'not array');
        console.log('  💼 businessValues type:', typeof businessValues, 'length:', Array.isArray(businessValues) ? businessValues.length : 'not array');

        // Debug: Log the actual structure of treatmentPlan
        if (treatmentPlan) {
            console.log('[Enhanced SecurityControls Report] TreatmentPlan structure:', {
                keys: Object.keys(treatmentPlan),
                hasplan: !!treatmentPlan.plan,
                hasplanData: !!treatmentPlan.planData,
                sample: treatmentPlan.plan ? Object.keys(treatmentPlan.plan).slice(0, 3) : 'no plan',
                samplePlanData: treatmentPlan.planData?.plan ? Object.keys(treatmentPlan.planData.plan).slice(0, 3) : 'no planData.plan',
                // Check if treatmentPlan is directly the plan object
                directPlanKeys: Object.keys(treatmentPlan).filter(key => key.includes('_')),
                sampleDirectEntry: Object.keys(treatmentPlan).length > 0 ? treatmentPlan[Object.keys(treatmentPlan)[0]] : null
            });
        }

        // Note: For now, we'll use the enhanced fallback method since async operations 
        // in template literals are complex. The structured endpoint can be used later 
        // when we refactor to handle async operations properly.

        // Enhanced fallback: use existing data but with better formatting
        console.log('[Enhanced SecurityControls Report] Using enhanced fallback with existing data structure');

        // Extract the actual plan data - try multiple possible structures
        let planData = {};
        if (treatmentPlan?.plan && typeof treatmentPlan.plan === 'object') {
            planData = treatmentPlan.plan;
        } else if (treatmentPlan?.planData?.plan && typeof treatmentPlan.planData.plan === 'object') {
            planData = treatmentPlan.planData.plan;
        } else if (treatmentPlan && typeof treatmentPlan === 'object' && Object.keys(treatmentPlan).length > 0) {
            const keys = Object.keys(treatmentPlan);
            if (keys.some(key => key.includes('_'))) {
                planData = treatmentPlan;
            }
        }

        // Get rule-event pairs from security framework
        const linkedPairs = [];
        const ruleDreadedEvents = securityFramework?.ruleDreadedEvents || {};
        const allRules = securityFramework?.selectedRules ? Object.values(securityFramework.selectedRules).flat() : [];
        const safeDreadedEvents = Array.isArray(dreadedEvents) ? dreadedEvents : [];

        // Create pairs from framework links
        Object.entries(ruleDreadedEvents).forEach(([ruleId, deIds]) => {
            if (Array.isArray(deIds)) {
                deIds.forEach(deId => {
                    const rule = allRules.find(r => String(r.id) === String(ruleId));
                    let de = safeDreadedEvents.find(d => String(d.id) === String(deId));
                    if (!de) de = { id: deId, name: '' };

                    if (rule && de) {
                        linkedPairs.push({
                            rule,
                            de,
                            key: `${ruleId}_${deId} `,
                            planEntry: planData[`${ruleId}_${deId} `] || {}
                        });
                    }
                });
            }
        });

        // Also add any pairs that exist in plan data but not in framework
        Object.keys(planData).forEach(key => {
            if (!linkedPairs.find(p => p.key === key)) {
                const [ruleId, deId] = key.split('_');
                const rule = allRules.find(r => String(r.id) === String(ruleId)) || { id: ruleId, name: `Rule ${ruleId} ` };
                const de = safeDreadedEvents.find(d => String(d.id) === String(deId)) || { id: deId, name: '' };

                linkedPairs.push({
                    rule,
                    de,
                    key,
                    planEntry: planData[key] || {}
                });
            }
        });

        if (linkedPairs.length === 0) {
            return `
    <div style="text-align: center; padding: 3rem; background: #f8fafc; border-radius: 12px; border: 2px dashed #cbd5e1;">
                    <div style="color: #64748b; font-size: 1.1rem; margin-bottom: 1rem;">
                        <i class="fas fa-shield-alt" style="font-size: 2rem; margin-bottom: 1rem; display: block; opacity: 0.5;"></i>
                        No security control associations found
                    </div>
                    <p style="font-size: 0.9rem; color: #94a3b8; margin: 0;">
                        Configure rule-event associations in Workshop 1 → Security Framework
                    </p>
                </div >
    `;
        }

        // Sort pairs by rule name
        linkedPairs.sort((a, b) => {
            const nameA = a.rule?.name?.toLowerCase() || String(a.rule?.id);
            const nameB = b.rule?.name?.toLowerCase() || String(b.rule?.id);
            return nameA.localeCompare(nameB);
        });

        // Calculate comprehensive stats
        const totalControls = linkedPairs.reduce((sum, pair) =>
            sum + (pair.planEntry.controls?.length || 0), 0);
        const pairsWithControls = linkedPairs.filter(pair =>
            pair.planEntry.controls && pair.planEntry.controls.length > 0).length;
        const implementedControls = linkedPairs.reduce((sum, pair) => {
            if (!pair.planEntry.controls) return sum;
            return sum + pair.planEntry.controls.filter(ctrl => ctrl.decision === 'implemented').length;
        }, 0);
        const plannedControls = linkedPairs.reduce((sum, pair) => {
            if (!pair.planEntry.controls) return sum;
            return sum + pair.planEntry.controls.filter(ctrl => ctrl.decision === 'planned').length;
        }, 0);
        const partialControls = linkedPairs.reduce((sum, pair) => {
            if (!pair.planEntry.controls) return sum;
            return sum + pair.planEntry.controls.filter(ctrl => ctrl.decision === 'partial').length;
        }, 0);

        return `
    < div style = "margin-bottom: 2rem;" >
                < !--Header -->
                <div style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); color: white; padding: 1.5rem; border-radius: 12px 12px 0 0; margin-bottom: 0;">
                    <h3 style="margin: 0; font-size: 1.4rem; font-weight: 700; display: flex; align-items: center;">
                        <i class="fas fa-shield-check" style="margin-right: 0.75rem; font-size: 1.2rem;"></i>
                        Security Controls Treatment Plan
                    </h3>
                    <p style="margin: 0.5rem 0 0 0; opacity: 0.9; font-size: 0.95rem;">
                        Comprehensive overview of security controls with implementation details
                    </p>
                </div>
                
                <!--Enhanced Stats Dashboard-- >
                <div style="background: white; border: 1px solid #e2e8f0; border-top: none; padding: 1.5rem;">
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; margin-bottom: 2rem;">
                        <div style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border: 1px solid #0ea5e9; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #0369a1; margin-bottom: 0.5rem;">${linkedPairs.length}</div>
                            <div style="font-size: 0.8rem; color: #0369a1; font-weight: 600; text-transform: uppercase;">Rule-Event Pairs</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 1px solid #22c55e; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #15803d; margin-bottom: 0.5rem;">${totalControls}</div>
                            <div style="font-size: 0.8rem; color: #15803d; font-weight: 600; text-transform: uppercase;">Total Controls</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 1px solid #10b981; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #047857; margin-bottom: 0.5rem;">${implementedControls}</div>
                            <div style="font-size: 0.8rem; color: #047857; font-weight: 600; text-transform: uppercase;">Implemented</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border: 1px solid #3b82f6; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #1d4ed8; margin-bottom: 0.5rem;">${plannedControls}</div>
                            <div style="font-size: 0.8rem; color: #1d4ed8; font-weight: 600; text-transform: uppercase;">Planned</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #fefce8 0%, #fef3c7 100%); border: 1px solid #eab308; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #a16207; margin-bottom: 0.5rem;">${partialControls}</div>
                            <div style="font-size: 0.8rem; color: #a16207; font-weight: 600; text-transform: uppercase;">Partial</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #fefce8 0%, #fef3c7 100%); border: 1px solid #eab308; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #a16207; margin-bottom: 0.5rem;">${pairsWithControls}</div>
                            <div style="font-size: 0.8rem; color: #a16207; font-weight: 600; text-transform: uppercase;">With Controls</div>
                        </div>
                    </div>
                </div>

                <!--Enhanced Table with Workshop 1 Style-- >
    <div style="background: white; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 12px 12px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
        <div style="overflow-x: auto;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.85rem;">
                <thead>
                    <tr style="background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%);">
                        <th style="border: 1px solid #e2e8f0; padding: 0.75rem; text-align: left; font-weight: 700; color: #1e293b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; width: 15%;">
                            <i class="fas fa-gavel" style="margin-right: 0.5rem; color: #64748b;"></i>Rule
                        </th>
                        <th style="border: 1px solid #e2e8f0; padding: 0.75rem; text-align: left; font-weight: 700; color: #1e293b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; width: 15%;">
                            <i class="fas fa-exclamation-triangle" style="margin-right: 0.5rem; color: #64748b;"></i>Feared Event
                        </th>
                        <th style="border: 1px solid #e2e8f0; padding: 0.75rem; text-align: center; font-weight: 700; color: #1e293b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; width: 8%;">
                            <i class="fas fa-thermometer-half" style="margin-right: 0.5rem; color: #64748b;"></i>Severity
                        </th>
                        <th style="border: 1px solid #e2e8f0; padding: 0.75rem; text-align: left; font-weight: 700; color: #1e293b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; width: 42%;">
                            <i class="fas fa-shield-alt" style="margin-right: 0.5rem; color: #64748b;"></i>Controls / Responsible / Decision
                        </th>
                        <th style="border: 1px solid #e2e8f0; padding: 0.75rem; text-align: center; font-weight: 700; color: #1e293b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; width: 10%;">
                            <i class="fas fa-chart-line" style="margin-right: 0.5rem; color: #64748b;"></i>Residual
                        </th>
                    </tr>
                </thead>
                <tbody>
                    ${linkedPairs.map((pair, index) => {
            const controls = pair.planEntry.controls || [];
            const residualSeverity = pair.planEntry.residualSeverity;

            // Debug: Log the actual structure of planEntry
            console.log('[Enhanced SecurityControls Report] PlanEntry for', pair.key, ':', {
                hasControls: !!pair.planEntry.controls,
                controlsLength: pair.planEntry.controls?.length || 0,
                residualSeverity,
                sampleControl: pair.planEntry.controls?.[0] || null,
                fullPlanEntry: pair.planEntry
            });

            return `
                                        <tr style="border-bottom: 1px solid #f1f5f9; ${index % 2 === 0 ? 'background-color: #ffffff;' : 'background-color: #fafbfc;'}">
                                            <!-- Rule Column -->
                                            <td style="border: 1px solid #e2e8f0; padding: 0.75rem; vertical-align: top;">
                                                <div style="font-weight: 600; color: #1e293b; margin-bottom: 0.25rem; line-height: 1.3; font-size: 0.85rem;">
                                                    ${pair.rule.name || `Rule ${pair.rule.id}`}
                                                </div>
                                            </td>
                                            
                                            <!-- Feared Event Column -->
                                            <td style="border: 1px solid #e2e8f0; padding: 0.75rem; vertical-align: top;">
                                                <div style="font-weight: 600; color: #1e293b; margin-bottom: 0.25rem; line-height: 1.3; font-size: 0.85rem;">
                                                    ${pair.de.name || `Event ${pair.de.id}`}
                                                </div>
                                            </td>
                                            
                                            <!-- Severity Column -->
                                            <td style="border: 1px solid #e2e8f0; padding: 0.75rem; vertical-align: top; text-align: center;">
                                                ${pair.de.severity ? this.getSeverityBadge(pair.de.severity) : '<span style="color: #94a3b8; font-style: italic; font-size: 0.75rem;">Not defined</span>'}
                                            </td>
                                            
                                            
                                            <!-- Controls Column (Enhanced) -->
                                            <td style="border: 1px solid #e2e8f0; padding: 0.75rem; vertical-align: top;">
                                                ${controls.length > 0 ? `
                                                    <div style="display: flex; flex-direction: column; gap: 0.6rem;">
                                                        ${controls.map((ctrl, ctrlIndex) => `
                                                            <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border: 1px solid #e2e8f0; border-left: 3px solid ${this.getControlBorderColor(ctrl)}; border-radius: 6px; padding: 0.6rem; font-size: 0.75rem;">
                                                                <!-- Control Name and Type -->
                                                                <div style="display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 0.4rem;">
                                                                    <div style="flex: 1; margin-right: 0.5rem;">
                                                                        <div style="font-weight: 700; color: #1e293b; margin-bottom: 0.2rem; line-height: 1.2;">
                                                                            ${ctrl.isNewSuggestion ? '<i class="fas fa-robot" style="color: #8b5cf6; margin-right: 0.4rem; font-size: 0.7rem;"></i>' : '<i class="fas fa-shield-check" style="color: #059669; margin-right: 0.4rem; font-size: 0.7rem;"></i>'}
                                                                            ${ctrl.name || ctrl.controlId || 'Unnamed Control'}
                                                                        </div>
                                                                        ${ctrl.measure_type ? `
                                                                            <span style="display: inline-flex; align-items: center; padding: 0.15rem 0.4rem; border-radius: 4px; font-size: 0.6rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 0.3rem; ${this.getMeasureTypeBadgeStyle(ctrl.measure_type)}">
                                                                                <i class="fas ${this.getMeasureTypeIcon(ctrl.measure_type)}" style="margin-right: 0.3rem; font-size: 0.5rem;"></i>
                                                                                ${this.formatMeasureType(ctrl.measure_type)}
                                                                            </span>
                                                                        ` : ''}
                                                                    </div>
                                                                    ${ctrl.decision ? `
                                                                        <span style="display: inline-flex; align-items: center; padding: 0.15rem 0.4rem; border-radius: 4px; font-size: 0.6rem; font-weight: 600; ${this.getDecisionStatusInfo(ctrl.decision).style}">
                                                                            <i class="${this.getDecisionStatusInfo(ctrl.decision).icon}" style="margin-right: 0.3rem; font-size: 0.5rem;"></i>
                                                                            ${this.getDecisionStatusInfo(ctrl.decision).label}
                                                                        </span>
                                                                    ` : ''}
                                                                </div>
                                                                
                                                                <!-- Control Details Grid -->
                                                                <div style="display: grid; grid-template-columns: 1fr; gap: 0.25rem; font-size: 0.7rem;">
                                                                    ${ctrl.responsiblePerson ? `
                                                                        <div style="display: flex; align-items: center; color: #475569;">
                                                                            <i class="fas fa-user" style="margin-right: 0.4rem; color: #64748b; width: 10px; font-size: 0.6rem;"></i>
                                                                            <span style="font-weight: 600; margin-right: 0.4rem;">Responsible:</span>
                                                                            <span style="background: #e0f2fe; color: #0369a1; padding: 0.1rem 0.3rem; border-radius: 3px; font-weight: 500;">${ctrl.responsiblePerson}</span>
                                                                        </div>
                                                                    ` : ''}
                                                                    ${ctrl.decisionDate ? `
                                                                        <div style="display: flex; align-items: center; color: #475569;">
                                                                            <i class="fas fa-calendar" style="margin-right: 0.4rem; color: #64748b; width: 10px; font-size: 0.6rem;"></i>
                                                                            <span style="font-weight: 600; margin-right: 0.4rem;">Date:</span>
                                                                            <span style="font-family: 'Courier New', monospace; background: #f1f5f9; padding: 0.1rem 0.3rem; border-radius: 3px;">${new Date(ctrl.decisionDate).toLocaleDateString('fr-FR')}</span>
                                                                        </div>
                                                                    ` : ''}
                                                                </div>
                                                            </div>
                                                        `).join('')}
                                                    </div>
                                                ` : `
                                                    <div style="text-align: center; padding: 1rem; color: #94a3b8; font-style: italic; background: #f8fafc; border: 2px dashed #e2e8f0; border-radius: 6px; font-size: 0.75rem;">
                                                        <i class="fas fa-shield-alt" style="font-size: 1rem; margin-bottom: 0.3rem; display: block; opacity: 0.5;"></i>
                                                        No controls assigned
                                                    </div>
                                                `}
                                            </td>
                                            
                                            <!-- Residual Severity Column -->
                                            <td style="border: 1px solid #e2e8f0; padding: 0.75rem; vertical-align: top; text-align: center;">
                                                ${residualSeverity ? this.getSeverityBadge(residualSeverity) : '<span style="color: #94a3b8; font-style: italic; font-size: 0.75rem;">Not defined</span>'}
                                            </td>
                                        </tr>
                                    `;
        }).join('')}
                </tbody>
            </table>
        </div>
    </div>
            </div >
    `;
    }

    /**
     * Generate enhanced table from structured data
     */
    generateEnhancedTableFromStructuredData(structuredData) {
        console.log('[Enhanced SecurityControls Report] Generating table from structured data:', structuredData.length, 'pairs');

        // Calculate comprehensive stats
        const totalControls = structuredData.reduce((sum, pair) =>
            sum + (pair.controls?.length || 0), 0);
        const pairsWithControls = structuredData.filter(pair =>
            pair.controls && pair.controls.length > 0).length;
        const implementedControls = structuredData.reduce((sum, pair) => {
            if (!pair.controls) return sum;
            return sum + pair.controls.filter(ctrl => ctrl.decision === 'implemented').length;
        }, 0);
        const plannedControls = structuredData.reduce((sum, pair) => {
            if (!pair.controls) return sum;
            return sum + pair.controls.filter(ctrl => ctrl.decision === 'planned').length;
        }, 0);
        const partialControls = structuredData.reduce((sum, pair) => {
            if (!pair.controls) return sum;
            return sum + pair.controls.filter(ctrl => ctrl.decision === 'partial').length;
        }, 0);

        return `
    < div style = "margin-bottom: 2rem;" >
                < !--Header -->
                <div style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); color: white; padding: 1.5rem; border-radius: 12px 12px 0 0; margin-bottom: 0;">
                    <h3 style="margin: 0; font-size: 1.4rem; font-weight: 700; display: flex; align-items: center;">
                        <i class="fas fa-shield-check" style="margin-right: 0.75rem; font-size: 1.2rem;"></i>
                        Security Controls Treatment Plan
                    </h3>
                    <p style="margin: 0.5rem 0 0 0; opacity: 0.9; font-size: 0.95rem;">
                        Comprehensive overview of security controls with implementation details
                    </p>
                </div>
                
                <!--Enhanced Stats Dashboard-- >
                <div style="background: white; border: 1px solid #e2e8f0; border-top: none; padding: 1.5rem;">
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; margin-bottom: 2rem;">
                        <div style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border: 1px solid #0ea5e9; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #0369a1; margin-bottom: 0.5rem;">${structuredData.length}</div>
                            <div style="font-size: 0.8rem; color: #0369a1; font-weight: 600; text-transform: uppercase;">Rule-Event Pairs</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 1px solid #22c55e; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #15803d; margin-bottom: 0.5rem;">${totalControls}</div>
                            <div style="font-size: 0.8rem; color: #15803d; font-weight: 600; text-transform: uppercase;">Total Controls</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 1px solid #10b981; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #047857; margin-bottom: 0.5rem;">${implementedControls}</div>
                            <div style="font-size: 0.8rem; color: #047857; font-weight: 600; text-transform: uppercase;">Implemented</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border: 1px solid #3b82f6; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #1d4ed8; margin-bottom: 0.5rem;">${plannedControls}</div>
                            <div style="font-size: 0.8rem; color: #1d4ed8; font-weight: 600; text-transform: uppercase;">Planned</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #fefce8 0%, #fef3c7 100%); border: 1px solid #eab308; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #a16207; margin-bottom: 0.5rem;">${partialControls}</div>
                            <div style="font-size: 0.8rem; color: #a16207; font-weight: 600; text-transform: uppercase;">Partial</div>
                        </div>
                        <div style="background: linear-gradient(135deg, #fefce8 0%, #fef3c7 100%); border: 1px solid #eab308; border-radius: 10px; padding: 1rem; text-align: center;">
                            <div style="font-size: 1.8rem; font-weight: 800; color: #a16207; margin-bottom: 0.5rem;">${pairsWithControls}</div>
                            <div style="font-size: 0.8rem; color: #a16207; font-weight: 600; text-transform: uppercase;">With Controls</div>
                        </div>
                    </div>
                </div>

                <!--Enhanced Table with Workshop 1 Style-- >
    <div style="background: white; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 12px 12px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
        <div style="overflow-x: auto;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.85rem;">
                <thead>
                    <tr style="background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%);">
                        <th style="border: 1px solid #e2e8f0; padding: 0.75rem; text-align: left; font-weight: 700; color: #1e293b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; width: 15%;">
                            <i class="fas fa-gavel" style="margin-right: 0.5rem; color: #64748b;"></i>Rule
                        </th>
                        <th style="border: 1px solid #e2e8f0; padding: 0.75rem; text-align: left; font-weight: 700; color: #1e293b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; width: 15%;">
                            <i class="fas fa-exclamation-triangle" style="margin-right: 0.5rem; color: #64748b;"></i>Feared Event
                        </th>
                        <th style="border: 1px solid #e2e8f0; padding: 0.75rem; text-align: center; font-weight: 700; color: #1e293b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; width: 8%;">
                            <i class="fas fa-thermometer-half" style="margin-right: 0.5rem; color: #64748b;"></i>Severity
                        </th>
                        <th style="border: 1px solid #e2e8f0; padding: 0.75rem; text-align: left; font-weight: 700; color: #1e293b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; width: 42%;">
                            <i class="fas fa-shield-alt" style="margin-right: 0.5rem; color: #64748b;"></i>Controls / Responsible / Decision
                        </th>
                        <th style="border: 1px solid #e2e8f0; padding: 0.75rem; text-align: center; font-weight: 700; color: #1e293b; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; width: 10%;">
                            <i class="fas fa-chart-line" style="margin-right: 0.5rem; color: #64748b;"></i>Residual
                        </th>
                    </tr>
                </thead>
                <tbody>
                    ${structuredData.map((pair, index) => {
            const controls = pair.controls || [];
            const residualSeverity = pair.residualSeverity;

            return `
                                        <tr style="border-bottom: 1px solid #f1f5f9; ${index % 2 === 0 ? 'background-color: #ffffff;' : 'background-color: #fafbfc;'}">
                                            <!-- Rule Column -->
                                            <td style="border: 1px solid #e2e8f0; padding: 0.75rem; vertical-align: top;">
                                                <div style="font-weight: 600; color: #1e293b; margin-bottom: 0.25rem; line-height: 1.3; font-size: 0.85rem;">
                                                    ${pair.rule.name || `Rule ${pair.rule.id}`}
                                                </div>
                                            </td>
                                            
                                            <!-- Feared Event Column -->
                                            <td style="border: 1px solid #e2e8f0; padding: 0.75rem; vertical-align: top;">
                                                <div style="font-weight: 600; color: #1e293b; margin-bottom: 0.25rem; line-height: 1.3; font-size: 0.85rem;">
                                                    ${pair.dreadedEvent.name || `Event ${pair.dreadedEvent.id}`}
                                                </div>
                                            </td>
                                            
                                            <!-- Severity Column -->
                                            <td style="border: 1px solid #e2e8f0; padding: 0.75rem; vertical-align: top; text-align: center;">
                                                ${pair.dreadedEvent.severity ? this.getSeverityBadge(pair.dreadedEvent.severity) : '<span style="color: #94a3b8; font-style: italic; font-size: 0.75rem;">Not defined</span>'}
                                            </td>
                                            
                                            
                                            <!-- Controls Column (Enhanced) -->
                                            <td style="border: 1px solid #e2e8f0; padding: 0.75rem; vertical-align: top;">
                                                ${controls.length > 0 ? `
                                                    <div style="display: flex; flex-direction: column; gap: 0.6rem;">
                                                        ${controls.map((ctrl, ctrlIndex) => `
                                                            <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border: 1px solid #e2e8f0; border-left: 3px solid ${this.getControlBorderColor(ctrl)}; border-radius: 6px; padding: 0.6rem; font-size: 0.75rem;">
                                                                <!-- Control Name and Type -->
                                                                <div style="display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 0.4rem;">
                                                                    <div style="flex: 1; margin-right: 0.5rem;">
                                                                        <div style="font-weight: 700; color: #1e293b; margin-bottom: 0.2rem; line-height: 1.2;">
                                                                            ${ctrl.isNewSuggestion ? '<i class="fas fa-robot" style="color: #8b5cf6; margin-right: 0.4rem; font-size: 0.7rem;"></i>' : '<i class="fas fa-shield-check" style="color: #059669; margin-right: 0.4rem; font-size: 0.7rem;"></i>'}
                                                                            ${ctrl.name || ctrl.id || 'Unnamed Control'}
                                                                        </div>
                                                                        ${ctrl.measure_type ? `
                                                                            <span style="display: inline-flex; align-items: center; padding: 0.15rem 0.4rem; border-radius: 4px; font-size: 0.6rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 0.3rem; ${this.getMeasureTypeBadgeStyle(ctrl.measure_type)}">
                                                                                <i class="fas ${this.getMeasureTypeIcon(ctrl.measure_type)}" style="margin-right: 0.3rem; font-size: 0.5rem;"></i>
                                                                                ${this.formatMeasureType(ctrl.measure_type)}
                                                                            </span>
                                                                        ` : ''}
                                                                    </div>
                                                                    ${ctrl.decision ? `
                                                                        <span style="display: inline-flex; align-items: center; padding: 0.15rem 0.4rem; border-radius: 4px; font-size: 0.6rem; font-weight: 600; ${this.getDecisionStatusInfo(ctrl.decision).style}">
                                                                            <i class="${this.getDecisionStatusInfo(ctrl.decision).icon}" style="margin-right: 0.3rem; font-size: 0.5rem;"></i>
                                                                            ${this.getDecisionStatusInfo(ctrl.decision).label}
                                                                        </span>
                                                                    ` : ''}
                                                                </div>
                                                                
                                                                <!-- Control Details Grid -->
                                                                <div style="display: grid; grid-template-columns: 1fr; gap: 0.25rem; font-size: 0.7rem;">
                                                                    ${ctrl.responsiblePerson ? `
                                                                        <div style="display: flex; align-items: center; color: #475569;">
                                                                            <i class="fas fa-user" style="margin-right: 0.4rem; color: #64748b; width: 10px; font-size: 0.6rem;"></i>
                                                                            <span style="font-weight: 600; margin-right: 0.4rem;">Responsible:</span>
                                                                            <span style="background: #e0f2fe; color: #0369a1; padding: 0.1rem 0.3rem; border-radius: 3px; font-weight: 500;">${ctrl.responsiblePerson}</span>
                                                                        </div>
                                                                    ` : ''}
                                                                    ${ctrl.decisionDate ? `
                                                                        <div style="display: flex; align-items: center; color: #475569;">
                                                                            <i class="fas fa-calendar" style="margin-right: 0.4rem; color: #64748b; width: 10px; font-size: 0.6rem;"></i>
                                                                            <span style="font-weight: 600; margin-right: 0.4rem;">Date:</span>
                                                                            <span style="font-family: 'Courier New', monospace; background: #f1f5f9; padding: 0.1rem 0.3rem; border-radius: 3px;">${new Date(ctrl.decisionDate).toLocaleDateString('fr-FR')}</span>
                                                                        </div>
                                                                    ` : ''}
                                                                </div>
                                                            </div>
                                                        `).join('')}
                                                    </div>
                                                ` : `
                                                    <div style="text-align: center; padding: 1rem; color: #94a3b8; font-style: italic; background: #f8fafc; border: 2px dashed #e2e8f0; border-radius: 6px; font-size: 0.75rem;">
                                                        <i class="fas fa-shield-alt" style="font-size: 1rem; margin-bottom: 0.3rem; display: block; opacity: 0.5;"></i>
                                                        No controls assigned
                                                    </div>
                                                `}
                                            </td>
                                            
                                            <!-- Residual Severity Column -->
                                            <td style="border: 1px solid #e2e8f0; padding: 0.75rem; vertical-align: top; text-align: center;">
                                                ${residualSeverity ? this.getSeverityBadge(residualSeverity) : '<span style="color: #94a3b8; font-style: italic; font-size: 0.75rem;">Not defined</span>'}
                                            </td>
                                        </tr>
                                    `;
        }).join('')}
                </tbody>
            </table>
        </div>
    </div>
            </div >
    `;
    }

    getDecisionStatusInfo(decision) {
        const statusMap = {
            'implemented': {
                label: 'Implemented',
                style: 'background-color: rgba(16, 185, 129, 0.15); color: #065f46;',
                icon: 'fas fa-check-circle'
            },
            'planned': {
                label: 'Planned',
                style: 'background-color: rgba(59, 130, 246, 0.15); color: #1e40af;',
                icon: 'fas fa-clock'
            },
            'apply_before': {
                label: 'To apply',
                style: 'background-color: rgba(245, 158, 11, 0.15); color: #92400e;',
                icon: 'fas fa-calendar-alt'
            },
            'abandoned': {
                label: 'Abandoned',
                style: 'background-color: rgba(239, 68, 68, 0.15); color: #991b1b;',
                icon: 'fas fa-times-circle'
            },
            'partial': {
                label: 'Partial',
                style: 'background-color: rgba(245, 158, 11, 0.15); color: #92400e;',
                icon: 'fas fa-exclamation-triangle'
            }
        };
        return statusMap[decision] || {
            label: 'Pending',
            style: 'background-color: rgba(107, 114, 128, 0.15); color: #374151;',
            icon: 'fas fa-question-circle'
        };
    }

    getSeverityInfo(severity) {
        const lowerSeverity = severity?.toLowerCase() || '';
        switch (lowerSeverity) {
            case 'catastrophic':
            case 'catastrophique':
                return {
                    level: 'G5',
                    label: 'Catastrophic',
                    textStyle: 'color: #ffffff;',
                    bgStyle: 'background-color: #000000;',
                    value: 5
                };
            case 'critical':
            case 'critique':
                return {
                    level: 'G4',
                    label: 'Critical',
                    textStyle: 'color: #ffffff;',
                    bgStyle: 'background-color: #DC2626;',
                    value: 4
                };
            case 'high':
            case 'major':
            case 'majeure':
                return {
                    level: 'G3',
                    label: 'Major',
                    textStyle: 'color: #000000;',
                    bgStyle: 'background-color: #F59E0B;',
                    value: 3
                };
            case 'moderate':
            case 'significative':
                return {
                    level: 'G2',
                    label: 'Moderate',
                    textStyle: 'color: #000000;',
                    bgStyle: 'background-color: #FEF08A;',
                    value: 2
                };
            case 'minor':
            case 'mineure':
                return {
                    level: 'G1',
                    label: 'Minor',
                    textStyle: 'color: #000000;',
                    bgStyle: 'background-color: #67E8F9;',
                    value: 1
                };
            default:
                return {
                    level: 'N/A',
                    label: 'Not defined',
                    textStyle: 'color: #374151;',
                    bgStyle: 'background-color: #E5E7EB;',
                    value: 0
                };
        }
    }

    getSeverityValue(severity) {
        return this.getSeverityInfo(severity).value;
    }

    formatMeasureType(measureType) {
        const measureTypeMap = {
            'preventive': 'Preventive',
            'detective': 'Detective',
            'corrective': 'Corrective',
            'compensatory': 'Compensatory',
            'gouvernance et anticipation': 'Governance',
            'protection': 'Protection',
            'd\u00e9fense': 'Defense',
            'r\u00e9silience': 'Resilience'
        };
        return measureTypeMap[measureType] || measureType || 'N/A';
    }

    getMeasureTypeBadgeStyle(measureType) {
        const styleMap = {
            'preventive': 'background-color: rgba(34, 197, 94, 0.1); color: #15803d; border-color: rgba(34, 197, 94, 0.2);',
            'detective': 'background-color: rgba(59, 130, 246, 0.1); color: #1d4ed8; border-color: rgba(59, 130, 246, 0.2);',
            'corrective': 'background-color: rgba(245, 158, 11, 0.1); color: #d97706; border-color: rgba(245, 158, 11, 0.2);',
            'compensatory': 'background-color: rgba(139, 92, 246, 0.1); color: #7c3aed; border-color: rgba(139, 92, 246, 0.2);',
            'gouvernance et anticipation': 'background-color: rgba(59, 130, 246, 0.1); color: #1d4ed8; border-color: rgba(59, 130, 246, 0.2);',
            'protection': 'background-color: rgba(34, 197, 94, 0.1); color: #15803d; border-color: rgba(34, 197, 94, 0.2);',
            'd\u00e9fense': 'background-color: rgba(245, 158, 11, 0.1); color: #d97706; border-color: rgba(245, 158, 11, 0.2);',
            'r\u00e9silience': 'background-color: rgba(139, 92, 246, 0.1); color: #7c3aed; border-color: rgba(139, 92, 246, 0.2);'
        };
        return styleMap[measureType] || 'background-color: rgba(107, 114, 128, 0.1); color: #374151; border-color: rgba(107, 114, 128, 0.2);';
    }

    getMeasureTypeBadgeStyle(measureType) {
        const typeConfig = {
            'gouvernance et anticipation': 'background-color: rgba(59, 130, 246, 0.15); color: #1e40af;',
            'protection': 'background-color: rgba(16, 185, 129, 0.15); color: #065f46;',
            'd\u00e9fense': 'background-color: rgba(245, 158, 11, 0.15); color: #92400e;',
            'r\u00e9silience': 'background-color: rgba(139, 92, 246, 0.15); color: #5b21b6;'
        };
        return typeConfig[measureType] || 'background-color: rgba(107, 114, 128, 0.15); color: #374151;';
    }

    formatMeasureType(measureType) {
        const typeLabels = {
            'gouvernance et anticipation': 'Gouvernance',
            'protection': 'Protection',
            'd\u00e9fense': 'D\u00e9fense',
            'r\u00e9silience': 'R\u00e9silience'
        };
        return typeLabels[measureType] || measureType || 'Unknown';
    }

    getSeverityBadge(severity) {
        const severityInfo = this.getSeverityInfo(severity);
        return `< span style = "display: inline-flex; align-items: center; padding: 0.25rem 0.5rem; border-radius: 9999px; font-size: 0.7rem; font-weight: 600; ${severityInfo.textStyle} ${severityInfo.bgStyle}" >
    ${severityInfo.level} - ${severityInfo.label}
        </span > `;
    }

    formatDecision(decision, date) {
        const decisions = {
            'planned': '\u00c0 planifier',
            'apply_before': 'Appliquer avant',
            'abandoned': 'Abandonner',
            'accepted': 'Accepter',
            'rejected': 'Rejet\u00e9',
            'pending': 'En attente',
            'implemented': 'Impl\u00e9ment\u00e9'
        };
        const formattedDecision = decisions[decision] || decision || 'En attente';
        if (decision === 'apply_before' && date) {
            return `${formattedDecision} le ${new Date(date).toLocaleDateString('fr-FR')} `;
        } else if (date) {
            return `${formattedDecision} (${new Date(date).toLocaleDateString('fr-FR')})`;
        }
        return formattedDecision;
    }

    calculateRiskReductionPercentage(initialSeverity, residualSeverity) {
        if (!initialSeverity || !residualSeverity) return 'N/A';
        const initialScore = this.mapSeverityToScore(initialSeverity);
        const residualScore = this.mapSeverityToScore(residualSeverity);
        if (initialScore <= 0) return 'N/A';
        const reduction = Math.max(0, ((initialScore - residualScore) / initialScore) * 100);
        return `${Math.round(reduction)}% `;
    }

    calculateResidualGravity(initialRisk, effectiveness) {
        const riskScore = this.mapSeverityToScore(initialRisk);
        const effectivenessScore = this.mapEffectivenessToScore(effectiveness);
        const residualScore = Math.max(1, riskScore - effectivenessScore);
        return this.mapScoreToSeverity(residualScore);
    }

    calculateRiskReductionForControl(effectiveness) {
        const effectivenessScore = this.mapEffectivenessToScore(effectiveness);
        return Math.min(90, effectivenessScore * 20); // Max 90% reduction
    }

    getMeasureTypeIcon(measureType) {
        const iconMap = {
            'preventive': 'fa-shield-alt',
            'detective': 'fa-search',
            'corrective': 'fa-wrench',
            'compensatory': 'fa-balance-scale',
            'gouvernance et anticipation': 'fa-chess-king',
            'protection': 'fa-shield-check',
            'd\u00e9fense': 'fa-sword',
            'r\u00e9silience': 'fa-heart'
        };
        return iconMap[measureType] || 'fa-cog';
    }

    getControlBorderColor(ctrl) {
        if (ctrl.isNewSuggestion) return '#8b5cf6';
        if (ctrl.decision === 'implemented') return '#059669';
        if (ctrl.decision === 'planned') return '#2563eb';
        if (ctrl.decision === 'abandoned') return '#dc2626';
        return '#64748b';
    }

    calculateDangerZone(threatLevel, dependency, penetration) {
        const threat = this.mapThreatToScore(threatLevel);
        const dep = this.mapLevelToScore(dependency);
        const pen = this.mapLevelToScore(penetration);
        const dangerScore = (threat + dep + pen) / 3;

        if (dangerScore >= 4) return 'Zone Rouge';
        if (dangerScore >= 3) return 'Zone Orange';
        if (dangerScore >= 2) return 'Zone Jaune';
        return 'Zone Verte';
    }

    // Utility mapping methods
    mapSeverityToScore(severity) {
        if (!severity || typeof severity !== 'string') {
            return 3;
        }

        const mapping = {
            'tr\u00e8s faible': 1, 'faible': 2, 'moyen': 3, '\u00e9lev\u00e9': 4, 'tr\u00e8s \u00e9lev\u00e9': 5,
            'minor': 1, 'moderate': 2, 'major': 3, 'critical': 4, 'catastrophic': 5
        };
        return mapping[severity.toLowerCase()] || 3;
    }

    mapScoreToSeverity(score) {
        if (typeof score !== 'number' || isNaN(score)) {
            return 'Moyen';
        }

        if (score >= 4.5) return 'Tr\u00e8s \u00e9lev\u00e9';
        if (score >= 3.5) return '\u00c9lev\u00e9';
        if (score >= 2.5) return 'Moyen';
        if (score >= 1.5) return 'Faible';
        return 'Tr\u00e8s faible';
    }

    mapEffectivenessToScore(effectiveness) {
        if (!effectiveness || typeof effectiveness !== 'string') {
            return 3;
        }

        const mapping = {
            'tr\u00e8s faible': 1, 'faible': 2, 'moyen': 3, '\u00e9lev\u00e9': 4, 'tr\u00e8s \u00e9lev\u00e9': 5,
            'low': 2, 'medium': 3, 'high': 4, 'very high': 5
        };
        return mapping[effectiveness.toLowerCase()] || 3;
    }

    mapThreatToScore(threatLevel) {
        return this.mapSeverityToScore(threatLevel);
    }

    mapLevelToScore(level) {
        if (typeof level === 'number') return level;
        return this.mapSeverityToScore(level);
    }

    getDifficultyClass(difficulty) {
        if (!difficulty || typeof difficulty !== 'string') {
            return 'warning';
        }

        switch (difficulty.toLowerCase()) {
            case 'tr\u00e8s difficile':
            case 'very hard':
                return 'success';
            case 'difficile':
            case 'hard':
                return 'info';
            case 'moyenne':
            case 'medium':
                return 'warning';
            case 'facile':
            case 'easy':
                return 'error';
            default:
                return 'warning';
        }
    }

    // Helper methods for executive summary
    getOverallStatus(completedWorkshops) {
        if (completedWorkshops === 5) return 'Analyse compl\u00e8te';
        if (completedWorkshops >= 3) return 'Analyse avanc\u00e9e';
        if (completedWorkshops >= 1) return 'Analyse en cours';
        return 'Analyse initi\u00e9e';
    }

    generateMethodologicalRecommendations(workshops) {
        const recommendations = [];

        // Check workshop completion and provide specific recommendations
        if (!workshops.workshop1 || workshops.workshop1.completionStatus !== 'completed') {
            recommendations.push('<li>💡 Finaliser l\'identification des valeurs m\u00e9tier et \u00e9v\u00e9nements redout\u00e9s (Atelier 1)</li>');
        }

        if (!workshops.workshop2 || workshops.workshop2.completionStatus !== 'completed') {
            recommendations.push('<li>💡 Compl\u00e9ter l\'analyse des sources de risque et parties prenantes (Atelier 2)</li>');
        }

        if (!workshops.workshop3 || workshops.workshop3.completionStatus !== 'completed') {
            recommendations.push('<li>💡 D\u00e9velopper les sc\u00e9narios strat\u00e9giques et chemins d\'attaque (Atelier 3)</li>');
        }

        if (!workshops.workshop4 || workshops.workshop4.completionStatus !== 'completed') {
            recommendations.push('<li>💡 D\u00e9tailler les sc\u00e9narios op\u00e9rationnels (Atelier 4)</li>');
        }

        if (!workshops.workshop5 || workshops.workshop5.completionStatus !== 'completed') {
            recommendations.push('<li>💡 \u00c9tablir les plans de traitement des risques (Atelier 5)</li>');
        }

        // Add general recommendations
        recommendations.push('<li>💡 Mettre en place un processus de r\u00e9vision p\u00e9riodique de l\'analyse</li>');
        recommendations.push('<li>💡 Former les \u00e9quipes sur les sc\u00e9narios de risque identifi\u00e9s</li>');
        recommendations.push('<li>💡 Documenter les d\u00e9cisions de traitement des risques</li>');

        return recommendations.join('');
    }

    // Logo generation methods
    generateLogoImages() {
        // Handle both array format and object format from backend
        let logos = [];
        if (Array.isArray(this.logoData)) {
            logos = this.logoData;
        } else if (this.logoData && this.logoData.header) {
            // Extract logos from header object
            if (this.logoData.header.left) logos.push(this.logoData.header.left);
            if (this.logoData.header.right) logos.push(this.logoData.header.right);
            if (this.logoData.header.company) logos.push(this.logoData.header.company);
        }
        // Always add ACG logo for cover page
        const acgLogo = {
            name: 'ACG',
            url: '/acg-logo.png',
            fallbackText: 'ACG',
            available: true
        };
        if (logos.length === 0) {
            // Fallback to default logos if no logo data available
            return `
    < div style = "display: flex; align-items: center; gap: 2rem;" >
                    <div style="background: rgba(255,255,255,0.2); padding: 1rem; border-radius: 8px; font-weight: bold;">ACG</div>
                    <div style="background: rgba(255,255,255,0.2); padding: 1rem; border-radius: 8px; font-weight: bold;">EBIOS RM</div>
                    <div style="background: rgba(255,255,255,0.2); padding: 1rem; border-radius: 8px; font-weight: bold;">ANSSI</div>
                </div >
    `;
        }
        // Add ACG logo if not already present
        const hasAcg = logos.some(logo => logo.name === 'ACG' || logo.key === 'acg');
        if (!hasAcg) {
            logos.unshift(acgLogo);
        }
        return `< div style = "display: flex; align-items: center; gap: 2rem;" >
    ${logos.filter(logo => logo && logo.available).map(logo => {
            if (logo.base64Data) {
                return `<img src="data:${logo.mimeType || 'image/png'};base64,${logo.base64Data}" alt="${logo.name || 'Logo'}" class="cover-logo" style="max-height: 60px;">`;
            } else if (logo.url) {
                return `<img src="${logo.url}" alt="${logo.name || 'Logo'}" class="cover-logo" style="max-height: 60px;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';"><div style="background: rgba(255,255,255,0.2); padding: 1rem; border-radius: 8px; font-weight: bold; display: none;">${logo.fallbackText || logo.name}</div>`;
            } else if (logo.fallbackText) {
                return `<div style="background: rgba(255,255,255,0.2); padding: 1rem; border-radius: 8px; font-weight: bold;">${logo.fallbackText}</div>`;
            }
            return '';
        }).join('')
            }
        </div > `;
    }

    generateFooterLogos() {
        // Footer logos have been removed as requested
        return '';
    }

    getRiskLevelClass(riskLevel) {
        if (!riskLevel || typeof riskLevel !== 'string') return 'info';
        switch (riskLevel.toLowerCase()) {
            case '\u00e9lev\u00e9':
            case 'high':
                return 'error';
            case 'moyen':
            case 'medium':
                return 'warning';
            case 'faible':
            case 'low':
                return 'success';
            default:
                return 'info';
        }
    }

    getDependencyClass(dependency) {
        if (!dependency || typeof dependency !== 'string') return 'info';
        switch (dependency.toLowerCase()) {
            case '\u00e9lev\u00e9':
            case 'high':
                return 'error';
            case 'moyen':
            case 'medium':
                return 'warning';
            case 'faible':
            case 'low':
                return 'success';
            default:
                return 'info';
        }
    }

    getPenetrationClass(penetration) {
        if (!penetration || typeof penetration !== 'string') return 'info';
        switch (penetration.toLowerCase()) {
            case '\u00e9lev\u00e9':
            case 'high':
                return 'error';
            case 'moyen':
            case 'medium':
                return 'warning';
            case 'faible':
            case 'low':
                return 'success';
            default:
                return 'info';
        }
    }

    getMaturityClass(maturity) {
        if (!maturity || typeof maturity !== 'string') return 'info';
        switch (maturity.toLowerCase()) {
            case '\u00e9lev\u00e9':
            case 'high':
                return 'success';
            case 'moyen':
            case 'medium':
                return 'warning';
            case 'faible':
            case 'low':
                return 'error';
            default:
                return 'info';
        }
    }

    getTrustLevelClass(trustLevel) {
        if (!trustLevel || typeof trustLevel !== 'string') return 'info';
        switch (trustLevel.toLowerCase()) {
            case '\u00e9lev\u00e9':
            case 'high':
                return 'success';
            case 'moyen':
            case 'medium':
                return 'warning';
            case 'faible':
            case 'low':
                return 'error';
            default:
                return 'info';
        }
    }

    getDangerZoneClass(threatLevel, dependency, penetration) {
        const dangerZone = this.calculateDangerZone(threatLevel, dependency, penetration);
        if (!dangerZone || typeof dangerZone !== 'string') return 'info';
        switch (dangerZone.toLowerCase()) {
            case 'critique':
            case 'critical':
                return 'error';
            case '\u00e9lev\u00e9':
            case 'high':
                return 'warning';
            case 'moyen':
            case 'medium':
                return 'info';
            case 'faible':
            case 'low':
                return 'success';
            default:
                return 'info';
        }
    }

    getSeverityClass(severity) {
        if (!severity || typeof severity !== 'string') return 'info';
        switch (severity.toLowerCase()) {
            case 'critique':
            case 'critical':
            case 'tr\u00e8s \u00e9lev\u00e9':
            case 'very high':
                return 'error';
            case '\u00e9lev\u00e9':
            case 'high':
                return 'warning';
            case 'moyen':
            case 'medium':
                return 'info';
            case 'faible':
            case 'low':
                return 'success';
            default:
                return 'info';
        }
    }

    getLikelihoodClass(likelihood) {
        if (!likelihood || typeof likelihood !== 'string') return 'info';
        switch (likelihood.toLowerCase()) {
            case 'tr\u00e8s probable':
            case 'very likely':
            case '\u00e9lev\u00e9':
            case 'high':
            case 'quasi certain':
            case 'almost certain':
                return 'error';
            case 'probable':
            case 'likely':
            case 'moyen':
            case 'medium':
            case 'vraisemblable':
                return 'warning';
            case 'possible':
            case 'possible':
            case 'faible':
            case 'low':
            case 'peu vraisemblable':
            case 'unlikely':
                return 'info';
            case 'improbable':
            case 'unlikely':
            case 'tr\u00e8s faible':
            case 'very low':
                return 'success';
            default:
                return 'info';
        }
    }

    getVraisemblanceClass(vraisemblance) {
        if (!vraisemblance || typeof vraisemblance !== 'string') return 'info';
        switch (vraisemblance.toLowerCase()) {
            case 'forte':
            case '\u00e9lev\u00e9e':
            case 'high':
            case 'quasi certain':
            case 'almost certain':
                return 'error';
            case 'moyenne':
            case 'moyen':
            case 'medium':
            case 'tr\u00e8s vraisemblable':
            case 'very likely':
                return 'warning';
            case 'faible':
            case 'low':
            case 'vraisemblable':
            case 'likely':
                return 'success';
            case 'peu vraisemblable':
            case 'unlikely':
                return 'info';
            default:
                return 'info';
        }
    }

    getSeverityInfo(severity) {
        const lowerSeverity = severity?.toLowerCase() || '';
        switch (lowerSeverity) {
            case 'catastrophic':
                return { level: 'G5', label: 'Catastrophique', textClass: 'text-white', bgClass: 'bg-black', value: 5 };
            case 'critical':
                return { level: 'G4', label: 'Critique', textClass: 'text-white', bgClass: 'bg-red-600', value: 4 };
            case 'high':
            case 'major':
                return { level: 'G3', label: 'Majeure', textClass: 'text-black', bgClass: 'bg-yellow-500', value: 3 };
            case 'moderate':
            case 'significative':
                return { level: 'G2', label: 'Significative', textClass: 'text-black', bgClass: 'bg-yellow-200', value: 2 };
            case 'minor':
            case 'mineure':
                return { level: 'G1', label: 'Mineure', textClass: 'text-black', bgClass: 'bg-cyan-300', value: 1 };
            default:
                return { level: 'N/A', label: 'Non d\u00e9fini', textClass: 'text-gray-800', bgClass: 'bg-gray-200', value: 0 };
        }
    }

    getSeverityLabel(severity) {
        const info = this.getSeverityInfo(severity);
        return info.label;
    }

    getSeverityBoxStyle(severityInfo) {
        if (!severityInfo) return 'background-color: #e5e7eb; color: #374151;';
        switch (severityInfo.level) {
            case 'G5':
                return 'background-color: #000000; color: white;';
            case 'G4':
                return 'background-color: #dc2626; color: white;';
            case 'G3':
                return 'background-color: #f59e0b; color: black;';
            case 'G2':
                return 'background-color: #fef08a; color: black;';
            case 'G1':
                return 'background-color: #67e8f9; color: black;';
            default:
                return 'background-color: #e5e7eb; color: #374151;';
        }
    }

    getUniqueCategories(sources) {
        const categories = sources.map(s => s.category || s.type).filter(Boolean);
        return [...new Set(categories)];
    }

    getCategoryDisplayName(category) {
        if (!category) return 'Cat\u00e9gorie non d\u00e9finie';
        const categoryMap = {
            'etatique': '🏛️ \u00c9TATIQUE',
            'criminel': '🔪 CRIME ORGANIS\u00c9',
            'crime_organise': '🔪 CRIME ORGANIS\u00c9',
            'terroriste': '💣 TERRORISTE',
            'activiste': '✊ ACTIVISTE ID\u00c9OLOGIQUE',
            'activiste_ideologique': '✊ ACTIVISTE ID\u00c9OLOGIQUE',
            'officine': '🕵️ OFFICINE SP\u00c9CIALIS\u00c9E',
            'officine_specialisee': '🕵️ OFFICINE SP\u00c9CIALIS\u00c9E',
            'amateur': '🎮 AMATEUR',
            'vengeur': '😡 VENGEUR',
            'malveillant': '🦹 MALVEILLANT PATHOLOGIQUE',
            'malveillant_pathologique': '🦹 MALVEILLANT PATHOLOGIQUE'
        };
        const lowerCategory = category.toLowerCase();
        return categoryMap[lowerCategory] || category.charAt(0).toUpperCase() + category.slice(1);
    }

    getThreatLevelClass(level) {
        if (!level) return 'info';
        const levelStr = String(level).toLowerCase();
        switch (levelStr) {
            case 'critique':
            case 'critical':
            case '\u00e9lev\u00e9':
            case 'high':
                return 'error';
            case 'moyen':
            case 'medium':
            case 'mod\u00e9r\u00e9':
            case 'moderate':
                return 'warning';
            case 'faible':
            case 'low':
                return 'success';
            default:
                return 'info';
        }
    }

    getResourcesClass(resources) {
        if (!resources) return 'info';
        const resourceStr = String(resources).toLowerCase();
        switch (resourceStr) {
            case 'importantes':
                return 'error';
            case 'moyennes':
                return 'warning';
            case 'faibles':
                return 'success';
            default:
                return 'info';
        }
    }

    getActivityClass(activity) {
        if (!activity) return 'info';
        const activityStr = String(activity).toLowerCase();
        switch (activityStr) {
            case 'eleve':
            case '\u00e9lev\u00e9':
                return 'error';
            case 'moyen':
                return 'warning';
            case 'faible':
                return 'success';
            default:
                return 'info';
        }
    }

    getMotivationClass(motivation) {
        if (!motivation) return 'info';
        const motivationStr = String(motivation).toLowerCase();
        switch (motivationStr) {
            case 'eleve':
            case '\u00e9lev\u00e9':
                return 'error';
            case 'moyen':
                return 'warning';
            case 'faible':
                return 'success';
            default:
                return 'info';
        }
    }

    getNiveauClass(niveau) {
        if (!niveau) return 'info';
        const niveauNum = Number(niveau);
        switch (niveauNum) {
            case 4:
                return 'error';
            case 3:
                return 'warning';
            case 2:
                return 'info';
            case 1:
                return 'success';
            default:
                return 'info';
        }
    }

    getNiveauClass(niveau) {
        if (!niveau && niveau !== 0) return 'info';
        const numNiveau = parseInt(String(niveau));
        if (isNaN(numNiveau)) return 'info';
        if (numNiveau >= 3) return 'error';
        if (numNiveau >= 2) return 'warning';
        return 'success';
    }

    getCriticalityClass(criticality) {
        if (!criticality) return 'info';
        const criticalityStr = String(criticality).toLowerCase();
        switch (criticalityStr) {
            case 'critique':
            case 'critical':
            case '\u00e9lev\u00e9':
            case 'high':
                return 'error';
            case 'moyen':
            case 'medium':
            case 'mod\u00e9r\u00e9':
            case 'moderate':
                return 'warning';
            case 'faible':
            case 'low':
                return 'success';
            default:
                return 'info';
        }
    }

    getDisplayValue(value) {
        if (!value) return 'Non d\u00e9fini';

        const normalizedValue = String(value).toLowerCase();

        // Handle motivation/activit\u00e9 values
        if (normalizedValue === 'faible') return 'Faible';
        if (normalizedValue === 'moyen') return 'Moyen';
        if (normalizedValue === 'eleve' || normalizedValue === '\u00e9lev\u00e9') return '\u00c9lev\u00e9';

        // Handle ressources values
        if (normalizedValue === 'faibles') return 'Faibles';
        if (normalizedValue === 'moyennes') return 'Moyennes';
        if (normalizedValue === 'importantes') return 'Importantes';

        return value;
    }

    // Helper methods for Workshop 3 ecosystem measures
    getCategoryClass(category) {
        if (!category) return 'info';
        const categoryStr = String(category).toLowerCase();
        switch (categoryStr) {
            case 'protection':
                return 'success';
            case 'defense':
                return 'warning';
            case 'governance':
                return 'info';
            default:
                return 'info';
        }
    }

    formatCategory(category) {
        if (!category) return 'Non d\u00e9finie';
        const categoryMap = {
            'protection': 'Protection',
            'defense': 'D\u00e9fense',
            'governance': 'Gouvernance'
        };
        return categoryMap[category.toLowerCase()] || category;
    }

    formatPriority(priority) {
        if (!priority) return 'Non d\u00e9finie';
        const priorityMap = {
            'high': '\u00c9lev\u00e9e',
            'medium': 'Moyenne',
            'low': 'Faible'
        };
        return priorityMap[priority.toLowerCase()] || priority;
    }

    formatImplementation(implementation) {
        if (!implementation) return 'Non d\u00e9fini';
        const implementationMap = {
            'implemented': 'Impl\u00e9ment\u00e9',
            'planned': 'Planifi\u00e9',
            'not-planned': 'Non planifi\u00e9'
        };
        return implementationMap[implementation.toLowerCase()] || implementation;
    }

    generateStarRating(effectiveness) {
        const rating = parseInt(effectiveness) || 0;
        const maxStars = 5;
        let stars = '';

        for (let i = 1; i <= maxStars; i++) {
            if (i <= rating) {
                stars += '<span style="color: #f59e0b;">★</span>';
            } else {
                stars += '<span style="color: #d1d5db;">☆</span>';
            }
        }

        return stars;
    }

    getRiskReductionClass(riskReduction) {
        if (!riskReduction) return 'info';
        const reductionStr = String(riskReduction).toLowerCase();
        switch (reductionStr) {
            case 'high':
                return 'success';
            case 'medium':
                return 'warning';
            case 'low':
                return 'error';
            default:
                return 'info';
        }
    }

    // Helper methods for Workshop 4 operational scenarios
    getPhaseClass(phase) {
        if (!phase) return 'info';
        const phaseStr = String(phase).toLowerCase();
        switch (phaseStr) {
            case 'connaitre':
                return 'info';
            case 'rentrer':
                return 'warning';
            case 'trouver':
                return 'error';
            case 'exploiter':
                return 'success';
            default:
                return 'info';
        }
    }

    getSkillsClass(skills) {
        if (!skills) return 'info';
        const skillsStr = String(skills).toLowerCase();
        switch (skillsStr) {
            case 'avanc\u00e9':
            case 'advanced':
                return 'error';
            case 'interm\u00e9diaire':
            case 'intermediate':
                return 'warning';
            case 'd\u00e9butant':
            case 'beginner':
                return 'success';
            default:
                return 'info';
        }
    }

    getDifficultyLevelClass(difficulty) {
        if (!difficulty) return 'info';
        const difficultyNum = parseInt(difficulty);
        if (isNaN(difficultyNum)) return 'info';

        if (difficultyNum >= 4) return 'error';
        if (difficultyNum >= 3) return 'warning';
        if (difficultyNum >= 2) return 'info';
        return 'success';
    }

    // Helper methods for Workshop 5 comprehensive display
    generateTreatmentStrategiesBreakdown(treatmentStrategies) {
        const strategies = {
            'avoid': { label: '\u00c9viter', color: 'var(--error-color)', icon: 'fas fa-ban' },
            'mitigate': { label: 'R\u00e9duire', color: 'var(--warning-color)', icon: 'fas fa-shield-alt' },
            'transfer': { label: 'Transf\u00e9rer', color: 'var(--info-color)', icon: 'fas fa-exchange-alt' },
            'accept': { label: 'Accepter', color: 'var(--success-color)', icon: 'fas fa-check-circle' }
        };

        if (!treatmentStrategies || Object.keys(treatmentStrategies).length === 0) {
            return '<div class="info-box warning">Aucune strat\u00e9gie de traitement d\u00e9finie.</div>';
        }

        return `
    < div style = "display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem; margin-bottom: 2rem;" >
        ${Object.entries(treatmentStrategies).map(([strategyKey, scenarios]) => {
            const strategy = strategies[strategyKey] || { label: strategyKey, color: 'var(--text-secondary)', icon: 'fas fa-cog' };
            return `
                <div class="info-box" style="border-left: 4px solid ${strategy.color};">
                    <h4 style="color: ${strategy.color}; margin: 0 0 1rem 0; display: flex; align-items: center; gap: 0.5rem;">
                        <i class="${strategy.icon}"></i> ${strategy.label} (${scenarios.length})
                    </h4>
                    ${scenarios.length > 0 ? `
                    <div style="max-height: 200px; overflow-y: auto;">
                        ${scenarios.map(scenario => `
                        <div style="padding: 0.5rem 0; border-bottom: 1px solid var(--light-border); font-size: 0.9rem;">
                            <div style="font-weight: 600;">${scenario.referenceCode}: ${scenario.scenarioName}</div>
                            <div style="color: var(--text-secondary); font-size: 0.8rem;">Risque: ${scenario.riskScore}, Gravit\u00e9: ${scenario.gravity}, Mesures: ${scenario.measures}</div>
                            ${scenario.justification ? `<div style="font-style: italic; color: var(--text-secondary); font-size: 0.75rem;">${scenario.justification}</div>` : ''}
                        </div>
                        `).join('')}
                    </div>
                    ` : '<div style="font-style: italic; color: var(--text-secondary);">Aucun sc\u00e9nario</div>'}
                </div>
                `;
        }).join('')
            }
        </div > `;
    }

    generateStrategicScenariosWithTreatment(strategicScenarios, treatmentPlans) {
        if (!strategicScenarios || strategicScenarios.length === 0) {
            return '<div class="info-box warning">Aucun sc\u00e9nario strat\u00e9gique d\u00e9fini.</div>';
        }

        return `
    < div class="table-container" >
        <table>
            <thead>
                <tr>
                    <th style="width: 10%;">R\u00e9f</th>
                    <th style="width: 25%;">Sc\u00e9nario</th>
                    <th style="width: 15%;">Source / Objectif</th>
                    <th style="width: 10%;">Gravit\u00e9</th>
                    <th style="width: 15%;">Strat\u00e9gie</th>
                    <th style="width: 10%;">Mesures</th>
                    <th style="width: 15%;">Efficacit\u00e9</th>
                </tr>
            </thead>
            <tbody>
                ${strategicScenarios.map(scenario => {
            const treatmentPlan = Object.values(treatmentPlans).find(tp => tp.scenarioId === scenario.id);
            return `
                        <tr>
                            <td style="font-weight: 600; text-align: center;">${scenario.referenceCode || 'N/A'}</td>
                            <td>
                                <div style="font-weight: 600; color: var(--text-primary);">${scenario.name}</div>
                                <div style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 0.25rem;">${scenario.description || ''}</div>
                            </td>
                            <td style="font-size: 0.8rem;">
                                <div><strong>Source:</strong> ${scenario.sourceRiskName || 'N/A'}</div>
                                <div><strong>Objectif:</strong> ${scenario.objectifVise || 'N/A'}</div>
                            </td>
                            <td style="text-align: center;">
                                <span class="status-badge ${this.getGravityClass(scenario.gravity)}" style="font-size: 0.75rem;">${scenario.gravity || 'N/A'}</span>
                            </td>
                            <td style="text-align: center;">
                                ${treatmentPlan ? `<span class="status-badge ${this.getTreatmentStrategyClass(treatmentPlan.treatmentStrategy)}" style="font-size: 0.75rem;">${this.formatTreatmentStrategy(treatmentPlan.treatmentStrategy)}</span>` : '<span style="color: var(--text-secondary);">N/A</span>'}
                            </td>
                            <td style="text-align: center;">
                                ${treatmentPlan ? `
                                <div style="font-weight: 600;">${this.getTotalMeasuresCount(treatmentPlan.measures)}</div>
                                ` : '0'}
                            </td>
                            <td style="text-align: center;">
                                ${treatmentPlan && treatmentPlan.effectiveness ? `
                                <div style="font-weight: 600; color: var(--success-color);">${treatmentPlan.effectiveness.overallEffectiveness || 0}%</div>
                                ` : '<span style="color: var(--text-secondary);">N/A</span>'}
                            </td>
                        </tr>
                        `;
        }).join('')}
            </tbody>
        </table>
        </div > `;
    }

    // Additional helper methods for Workshop 5
    generateDetailedTreatmentPlans(treatmentPlans) {
        if (!treatmentPlans || Object.keys(treatmentPlans).length === 0) {
            return '<div class="info-box warning">Aucun plan de traitement d\u00e9taill\u00e9 disponible.</div>';
        }

        return Object.values(treatmentPlans).slice(0, 5).map(plan => `
    < div class="info-box ai" style = "margin-bottom: 1.5rem;" >
            <h4>${plan.scenarioName} (${plan.referenceCode})</h4>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 0.75rem; font-size: 0.9rem;">
                <div><strong>Strat\u00e9gie:</strong> ${this.formatTreatmentStrategy(plan.treatmentStrategy)}</div>
                <div><strong>Priorit\u00e9:</strong> ${plan.implementation?.priority || 'N/A'}</div>
                <div><strong>Co\u00fbt:</strong> ${this.formatCost(plan.implementation?.estimatedCost)}</div>
                <div><strong>Efficacit\u00e9:</strong> ${plan.effectiveness?.overallEffectiveness || 0}%</div>
            </div>
        </div > `).join('') + (Object.keys(treatmentPlans).length > 5 ? '<div style="text-align: center; color: var(--text-secondary); font-size: 0.9rem;">... et plus de plans de traitement</div>' : '');
    }

    generateEcosystemMeasuresIntegration(ecosystemMeasures) {
        if (!ecosystemMeasures || ecosystemMeasures.length === 0) {
            return '<div class="info-box warning">Aucune mesure d\'\u00e9cosyst\u00e8me int\u00e9gr\u00e9e.</div>';
        }

        return `
    < div class="table-container" >
        <table>
            <thead>
                <tr>
                    <th>Titre</th>
                    <th>Cat\u00e9gorie</th>
                    <th>Priorit\u00e9</th>
                    <th>Efficacit\u00e9</th>
                    <th>Impl\u00e9mentation</th>
                </tr>
            </thead>
            <tbody>
                ${ecosystemMeasures.slice(0, 10).map(measure => `
                    <tr>
                        <td style="font-weight: 600;">${measure.title}</td>
                        <td><span class="status-badge ${this.getCategoryClass(measure.category)}">${this.formatCategory(measure.category)}</span></td>
                        <td><span class="status-badge ${this.getPriorityClass(measure.priority)}">${this.formatPriority(measure.priority)}</span></td>
                        <td style="text-align: center;">${this.generateStarRating(measure.effectiveness || 0)}</td>
                        <td><span class="status-badge ${this.getImplementationClass(measure.implementation)}">${this.formatImplementation(measure.implementation)}</span></td>
                    </tr>
                    `).join('')}
            </tbody>
        </table>
        </div >
    ${ecosystemMeasures.length > 10 ? `<div style="text-align: center; color: var(--text-secondary); font-size: 0.9rem; margin-top: 0.5rem;">... et ${ecosystemMeasures.length - 10} autres mesures</div>` : ''} `;
    }

    generateResidualRisksAnalysis(residualRisks, operationalScenarios) {
        if (!residualRisks || residualRisks.length === 0) {
            return '<div class="info-box warning">Aucune analyse de risque r\u00e9siduel disponible.</div>';
        }

        return `
        < div class="table-container" >
            <table>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Sc\u00e9nario</th>
                        <th>Chronologie</th>
                        <th>Statut</th>
                    </tr>
                </thead>
                <tbody>
                    ${residualRisks.map(risk => `
                    <tr>
                        <td><span class="status-badge info">${this.formatResidualRiskType(risk.type)}</span></td>
                        <td style="font-weight: 500;">${risk.scenarioName}</td>
                        <td style="text-align: center;">${risk.timeline || 'N/A'}</td>
                        <td style="text-align: center;">
                            <span class="status-badge ${risk.accepted ? 'completed' : 'warning'}">${risk.accepted ? 'Accept\u00e9' : 'En analyse'}</span>
                        </td>
                    </tr>
                    `).join('')}
                </tbody>
            </table>
        </div > `;
    }

    generateMonitoringFramework(monitoringFramework) {
        if (!monitoringFramework || Object.keys(monitoringFramework).length === 0) {
            return '<div class="info-box warning">Aucun cadre de surveillance d\u00e9fini.</div>';
        }

        const kris = monitoringFramework.keyRiskIndicators || [];
        const reporting = monitoringFramework.reporting || {};

        return `
        ${kris.length > 0 ? `
        <h3>Indicateurs Cl\u00e9s de Risque</h3>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.5rem;">
            ${kris.map(kri => `
            <div class="info-box">
                <h4 style="margin: 0 0 0.5rem 0;">${kri.name}</h4>
                <div style="font-size: 1.2rem; font-weight: 600; color: ${this.getKRIStatusColor(kri.status)};">${kri.value}</div>
                <div style="font-size: 0.8rem; color: var(--text-secondary);">Seuil: ${kri.threshold}</div>
            </div>
            `).join('')}
        </div>
        ` : ''
            }
        ${reporting && Object.keys(reporting).length > 0 ? `
        <h3>Param\u00e8tres de Reporting</h3>
        <div class="info-box">
            ${reporting.dashboardMetrics ? `<p><strong>Risques r\u00e9siduels:</strong> ${reporting.dashboardMetrics.totalResidualRisks || 0}</p>` : ''}
            ${reporting.reportingFrequency ? `<p><strong>Fr\u00e9quence:</strong> Ex\u00e9cutif (${reporting.reportingFrequency.executive}), Op\u00e9rationnel (${reporting.reportingFrequency.operational})</p>` : ''}
        </div>
        ` : ''
            } `;
    }

    // Helper methods for formatting
    getGravityClass(gravity) {
        const gravityNum = parseInt(gravity);
        if (isNaN(gravityNum)) return 'info';
        if (gravityNum >= 5) return 'error';
        if (gravityNum >= 4) return 'warning';
        if (gravityNum >= 3) return 'info';
        return 'success';
    }

    getTreatmentStrategyClass(strategy) {
        switch (strategy?.toLowerCase()) {
            case 'avoid': return 'error';
            case 'mitigate': return 'warning';
            case 'transfer': return 'info';
            case 'accept': return 'success';
            default: return 'info';
        }
    }

    formatTreatmentStrategy(strategy) {
        const strategies = {
            'avoid': '\u00c9viter',
            'mitigate': 'R\u00e9duire',
            'transfer': 'Transf\u00e9rer',
            'accept': 'Accepter'
        };
        return strategies[strategy?.toLowerCase()] || strategy || 'N/A';
    }

    getTotalMeasuresCount(measures) {
        if (!measures) return 0;
        return (measures.preventive?.length || 0) + (measures.detective?.length || 0) + (measures.corrective?.length || 0);
    }

    formatCost(cost) {
        if (!cost || cost === 0) return 'Gratuit';
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0
        }).format(cost);
    }

    formatResidualRiskType(type) {
        const types = {
            'timeline': 'Chronologie',
            'impact': 'Impact',
            'likelihood': 'Vraisemblance'
        };
        return types[type?.toLowerCase()] || type || 'G\u00e9n\u00e9ral';
    }

    getKRIStatusColor(status) {
        switch (status?.toLowerCase()) {
            case 'good': return 'var(--success-color)';
            case 'warning': return 'var(--warning-color)';
            case 'critical': return 'var(--error-color)';
            default: return 'var(--text-secondary)';
        }
    }

    /**
     * Format implementation status for display
     * @param {string} status - Implementation status
     * @returns {string} Formatted status
     */
    formatImplementationStatus(status) {
        const statuses = {
            'implemented': 'Impl\u00e9ment\u00e9',
            'in_progress': 'En cours',
            'planned': 'Planifi\u00e9',
            'not_planned': 'Non planifi\u00e9',
            'reduce': 'R\u00e9duire',
            'accept': 'Accepter',
            'transfer': 'Transf\u00e9rer',
            'avoid': '\u00c9viter'
        };
        return statuses[status] || status || 'Non d\u00e9fini';
    }

    /**
     * Get CSS class for implementation status
     * @param {string} status - Implementation status
     * @returns {string} CSS class
     */
    getImplementationClass(status) {
        const classes = {
            'implemented': 'completed',
            'in_progress': 'in-progress',
            'planned': 'warning',
            'not_planned': 'not-started',
            'reduce': 'info',
            'accept': 'warning',
            'transfer': 'secondary',
            'avoid': 'error'
        };
        return classes[status] || 'secondary';
    }

    /**
     * Get CSS class for priority level
     * @param {string} priority - Priority level
     * @returns {string} CSS class
     */
    getPriorityClass(priority) {
        const classes = {
            'critical': 'error',
            'high': 'warning',
            'medium': 'info',
            'low': 'secondary'
        };
        return classes[priority] || 'info';
    }

    /**
     * Generate enhanced couples SR/OV evaluation table
     * This method creates a comprehensive table matching the EvaluationTable component
     */
    generateEnhancedCouplesSROVTable(couplesSROV, riskSources, objectives) {
        console.log('🔥 ===== ENHANCED COUPLES SR/OV TABLE GENERATION =====');
        console.log('📋 [Enhanced CouplesSROV Report] Input parameters received:');
        console.log('  🎯 couplesSROV:', couplesSROV);
        console.log('  🛡️ riskSources:', riskSources);
        console.log('  🔒 objectives:', objectives);

        // This section has been removed as requested - "\u00c9valuation des couples SR/OV" is no longer displayed in workshop 5
        return '';
    }

    /**
     * Get evaluation badge for couples SR/OV (ressources, activite, motivation)
     */
    getCoupleEvaluationBadge(value, type) {
        if (!value) {
            return '<span style="padding: 0.25rem 0.75rem; background: #f3f4f6; color: #6b7280; border-radius: 20px; font-size: 0.8rem; font-weight: 500;">Non \u00e9valu\u00e9</span>';
        }

        const normalizedValue = String(value).toLowerCase();

        // For ressources
        if (type === 'ressources') {
            if (normalizedValue.includes('import') || normalizedValue === 'importantes') {
                return '<span style="padding: 0.25rem 0.75rem; background: #fecaca; color: #991b1b; border-radius: 20px; font-size: 0.8rem; font-weight: 600;">Importantes</span>';
            }
            if (normalizedValue === 'moyennes') {
                return '<span style="padding: 0.25rem 0.75rem; background: #fed7aa; color: #9a3412; border-radius: 20px; font-size: 0.8rem; font-weight: 600;">Moyennes</span>';
            }
            if (normalizedValue === 'faibles') {
                return '<span style="padding: 0.25rem 0.75rem; background: #bbf7d0; color: #166534; border-radius: 20px; font-size: 0.8rem; font-weight: 600;">Faibles</span>';
            }
        }

        // For activite and motivation
        if (normalizedValue.includes('elev') || normalizedValue === '\u00e9lev\u00e9') {
            return '<span style="padding: 0.25rem 0.75rem; background: #fecaca; color: #991b1b; border-radius: 20px; font-size: 0.8rem; font-weight: 600;">\u00c9lev\u00e9</span>';
        }
        if (normalizedValue === 'moyen') {
            return '<span style="padding: 0.25rem 0.75rem; background: #fed7aa; color: #9a3412; border-radius: 20px; font-size: 0.8rem; font-weight: 600;">Moyen</span>';
        }
        if (normalizedValue === 'faible') {
            return '<span style="padding: 0.25rem 0.75rem; background: #bbf7d0; color: #166534; border-radius: 20px; font-size: 0.8rem; font-weight: 600;">Faible</span>';
        }

        // Default case
        return `< span style = "padding: 0.25rem 0.75rem; background: #f3f4f6; color: #374151; border-radius: 20px; font-size: 0.8rem; font-weight: 500;" > ${value}</span > `;
    }

    /**
     * Get niveau badge for couples SR/OV
     */
    getNiveauBadge(niveau) {
        if (!niveau) {
            return '<span style="padding: 0.4rem 0.8rem; background: #f3f4f6; color: #6b7280; border-radius: 20px; font-size: 0.85rem; font-weight: 600;">Non \u00e9valu\u00e9</span>';
        }

        const niveauNum = parseInt(niveau);

        if (niveauNum === 1) {
            return '<span style="padding: 0.4rem 0.8rem; background: #bbf7d0; color: #166534; border-radius: 20px; font-size: 0.85rem; font-weight: 700; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">1</span>';
        }
        if (niveauNum === 2) {
            return '<span style="padding: 0.4rem 0.8rem; background: #fef3c7; color: #92400e; border-radius: 20px; font-size: 0.85rem; font-weight: 700; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">2</span>';
        }
        if (niveauNum === 3) {
            return '<span style="padding: 0.4rem 0.8rem; background: #fed7aa; color: #9a3412; border-radius: 20px; font-size: 0.85rem; font-weight: 700; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">3</span>';
        }
        if (niveauNum === 4) {
            return '<span style="padding: 0.4rem 0.8rem; background: #fecaca; color: #991b1b; border-radius: 20px; font-size: 0.85rem; font-weight: 700; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">4</span>';
        }

        return `< span style = "padding: 0.4rem 0.8rem; background: #f3f4f6; color: #374151; border-radius: 20px; font-size: 0.85rem; font-weight: 600;" > ${niveau}</span > `;
    }

    /**
     * Get retenu (selected) badge for couples SR/OV
     */
    getRetenuBadge(selected) {
        if (selected) {
            return '<span style="padding: 0.4rem 0.8rem; background: #dc2626; color: white; border-radius: 20px; font-size: 0.85rem; font-weight: 700; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"><i class="fas fa-check" style="margin-right: 0.3rem;"></i>Oui</span>';
        } else {
            return '<span style="padding: 0.4rem 0.8rem; background: #6b7280; color: white; border-radius: 20px; font-size: 0.85rem; font-weight: 700; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"><i class="fas fa-times" style="margin-right: 0.3rem;"></i>Non</span>';
        }
    }

    /**
     * Generate stakeholder radar visualization from capture
     * Uses the actual visualization from Workshop 3 Activity 1
     */
    async generateStakeholderRadarVisualizationFromCapture(stakeholders) {
        console.log('🔥 ===== STAKEHOLDER RADAR VISUALIZATION FROM CAPTURE =====');
        console.log('📋 [Stakeholder Radar] Input stakeholders:', stakeholders);

        try {
            let imageDataUrl = null;
            let captureMethod = 'unknown';

            // Method 1: Try to get saved stakeholder radar image from database
            const analysisId = this.analysisId;
            console.log('🔍 Report Generator - Analysis ID:', analysisId);
            if (analysisId) {
                try {
                    console.log('📸 Attempting to retrieve saved stakeholder radar visualization from database...');
                    // Import the service directly
                    const stakeholderService = await import('../services/stakeholderService');
                    const service = stakeholderService.default || stakeholderService;
                    console.log('📦 Service imported:', !!service, 'has method:', !!service?.getStakeholderRadarImage);
                    if (service && service.getStakeholderRadarImage) {
                        const response = await service.getStakeholderRadarImage(analysisId);
                        if (response && response.success && response.data && response.data.imageData) {
                            imageDataUrl = response.data.imageData;
                            captureMethod = 'database';
                            console.log('✅ Stakeholder radar visualization retrieved from database successfully, length:', imageDataUrl.length);
                        } else {
                            console.log('⚠️ No stakeholder radar visualization found in database');
                        }
                    }
                } catch (error) {
                    console.warn('⚠️ Failed to retrieve stakeholder radar from database:', error);
                }
            } else {
                console.warn('⚠️ No analysisId available for stakeholder radar retrieval');
            }

            // Method 2: Try the component capture function (if available in the future)
            if (!imageDataUrl && window.captureStakeholderRadarVisualization && typeof window.captureStakeholderRadarVisualization === 'function') {
                console.log('✅ Found stakeholder radar visualization capture function');
                try {
                    console.log('📸 Capturing stakeholder radar visualization using component function...');
                    imageDataUrl = await window.captureStakeholderRadarVisualization();
                    captureMethod = 'component';
                    console.log('✅ Stakeholder radar visualization captured successfully using component function');
                } catch (error) {
                    console.warn('⚠️ Component capture failed:', error);
                }
            }

            if (imageDataUrl) {
                const captureMethodText = captureMethod === 'database' ? 'depuis la base de donn\u00e9es' :
                    captureMethod === 'component' ? 'directement depuis l\'interface' :
                        'automatiquement par capture d\'\u00e9cran';

                return `
                    <div class="radar-visualization-container" style="margin: 2rem 0; text-align: center;">
                        <h3 style="color: #4f46e5; margin-bottom: 1rem;">
                            <i class="fas fa-radar-chart"></i> Radar des Parties Prenantes
                        </h3>
                        <div class="radar-image-wrapper" style="background: white; padding: 1rem; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); display: inline-block;">
                            <img src="${imageDataUrl}" 
                                 alt="Radar des Parties Prenantes - Atelier 3" 
                                 style="max-width: 100%; height: auto; border-radius: 4px;" />
                        </div>
                        <p style="font-size: 0.9rem; color: #6b7280; margin-top: 0.5rem; font-style: italic;">
                            Visualisation captur\u00e9e ${captureMethodText}
                        </p>
                    </div>
                `;
            } else {
                console.log('⚠️ No stakeholder radar visualization could be captured');
                return `
                    <div class="info-box warning" style="margin: 2rem 0;">
                        <h3><i class="fas fa-exclamation-triangle"></i> Radar des Parties Prenantes</h3>
                        <p>La visualisation radar des parties prenantes n'est pas disponible. Veuillez vous assurer que l'Atelier 3 a \u00e9t\u00e9 compl\u00e9t\u00e9 et sauvegard\u00e9.</p>
                    </div>
                `;
            }
        } catch (error) {
            console.error('❌ Error generating stakeholder radar visualization:', error);
            return `
                <div class="info-box error" style="margin: 2rem 0;">
                    <h3><i class="fas fa-exclamation-circle"></i> Erreur de Visualisation</h3>
                    <p>Une erreur s'est produite lors de la g\u00e9n\u00e9ration du radar des parties prenantes: ${error.message}</p>
                </div>
            `;
        }
    }

    /**
     * Generate radar visualization from captured component
     * Uses the actual visualization from Workshop 2 Activity 2
     */
    async generateRadarVisualizationFromCapture(couplesSROV) {
        console.log('🔥 ===== RADAR VISUALIZATION FROM CAPTURE =====');
        console.log('📋 [Radar Visualization] Input couples:', couplesSROV);

        try {
            let imageDataUrl = null;
            let captureMethod = 'unknown';

            // Method 1: Try to get saved radar image from database
            const analysisId = this.analysisId;
            console.log('🔍 Report Generator - Analysis ID:', analysisId);
            if (analysisId) {
                try {
                    console.log('📸 Attempting to retrieve saved radar visualization from database...');
                    // Import the service directly
                    const atelier2Service = await import('../services/atelier2Service');
                    const service = atelier2Service.default || atelier2Service;
                    console.log('📦 Service imported:', !!service, 'has method:', !!service?.getRadarVisualization);
                    if (service && service.getRadarVisualization) {
                        imageDataUrl = await service.getRadarVisualization(analysisId);
                        if (imageDataUrl) {
                            captureMethod = 'database';
                            console.log('✅ Radar visualization retrieved from database successfully, length:', imageDataUrl.length);
                        } else {
                            console.log('⚠️ No radar visualization found in database');
                        }
                    }
                } catch (error) {
                    console.warn('⚠️ Failed to retrieve radar from database:', error);
                }
            } else {
                console.warn('⚠️ No analysisId available for radar retrieval');
            }

            // Method 2: Try the component capture function
            if (!imageDataUrl && window.captureRSToVisualization && typeof window.captureRSToVisualization === 'function') {
                console.log('✅ Found visualization capture function');
                try {
                    console.log('📸 Capturing radar visualization using component function...');
                    imageDataUrl = await window.captureRSToVisualization();
                    captureMethod = 'component';
                    console.log('✅ Radar visualization captured successfully using component function');
                } catch (error) {
                    console.warn('⚠️ Component capture failed, trying screenshot method:', error);
                }
            }

            // Method 3: If component capture failed or unavailable, try automatic screenshot
            if (!imageDataUrl) {
                console.log('📸 Attempting automatic screenshot capture...');
                imageDataUrl = await this.captureVisualizationScreenshot();
                captureMethod = 'screenshot';
                console.log('✅ Radar visualization captured successfully using screenshot');
            }

            if (imageDataUrl) {
                const captureMethodText = captureMethod === 'database' ? 'depuis la base de donn\u00e9es' :
                    captureMethod === 'component' ? 'directement depuis l\'interface' :
                        'automatiquement par capture d\'\u00e9cran';

                return `
            < div class="radar-visualization-section" >
                    <h3 class="section-title">
                        <i class="fas fa-chart-radar"></i>
                        Visualisation Radar des Couples SR/OV
                    </h3>
                    <p style="color: var(--text-secondary); font-style: italic; margin-bottom: 1.5rem;">
                        Cette visualisation a \u00e9t\u00e9 captur\u00e9e ${captureMethodText} d'\u00e9valuation des couples SR/OV.
                    </p>
                    <div class="visualization-container">
                        <img src="${imageDataUrl}" 
                             alt="Visualisation radar des couples SR/OV" />
                    </div>
                    <div class="visualization-description">
                        <p><strong>Interpr\u00e9tation :</strong> Cette visualisation montre la r\u00e9partition des couples Sources de Risque / Objectifs Vis\u00e9s selon leur niveau de criticit\u00e9 et leur secteur d'appartenance.</p>
                        <ul>
                            <li><span style="color: #ef4444;">●</span> <strong>Points rouges :</strong> Couples retenus pour la suite de l'analyse</li>
                            <li><span style="color: #22c55e;">●</span> <strong>Points verts :</strong> Couples non retenus</li>
                            <li><strong>Proximit\u00e9 du centre :</strong> Plus un point est proche du centre, plus son niveau de risque est \u00e9lev\u00e9</li>
                            <li><strong>Zones de criticit\u00e9 :</strong> Zone rouge (critique), zone orange (mod\u00e9r\u00e9e), zone verte (faible)</li>
                        </ul>
                        <div style="margin-top: 1rem; padding: 1rem; background: rgba(59, 130, 246, 0.1); border-radius: 8px; border-left: 4px solid var(--secondary-color);">
                            <p style="margin: 0; font-size: 0.9rem; color: var(--text-secondary);">
                                <i class="fas fa-info-circle" style="margin-right: 0.5rem; color: var(--secondary-color);"></i>
                                <strong>Note :</strong> Cette visualisation refl\u00e8te exactement l'\u00e9tat des donn\u00e9es au moment de la g\u00e9n\u00e9ration du rapport, 
                                incluant les niveaux d'\u00e9valuation et les s\u00e9lections effectu\u00e9es dans l'interface.
                            </p>
                        </div>
                    </div>
                </div >
            `;
            } else {
                console.log('⚠️ All capture methods failed, falling back to static representation');
                return this.generateRadarVisualization(couplesSROV);
            }
        } catch (error) {
            console.error('❌ Error capturing visualization:', error);
            // Fallback to the original method
            return this.generateRadarVisualization(couplesSROV);
        }
    }

    /**
     * Capture visualization using automatic screenshot
     * This method tries to find and screenshot the radar visualization
     */
    async captureVisualizationScreenshot() {
        try {
            // Try to import html-to-image dynamically
            let htmlToImage = window.htmlToImage;
            if (!htmlToImage) {
                try {
                    const htmlToImageModule = await import('html-to-image');
                    htmlToImage = htmlToImageModule;
                    // Make it globally available for future use
                    window.htmlToImage = htmlToImage;
                } catch (importError) {
                    console.warn('⚠️ html-to-image not available for screenshot capture:', importError);
                    return null;
                }
            }

            // Try to find the radar visualization container
            const selectors = [
                '.radar-visualization',
                '.chart-container',
                '[class*="radar"]',
                '[class*="visualization"]',
                '.EnhancedRadarVisualization',
                // Fallback to any SVG elements that might be the charts
                'svg'
            ];

            let targetElement = null;
            for (const selector of selectors) {
                const elements = document.querySelectorAll(selector);
                if (elements.length > 0) {
                    // If multiple elements, try to find the one that looks like our radar viz
                    for (const element of elements) {
                        if (element.querySelector('svg') || element.tagName === 'SVG') {
                            targetElement = element;
                            console.log(`📍 Found visualization element using selector: ${selector}`);
                            break;
                        }
                    }
                    if (targetElement) break;
                }
            }

            if (!targetElement) {
                // Last resort: try to capture the entire page area where the viz should be
                const mainContent = document.querySelector('main') || document.querySelector('.slide-content') || document.body;
                if (mainContent) {
                    targetElement = mainContent;
                    console.log('📍 Using main content area as fallback');
                }
            }

            if (targetElement) {
                console.log('📸 Taking screenshot of visualization area...');
                const dataUrl = await htmlToImage.toPng(targetElement, {
                    backgroundColor: '#ffffff',
                    pixelRatio: 2,
                    quality: 1.0,
                    width: Math.min(targetElement.scrollWidth, 1200),
                    height: Math.min(targetElement.scrollHeight, 800)
                });

                console.log('✅ Screenshot captured successfully');
                return dataUrl;
            } else {
                console.warn('⚠️ Could not find visualization element for screenshot');
                return null;
            }
        } catch (error) {
            console.error('❌ Error taking screenshot:', error);
            return null;
        }
    }

    /**
     * Generate radar visualization for couples SR/OV (fallback method)
     * This recreates the radar charts from Workshop 2 Activity 2
     */
    generateRadarVisualization(couplesSROV) {
        console.log('🔥 ===== RADAR VISUALIZATION GENERATION =====');
        console.log('📋 [Radar Visualization] Input couples:', couplesSROV);

        // Chart configuration
        const chartSize = 500;
        const centerX = chartSize / 2;
        const centerY = chartSize / 2;
        const maxRadius = chartSize * 0.4;
        const maxLevel = 3;

        // Risk source sectors (matching the component)
        const riskSectors = [
            { name: '🏛️ \u00c9TATIQUE', color: '#DC2626', startAngle: 0, endAngle: 45 },
            { name: '🔪 CRIME ORGANIS\u00c9', color: '#9333EA', startAngle: 45, endAngle: 90 },
            { name: '💣 TERRORISTE', color: '#F97316', startAngle: 90, endAngle: 135 },
            { name: '✊ ACTIVISTE ID\u00c9OLOGIQUE', color: '#16A34A', startAngle: 135, endAngle: 180 },
            { name: '🕵️ OFFICINE SP\u00c9CIALIS\u00c9E', color: '#2563EB', startAngle: 180, endAngle: 225 },
            { name: '🎮 AMATEUR', color: '#CA8A04', startAngle: 225, endAngle: 270 },
            { name: '😡 VENGEUR', color: '#DB2777', startAngle: 270, endAngle: 315 },
            { name: '🦹 MALVEILLANT PATHOLOGIQUE', color: '#4F46E5', startAngle: 315, endAngle: 360 }
        ];

        // Objective sectors (matching the component)
        const objectiveSectors = [
            { name: 'ESPIONNAGE', color: '#EC4899', startAngle: 0, endAngle: 60 },
            { name: 'PR\u00c9POSITIONNEMENT STRAT\u00c9GIQUE', color: '#8B5CF6', startAngle: 60, endAngle: 120 },
            { name: 'INFLUENCE', color: '#3B82F6', startAngle: 120, endAngle: 180 },
            { name: 'ENTRAVE AU FONCTIONNEMENT', color: '#10B981', startAngle: 180, endAngle: 240 },
            { name: 'LUCRATIF', color: '#F59E0B', startAngle: 240, endAngle: 300 },
            { name: 'D\u00c9FI, AMUSEMENT', color: '#EF4444', startAngle: 300, endAngle: 360 }
        ];

        // Helper functions
        const categoryToSector = (category) => {
            if (!category) return '🏛️ \u00c9TATIQUE';
            const lowerCategory = category.toLowerCase();
            if (lowerCategory.includes('etatique')) return '🏛️ \u00c9TATIQUE';
            if (lowerCategory.includes('criminel') || lowerCategory.includes('crime') || lowerCategory.includes('organise')) return '🔪 CRIME ORGANIS\u00c9';
            if (lowerCategory.includes('terroriste')) return '💣 TERRORISTE';
            if (lowerCategory.includes('activiste') || lowerCategory.includes('ideologique')) return '✊ ACTIVISTE ID\u00c9OLOGIQUE';
            if (lowerCategory.includes('officine') || lowerCategory.includes('specialisee')) return '🕵️ OFFICINE SP\u00c9CIALIS\u00c9E';
            if (lowerCategory.includes('amateur')) return '🎮 AMATEUR';
            if (lowerCategory.includes('vengeur')) return '😡 VENGEUR';
            if (lowerCategory.includes('malveillant') || lowerCategory.includes('pathologique')) return '🦹 MALVEILLANT PATHOLOGIQUE';
            return '🏛️ \u00c9TATIQUE';
        };

        const objectiveToSector = (category) => {
            if (!category) return 'ESPIONNAGE';
            const lowerCategory = category.toLowerCase();
            if (lowerCategory.includes('espionnage')) return 'ESPIONNAGE';
            if (lowerCategory.includes('prepositionnement') || lowerCategory.includes('strat\u00e9gique') || lowerCategory.includes('strategique')) return 'PR\u00c9POSITIONNEMENT STRAT\u00c9GIQUE';
            if (lowerCategory.includes('influence')) return 'INFLUENCE';
            if (lowerCategory.includes('entrave') || lowerCategory.includes('fonctionnement')) return 'ENTRAVE AU FONCTIONNEMENT';
            if (lowerCategory.includes('lucratif') || lowerCategory.includes('financier')) return 'LUCRATIF';
            if (lowerCategory.includes('d\u00e9fi') || lowerCategory.includes('defi') || lowerCategory.includes('amusement')) return 'D\u00c9FI, AMUSEMENT';
            return 'ESPIONNAGE';
        };

        const getSectorAngle = (sectorName, sectors) => {
            const sector = sectors.find(s => s.name === sectorName);
            return sector ? (sector.startAngle + sector.endAngle) / 2 : 0;
        };

        const calculateNiveau = (motivation, activite, ressources) => {
            const normalizedMotivation = String(motivation || '').toLowerCase();
            const normalizedActivite = String(activite || '').toLowerCase();
            const normalizedRessources = String(ressources || '').toLowerCase();

            const motivationScore = normalizedMotivation.startsWith('elev') ? 3 : normalizedMotivation.startsWith('moyen') ? 2 : 1;
            const activiteScore = normalizedActivite.startsWith('elev') ? 3 : normalizedActivite.startsWith('moyen') ? 2 : 1;
            const ressourcesScore = normalizedRessources.startsWith('import') ? 3 : normalizedRessources.startsWith('moyen') ? 2 : 1;

            const avgScore = (motivationScore + activiteScore + ressourcesScore) / 3;
            return Math.round(avgScore);
        };

        // Generate radar grid
        const generateRadarGrid = (sectors, chartId) => {
            let gridSVG = '';

            // Concentric circles (levels)
            for (let level = 1; level <= maxLevel; level++) {
                const radius = (level / maxLevel) * maxRadius;
                const strokeColor = level === maxLevel ? '#dc2626' : level === 2 ? '#f59e0b' : '#22c55e';
                const strokeWidth = level === maxLevel ? 2 : 1;
                gridSVG += `< circle cx = "${centerX}" cy = "${centerY}" r = "${radius}" fill = "none" stroke = "${strokeColor}" stroke - width="${strokeWidth}" opacity = "0.3" /> `;
            }

            // Sector lines and labels
            sectors.forEach(sector => {
                const angle = (sector.startAngle + sector.endAngle) / 2;
                const angleRad = (angle * Math.PI) / 180;
                const x2 = centerX + Math.cos(angleRad) * maxRadius;
                const y2 = centerY + Math.sin(angleRad) * maxRadius;

                // Sector line
                gridSVG += `< line x1 = "${centerX}" y1 = "${centerY}" x2 = "${x2}" y2 = "${y2}" stroke = "${sector.color}" stroke - width="2" opacity = "0.6" /> `;

                // Sector label
                const labelRadius = maxRadius + 30;
                const labelX = centerX + Math.cos(angleRad) * labelRadius;
                const labelY = centerY + Math.sin(angleRad) * labelRadius;
                gridSVG += `< text x = "${labelX}" y = "${labelY}" text - anchor="middle" dominant - baseline="middle" font - size="10" font - weight="bold" fill = "${sector.color}" > ${sector.name}</text > `;
            });

            return gridSVG;
        };

        // Process points for visualization
        const processPoints = (couples, sectors, isSourceChart) => {
            return couples.map((couple, index) => {
                const sectorName = isSourceChart
                    ? categoryToSector(couple.sourceCategory)
                    : objectiveToSector(couple.objectifViseCategory);

                const angle = getSectorAngle(sectorName, sectors);
                const level = couple.niveau || calculateNiveau(couple.motivation, couple.activite, couple.ressources);

                // Inverted radius (higher level = closer to center)
                const radius = ((maxLevel + 1 - level) / maxLevel) * maxRadius;
                const angleRad = (angle * Math.PI) / 180;
                const x = centerX + Math.cos(angleRad) * radius;
                const y = centerY + Math.sin(angleRad) * radius;

                return {
                    id: couple.id || index,
                    name: isSourceChart ? couple.sourceName : couple.objectifVise,
                    displayId: isSourceChart ? `SR${index + 1} ` : `OV${index + 1} `,
                    level: level,
                    x: x,
                    y: y,
                    retained: couple.selected || couple.retenu,
                    sector: sectorName
                };
            });
        };

        const riskPoints = processPoints(couplesSROV, riskSectors, true);
        const objectivePoints = processPoints(couplesSROV, objectiveSectors, false);

        // Generate points SVG
        const generatePoints = (points) => {
            return points.map(point => {
                const fillColor = point.retained ? '#ef4444' : '#22c55e';
                const borderColor = point.retained ? '#dc2626' : '#16a34a';

                return `
            < circle cx = "${point.x}" cy = "${point.y}" r = "12" fill = "${fillColor}" stroke = "${borderColor}" stroke - width="2" />
                <text x="${point.x}" y="${point.y + 4}" font-size="10" font-weight="bold" text-anchor="middle" fill="white">${point.displayId}</text>
        `;
            }).join('');
        };

        return `
            < div class="info-box info" style = "margin-top: 2rem;" >
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem;">
                    <div>
                        <h3 style="margin: 0; font-size: 1.4rem; font-weight: 700; display: flex; align-items: center;">
                            <i class="fas fa-chart-line" style="margin-right: 0.75rem; font-size: 1.2rem;"></i>
                            Visualisation Radar des Couples SR/OV
                        </h3>
                        <p style="margin: 0.5rem 0 0 0; opacity: 0.9; font-size: 0.95rem;">
                            Repr\u00e9sentation graphique des sources de risque et objectifs vis\u00e9s selon leur niveau d'\u00e9valuation
                        </p>
                    </div>
                </div>

                <!--Radar Charts Side by Side-- >
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; margin-bottom: 2rem;">
                    <!-- Sources de Risque Chart -->
                    <div style="text-align: center;">
                        <h4 style="margin-bottom: 1rem; font-size: 1.1rem; font-weight: 600; color: #1e293b;">Vision par Sources de Risque</h4>
                        <svg width="${chartSize}" height="${chartSize}" style="border: 1px solid #e2e8f0; border-radius: 8px; background: white;">
                            ${generateRadarGrid(riskSectors, 'sources')}
                            ${generatePoints(riskPoints)}
                        </svg>
                    </div>

                    <!-- Objectifs Vis\u00e9s Chart -->
                    <div style="text-align: center;">
                        <h4 style="margin-bottom: 1rem; font-size: 1.1rem; font-weight: 600; color: #1e293b;">Vision par Objectifs Vis\u00e9s</h4>
                        <svg width="${chartSize}" height="${chartSize}" style="border: 1px solid #e2e8f0; border-radius: 8px; background: white;">
                            ${generateRadarGrid(objectiveSectors, 'objectives')}
                            ${generatePoints(objectivePoints)}
                        </svg>
                    </div>
                </div>

                <!--Legend and Interpretation-- >
            <div style="background: #f8fafc; border-radius: 12px; padding: 1.5rem; border: 1px solid #e2e8f0;">
                <h4 style="margin: 0 0 1rem 0; font-size: 1rem; font-weight: 700; text-align: center; color: #1e293b;">Guide d'Interpr\u00e9tation</h4>

                <!-- Risk Levels -->
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1rem; margin-bottom: 1.5rem;">
                    <div style="display: flex; align-items: center; padding: 0.75rem; background: white; border-radius: 8px; border: 1px solid #fecaca;">
                        <div style="width: 16px; height: 16px; border-radius: 50%; background: #fecaca; border: 2px solid #dc2626; margin-right: 0.75rem;"></div>
                        <div>
                            <div style="font-weight: 600; color: #dc2626;">Zone Critique</div>
                            <div style="font-size: 0.8rem; color: #374151;">Niveau 3 - Attention prioritaire</div>
                        </div>
                    </div>
                    <div style="display: flex; align-items: center; padding: 0.75rem; background: white; border-radius: 8px; border: 1px solid #fed7aa;">
                        <div style="width: 16px; height: 16px; border-radius: 50%; background: #fed7aa; border: 2px solid #f59e0b; margin-right: 0.75rem;"></div>
                        <div>
                            <div style="font-weight: 600; color: #f59e0b;">Zone Mod\u00e9r\u00e9e</div>
                            <div style="font-size: 0.8rem; color: #374151;">Niveau 2 - Vigilance recommand\u00e9e</div>
                        </div>
                    </div>
                    <div style="display: flex; align-items: center; padding: 0.75rem; background: white; border-radius: 8px; border: 1px solid #bbf7d0;">
                        <div style="width: 16px; height: 16px; border-radius: 50%; background: #bbf7d0; border: 2px solid #22c55e; margin-right: 0.75rem;"></div>
                        <div>
                            <div style="font-weight: 600; color: #22c55e;">Zone Faible</div>
                            <div style="font-size: 0.8rem; color: #374151;">Niveau 1 - Risque limit\u00e9</div>
                        </div>
                    </div>
                </div>

                <!-- Selection Status -->
                <div style="display: flex; justify-content: center; gap: 2rem; margin-bottom: 1.5rem;">
                    <div style="display: flex; align-items: center;">
                        <div style="width: 16px; height: 16px; border-radius: 50%; background: #ef4444; border: 2px solid #dc2626; margin-right: 0.5rem;"></div>
                        <span style="font-size: 0.9rem; font-weight: 600; color: #dc2626;">Couples Retenus</span>
                    </div>
                    <div style="display: flex; align-items: center;">
                        <div style="width: 16px; height: 16px; border-radius: 50%; background: #22c55e; border: 2px solid #16a34a; margin-right: 0.5rem;"></div>
                        <span style="font-size: 0.9rem; font-weight: 600; color: #16a34a;">Couples Non Retenus</span>
                    </div>
                </div>

                <!-- Interpretation Text -->
                <div style="text-align: center; font-size: 0.85rem; color: #374151; line-height: 1.5;">
                    <p style="margin: 0 0 0.5rem 0;"><strong>Interpr\u00e9tation :</strong> Plus un point est proche du centre, plus son niveau de risque est \u00e9lev\u00e9.</p>
                    <p style="margin: 0;">Les couples retenus (rouge) n\u00e9cessitent une attention particuli\u00e8re dans l'analyse des risques.</p>
                </div>
            </div>
            </div >
            `;
    }

    /**
     * Generate Cross-Analysis Section
     * @param {Object} workshops - All workshop data
     * @returns {string} Cross-analysis HTML
     */
    generateCrossAnalysisSection(workshops) {
        // Only proceed if we have real data
        if (!this.hasMinimumDataForEnhancedAnalysis(workshops)) {
            return '';
        }

        const correlations = this.analyzeWorkshopCorrelations(workshops);
        const riskFlows = this.analyzeRiskFlows(workshops);
        const coverageGaps = this.identifyCoverageGaps(workshops);

        // Additional check: if no meaningful analysis can be done, return empty
        if (correlations.length === 0 && riskFlows.length === 0 && coverageGaps.length === 0) {
            console.log('[EnhancedHtmlReportGenerator] No meaningful cross-analysis data available');
            return '';
        }

        return `
    <div class="slide" id="cross-analysis">
        <div class="slide-header">
            <div class="slide-title">Analyse Transversale des Ateliers</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Analyse Transversale des Ateliers</h1>
            <p style="font-size: 1.2rem; margin-bottom: 2rem;">Cette section pr\u00e9sente une analyse transversale des corr\u00e9lations entre les diff\u00e9rents ateliers EBIOS RM.</p>
            
            <!-- Corr\u00e9lations entre Ateliers -->
            <h2>Corr\u00e9lations entre Ateliers</h2>
            <div class="info-box info">
                <h3><i class="fas fa-project-diagram"></i> Flux de Risques</h3>
                <div class="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Source (Atelier)</th>
                                <th>Cible (Atelier)</th>
                                <th>\u00c9l\u00e9ments Corr\u00e9l\u00e9s</th>
                                <th>Impact</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${correlations.map(corr => `
                                <tr>
                                    <td><strong>${corr.source}</strong></td>
                                    <td><strong>${corr.target}</strong></td>
                                    <td>${corr.elements}</td>
                                    <td><span class="status-badge ${corr.impact.toLowerCase()}">${corr.impact}</span></td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Flux de Risques -->
            <h2>Flux de Risques</h2>
            <div class="info-box warning">
                <h3><i class="fas fa-stream"></i> Cha\u00eene de Risques Identifi\u00e9e</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem;">
                    ${riskFlows.map(flow => `
                        <div style="background: white; border-radius: 12px; padding: 1.5rem; border-left: 4px solid #f59e0b;">
                            <h4 style="margin: 0 0 1rem 0; color: #92400e;">${flow.title}</h4>
                            <div style="font-size: 0.95rem; line-height: 1.6;">
                                <div><strong>Origine:</strong> ${flow.origin}</div>
                                <div><strong>Propagation:</strong> ${flow.propagation}</div>
                                <div><strong>Impact Final:</strong> ${flow.finalImpact}</div>
                            </div>
                        </div>
                    `).join('')}
                </div>
            </div>

            <!-- Lacunes de Couverture -->
            <h2>Lacunes de Couverture</h2>
            <div class="info-box error">
                <h3><i class="fas fa-exclamation-triangle"></i> Zones d'Am\u00e9lioration Identifi\u00e9es</h3>
                <ul style="list-style: none; padding: 0;">
                    ${coverageGaps.map(gap => `
                        <li style="margin-bottom: 1rem; padding: 1rem; background: rgba(255, 255, 255, 0.8); border-radius: 8px; border-left: 4px solid #ef4444;">
                            <div style="font-weight: 700; color: #991b1b; margin-bottom: 0.5rem;">${gap.area}</div>
                            <div style="color: #374151;">${gap.description}</div>
                            <div style="margin-top: 0.5rem; font-size: 0.9rem; color: #6b7280;"><strong>Recommandation:</strong> ${gap.recommendation}</div>
                        </li>
                    `).join('')}
                </ul>
            </div>
        </div>
    </div>`;
    }

    /**
     * Generate Risk Matrix Section
     * @param {Object} workshops - All workshop data
     * @returns {string} Risk matrix HTML
     */
    generateRiskMatrixSection(workshops) {
        // Only proceed if we have real data
        if (!this.hasMinimumDataForEnhancedAnalysis(workshops)) {
            return '';
        }

        const riskMatrix = this.buildConsolidatedRiskMatrix(workshops);
        const priorityRisks = this.identifyPriorityRisks(workshops);

        // Additional check: if no risks identified, return empty
        if (riskMatrix.allRisks.length === 0) {
            console.log('[EnhancedHtmlReportGenerator] No risks available for matrix generation');
            return '';
        }

        return `
    <div class="slide" id="risk-matrix">
        <div class="slide-header">
            <div class="slide-title">Matrice des Risques Consolid\u00e9e</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Matrice des Risques Consolid\u00e9e</h1>
            <p style="font-size: 1.2rem; margin-bottom: 2rem;">Vue d'ensemble consolid\u00e9e de tous les risques identifi\u00e9s dans l'analyse EBIOS RM.</p>
            
            <!-- Matrice de Risques -->
            <h2>Matrice Probabilit\u00e9 × Impact</h2>
            <div class="info-box info">
                <div style="overflow-x: auto;">
                    <div style="display: inline-block; min-width: 600px; background: white; border-radius: 12px; padding: 2rem;">
                        ${this.generateRiskMatrixVisualization(riskMatrix)}
                    </div>
                </div>
            </div>

            <!-- Risques Prioritaires -->
            <h2>Risques Prioritaires</h2>
            <div class="kpi-grid">
                <div class="kpi-card">
                    <i class="fas fa-exclamation-circle kpi-icon"></i>
                    <div class="kpi-value" style="color: #dc2626;">${priorityRisks.critical}</div>
                    <div class="kpi-label">Risques Critiques</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-exclamation-triangle kpi-icon"></i>
                    <div class="kpi-value" style="color: #f59e0b;">${priorityRisks.high}</div>
                    <div class="kpi-label">Risques \u00c9lev\u00e9s</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-info-circle kpi-icon"></i>
                    <div class="kpi-value" style="color: #3b82f6;">${priorityRisks.medium}</div>
                    <div class="kpi-label">Risques Mod\u00e9r\u00e9s</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-check-circle kpi-icon"></i>
                    <div class="kpi-value" style="color: #10b981;">${priorityRisks.low}</div>
                    <div class="kpi-label">Risques Faibles</div>
                </div>
            </div>

            <!-- D\u00e9tail des Risques Critiques -->
            <h2>D\u00e9tail des Risques Critiques</h2>
            <div class="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Risque</th>
                            <th>Source</th>
                            <th>Probabilit\u00e9</th>
                            <th>Impact</th>
                            <th>Niveau</th>
                            <th>Traitement</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${riskMatrix.criticalRisks.map(risk => `
                            <tr>
                                <td><strong>${risk.name}</strong></td>
                                <td>${risk.source}</td>
                                <td><span class="status-badge ${risk.probabilityClass}">${risk.probability}</span></td>
                                <td><span class="status-badge ${risk.impactClass}">${risk.impact}</span></td>
                                <td><span class="status-badge ${risk.levelClass}">${risk.level}</span></td>
                                <td>${risk.treatment || '\u00c0 d\u00e9finir'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        </div>
    </div>`;
    }

    /**
     * Generate Implementation Roadmap Section
     * @param {Object} workshops - All workshop data
     * @returns {string} Implementation roadmap HTML
     */
    generateImplementationRoadmapSection(workshops) {
        // Only proceed if we have real data
        if (!this.hasMinimumDataForEnhancedAnalysis(workshops)) {
            return '';
        }

        // Check if we have treatment plans to base roadmap on
        const hasTreatmentPlans = workshops.workshop5?.riskTreatmentPlans?.length > 0;
        const hasIdentifiedRisks = (workshops.workshop1?.dreadedEvents?.length || 0) +
            (workshops.workshop4?.operationalScenarios?.length || 0) > 0;

        if (!hasTreatmentPlans && !hasIdentifiedRisks) {
            console.log('[EnhancedHtmlReportGenerator] No treatment plans or identified risks for roadmap generation');
            return '';
        }

        const roadmap = this.buildImplementationRoadmap(workshops);
        const timeline = this.generateImplementationTimeline(workshops);

        return `
    <div class="slide" id="implementation-roadmap">
        <div class="slide-header">
            <div class="slide-title">Feuille de Route d'Impl\u00e9mentation</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Feuille de Route d'Impl\u00e9mentation</h1>
            <p style="font-size: 1.2rem; margin-bottom: 2rem;">Plan d'action structur\u00e9 pour la mise en \u0153uvre des mesures de s\u00e9curit\u00e9 identifi\u00e9es.</p>
            
            <!-- Vue d'ensemble du Plan -->
            <h2>Vue d'ensemble du Plan</h2>
            <div class="kpi-grid">
                <div class="kpi-card">
                    <i class="fas fa-tasks kpi-icon"></i>
                    <div class="kpi-value">${roadmap.totalActions}</div>
                    <div class="kpi-label">Actions Totales</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-clock kpi-icon"></i>
                    <div class="kpi-value">${roadmap.phases.length}</div>
                    <div class="kpi-label">Phases</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-calendar-alt kpi-icon"></i>
                    <div class="kpi-value">${roadmap.duration}</div>
                    <div class="kpi-label">Dur\u00e9e (mois)</div>
                </div>
                <div class="kpi-card">
                    <i class="fas fa-euro-sign kpi-icon"></i>
                    <div class="kpi-value">${roadmap.estimatedBudget}</div>
                    <div class="kpi-label">Budget Estim\u00e9</div>
                </div>
            </div>

            <!-- Timeline d'Impl\u00e9mentation -->
            <h2>Timeline d'Impl\u00e9mentation</h2>
            <div class="info-box info">
                <h3><i class="fas fa-road"></i> Phases d'Impl\u00e9mentation</h3>
                <div style="position: relative; padding: 2rem 0;">
                    ${timeline.phases.map((phase, index) => `
                        <div style="display: flex; align-items: center; margin-bottom: 2rem; position: relative;">
                            <div style="width: 60px; height: 60px; border-radius: 50%; background: ${phase.color}; color: white; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.2rem; margin-right: 2rem; z-index: 2;">
                                ${index + 1}
                            </div>
                            <div style="flex: 1; background: white; border-radius: 12px; padding: 1.5rem; border-left: 4px solid ${phase.color}; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                                <h4 style="margin: 0 0 0.5rem 0; color: #1e293b; font-size: 1.2rem;">${phase.name}</h4>
                                <div style="color: #64748b; margin-bottom: 1rem; font-size: 0.95rem;">
                                    <i class="fas fa-calendar"></i> ${phase.duration} • 
                                    <i class="fas fa-tasks"></i> ${phase.actions.length} actions • 
                                    <i class="fas fa-euro-sign"></i> ${phase.budget}
                                </div>
                                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1rem;">
                                    ${phase.actions.map(action => `
                                        <div style="background: #f8fafc; border-radius: 8px; padding: 1rem; border-left: 3px solid ${phase.color};">
                                            <div style="font-weight: 600; color: #374151; margin-bottom: 0.5rem;">${action.name}</div>
                                            <div style="font-size: 0.85rem; color: #6b7280;">${action.description}</div>
                                            <div style="margin-top: 0.5rem; font-size: 0.8rem;">
                                                <span style="background: ${action.priorityColor}; color: white; padding: 2px 8px; border-radius: 12px; font-weight: 600;">${action.priority}</span>
                                            </div>
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                            ${index < timeline.phases.length - 1 ? `
                                <div style="position: absolute; left: 29px; top: 60px; width: 2px; height: 40px; background: #e2e8f0; z-index: 1;"></div>
                            ` : ''}
                        </div>
                    `).join('')}
                </div>
            </div>

            <!-- Ressources et Responsabilit\u00e9s -->
            <h2>Ressources et Responsabilit\u00e9s</h2>
            <div class="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Domaine</th>
                            <th>Responsable</th>
                            <th>Ressources Requises</th>
                            <th>\u00c9ch\u00e9ance</th>
                            <th>Statut</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${roadmap.responsibilities.map(resp => `
                            <tr>
                                <td><strong>${resp.domain}</strong></td>
                                <td>${resp.responsible}</td>
                                <td>${resp.resources}</td>
                                <td>${resp.deadline}</td>
                                <td><span class="status-badge ${resp.statusClass}">${resp.status}</span></td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        </div>
    </div>`;
    }

    /**
     * Analyze correlations between workshops
     * @private
     */
    analyzeWorkshopCorrelations(workshops) {
        const correlations = [];

        // Only add correlations if both workshops have meaningful data (length > 0)
        // Atelier 1 → Atelier 2
        if (workshops.workshop1?.businessValues?.length > 0 && workshops.workshop2?.riskSources?.length > 0) {
            correlations.push({
                source: "Atelier 1",
                target: "Atelier 2",
                elements: `${workshops.workshop1.businessValues.length} valeurs m\u00e9tier → ${workshops.workshop2.riskSources.length} sources de risque`,
                impact: "\u00c9lev\u00e9"
            });
        }

        // Atelier 2 → Atelier 3
        if (workshops.workshop2?.riskSources?.length > 0 && workshops.workshop3?.strategicScenarios?.length > 0) {
            correlations.push({
                source: "Atelier 2",
                target: "Atelier 3",
                elements: `${workshops.workshop2.riskSources.length} sources → ${workshops.workshop3.strategicScenarios.length} sc\u00e9narios strat\u00e9giques`,
                impact: "Critique"
            });
        }

        // Atelier 3 → Atelier 4
        if (workshops.workshop3?.strategicScenarios?.length > 0 && workshops.workshop4?.operationalScenarios?.length > 0) {
            correlations.push({
                source: "Atelier 3",
                target: "Atelier 4",
                elements: `${workshops.workshop3.strategicScenarios.length} sc\u00e9narios strat\u00e9giques → ${workshops.workshop4.operationalScenarios.length} sc\u00e9narios op\u00e9rationnels`,
                impact: "Critique"
            });
        }

        // Atelier 4 → Atelier 5
        if (workshops.workshop4?.operationalScenarios?.length > 0 && workshops.workshop5?.riskTreatmentPlans?.length > 0) {
            correlations.push({
                source: "Atelier 4",
                target: "Atelier 5",
                elements: `${workshops.workshop4.operationalScenarios.length} sc\u00e9narios → ${workshops.workshop5.riskTreatmentPlans.length} plans de traitement`,
                impact: "Critique"
            });
        }

        return correlations;
    }

    /**
     * Analyze risk flows across workshops
     * @private
     */
    analyzeRiskFlows(workshops) {
        const flows = [];

        // Only add flows if there's real data (length > 0)
        if (workshops.workshop1?.dreadedEvents?.length > 0 && workshops.workshop2?.riskSources?.length > 0) {
            flows.push({
                title: "Flux Principal de Risques",
                origin: `${workshops.workshop1.dreadedEvents.length} \u00e9v\u00e9nements redout\u00e9s (Atelier 1)`,
                propagation: "Sources de risque → Sc\u00e9narios strat\u00e9giques → Sc\u00e9narios op\u00e9rationnels",
                finalImpact: "Plans de traitement cibl\u00e9s (Atelier 5)"
            });
        }

        if (workshops.workshop1?.businessValues?.length > 0 && workshops.workshop5?.riskTreatmentPlans?.length > 0) {
            flows.push({
                title: "Protection des Actifs",
                origin: `${workshops.workshop1.businessValues.length} valeurs m\u00e9tier critiques`,
                propagation: "Identification des menaces → \u00c9valuation des risques",
                finalImpact: `${workshops.workshop5.riskTreatmentPlans.length} mesures de protection adapt\u00e9es`
            });
        }

        return flows;
    }

    /**
     * Identify coverage gaps
     * @private
     */
    identifyCoverageGaps(workshops) {
        const gaps = [];

        // Only identify gaps based on actual data analysis, not just missing data
        const totalBusinessValues = workshops.workshop1?.businessValues?.length || 0;
        const totalRiskSources = workshops.workshop2?.riskSources?.length || 0;
        const totalScenarios = (workshops.workshop3?.strategicScenarios?.length || 0) +
            (workshops.workshop4?.operationalScenarios?.length || 0);
        const totalTreatmentPlans = workshops.workshop5?.riskTreatmentPlans?.length || 0;

        // Identify imbalances in the analysis
        if (totalBusinessValues > 0 && totalRiskSources === 0) {
            gaps.push({
                area: "Couverture des Menaces",
                description: `${totalBusinessValues} valeurs m\u00e9tier identifi\u00e9es mais aucune source de risque analys\u00e9e`,
                recommendation: "Analyser les menaces potentielles pour chaque valeur m\u00e9tier critique"
            });
        }

        if (totalRiskSources > 0 && totalScenarios === 0) {
            gaps.push({
                area: "Sc\u00e9narisation des Risques",
                description: `${totalRiskSources} sources de risque identifi\u00e9es mais aucun sc\u00e9nario d\u00e9velopp\u00e9`,
                recommendation: "D\u00e9velopper des sc\u00e9narios d'attaque bas\u00e9s sur les sources de risque identifi\u00e9es"
            });
        }

        if (totalScenarios > 0 && totalTreatmentPlans === 0) {
            gaps.push({
                area: "Traitement des Risques",
                description: `${totalScenarios} sc\u00e9narios identifi\u00e9s mais aucun plan de traitement d\u00e9fini`,
                recommendation: "D\u00e9finir des strat\u00e9gies de traitement pour les sc\u00e9narios de risque identifi\u00e9s"
            });
        }

        // Check for coverage ratios
        if (totalBusinessValues > 0 && totalTreatmentPlans > 0) {
            const coverageRatio = totalTreatmentPlans / totalBusinessValues;
            if (coverageRatio < 0.5) {
                gaps.push({
                    area: "Couverture de Protection",
                    description: `Ratio de couverture faible: ${totalTreatmentPlans} plans pour ${totalBusinessValues} valeurs m\u00e9tier`,
                    recommendation: "Augmenter la couverture des mesures de protection pour les actifs critiques"
                });
            }
        }

        return gaps;
    }

    /**
     * Build consolidated risk matrix
     * @private
     */
    buildConsolidatedRiskMatrix(workshops) {
        const risks = [];

        // Collect risks from all workshops
        if (workshops.workshop1?.dreadedEvents) {
            workshops.workshop1.dreadedEvents.forEach(event => {
                risks.push({
                    name: event.name || event.description,
                    source: "Atelier 1",
                    probability: event.probability || "Moyen",
                    impact: event.impact || "Moyen",
                    level: this.calculateRiskLevel(event.probability, event.impact),
                    treatment: event.treatment
                });
            });
        }

        if (workshops.workshop4?.operationalScenarios) {
            workshops.workshop4.operationalScenarios.forEach(scenario => {
                risks.push({
                    name: scenario.name || scenario.description,
                    source: "Atelier 4",
                    probability: scenario.probability || "Moyen",
                    impact: scenario.impact || "Moyen",
                    level: this.calculateRiskLevel(scenario.probability, scenario.impact),
                    treatment: scenario.treatment
                });
            });
        }

        const criticalRisks = risks.filter(r => r.level === "Critique");

        return {
            allRisks: risks,
            criticalRisks: criticalRisks.map(risk => ({
                ...risk,
                probabilityClass: this.getProbabilityClass(risk.probability),
                impactClass: this.getImpactClass(risk.impact),
                levelClass: this.getLevelClass(risk.level)
            }))
        };
    }

    /**
     * Identify priority risks
     * @private
     */
    identifyPriorityRisks(workshops) {
        const allRisks = [];

        // Collect all risks
        Object.values(workshops).forEach(workshop => {
            if (workshop.dreadedEvents) allRisks.push(...workshop.dreadedEvents);
            if (workshop.operationalScenarios) allRisks.push(...workshop.operationalScenarios);
        });

        return {
            critical: allRisks.filter(r => this.calculateRiskLevel(r.probability, r.impact) === "Critique").length,
            high: allRisks.filter(r => this.calculateRiskLevel(r.probability, r.impact) === "\u00c9lev\u00e9").length,
            medium: allRisks.filter(r => this.calculateRiskLevel(r.probability, r.impact) === "Moyen").length,
            low: allRisks.filter(r => this.calculateRiskLevel(r.probability, r.impact) === "Faible").length
        };
    }

    /**
     * Build implementation roadmap
     * @private
     */
    buildImplementationRoadmap(workshops) {
        const immediateActions = this.getImmediateActions(workshops);
        const shortTermActions = this.getShortTermActions(workshops);
        const longTermActions = this.getLongTermActions(workshops);
        const responsibilities = this.getResponsibilities(workshops);

        // Only include phases that have actions
        const phases = [];

        if (immediateActions.length > 0) {
            phases.push({
                name: "Phase 1 - Actions Imm\u00e9diates",
                duration: "0-3 mois",
                actions: immediateActions,
                budget: this.estimateBudget(immediateActions, "immediate"),
                color: "#dc2626"
            });
        }

        if (shortTermActions.length > 0) {
            phases.push({
                name: "Phase 2 - Renforcement",
                duration: "3-9 mois",
                actions: shortTermActions,
                budget: this.estimateBudget(shortTermActions, "short"),
                color: "#f59e0b"
            });
        }

        if (longTermActions.length > 0) {
            phases.push({
                name: "Phase 3 - Optimisation",
                duration: "9-18 mois",
                actions: longTermActions,
                budget: this.estimateBudget(longTermActions, "long"),
                color: "#3b82f6"
            });
        }

        const totalActions = phases.reduce((sum, phase) => sum + phase.actions.length, 0);

        return {
            totalActions: totalActions,
            phases: phases,
            duration: phases.length * 6, // 6 months per phase on average
            estimatedBudget: this.calculateTotalBudget(phases),
            responsibilities: responsibilities
        };
    }

    /**
     * Estimate budget based on action type and phase
     * @private
     */
    estimateBudget(actions, phase) {
        if (actions.length === 0) return "0€";

        const baseAmounts = {
            immediate: 25000,
            short: 50000,
            long: 75000
        };

        const base = baseAmounts[phase] || 25000;
        const total = base * actions.length;

        if (total < 1000) return `${total}€`;
        if (total < 1000000) return `${Math.round(total / 1000)}k€`;
        return `${Math.round(total / 1000000)}M€`;
    }

    /**
     * Calculate total budget from all phases
     * @private
     */
    calculateTotalBudget(phases) {
        if (phases.length === 0) return "\u00c0 d\u00e9finir";

        const totalActions = phases.reduce((sum, phase) => sum + phase.actions.length, 0);
        const estimatedTotal = totalActions * 50000; // Average 50k per action

        if (estimatedTotal < 1000000) return `${Math.round(estimatedTotal / 1000)}k€`;
        return `${Math.round(estimatedTotal / 1000000)}M€`;
    }

    /**
     * Generate implementation timeline
     * @private
     */
    generateImplementationTimeline(workshops) {
        return {
            phases: [
                {
                    name: "S\u00e9curisation Imm\u00e9diate",
                    duration: "0-3 mois",
                    color: "#dc2626",
                    actions: [
                        { name: "Audit de s\u00e9curit\u00e9", description: "\u00c9valuation compl\u00e8te de l'existant", priority: "Critique", priorityColor: "#dc2626" },
                        { name: "Correctifs critiques", description: "Application des correctifs urgents", priority: "Critique", priorityColor: "#dc2626" },
                        { name: "Formation \u00e9quipes", description: "Sensibilisation s\u00e9curit\u00e9", priority: "\u00c9lev\u00e9", priorityColor: "#f59e0b" }
                    ],
                    budget: "50-100k€"
                },
                {
                    name: "Renforcement Structurel",
                    duration: "3-9 mois",
                    color: "#f59e0b",
                    actions: [
                        { name: "Mise en place SOC", description: "Centre op\u00e9rationnel de s\u00e9curit\u00e9", priority: "\u00c9lev\u00e9", priorityColor: "#f59e0b" },
                        { name: "Outils de monitoring", description: "D\u00e9ploiement SIEM/SOAR", priority: "\u00c9lev\u00e9", priorityColor: "#f59e0b" },
                        { name: "Politiques s\u00e9curit\u00e9", description: "Formalisation des proc\u00e9dures", priority: "Moyen", priorityColor: "#3b82f6" }
                    ],
                    budget: "100-200k€"
                },
                {
                    name: "Optimisation Continue",
                    duration: "9-18 mois",
                    color: "#3b82f6",
                    actions: [
                        { name: "Automatisation", description: "R\u00e9ponse automatis\u00e9e aux incidents", priority: "Moyen", priorityColor: "#3b82f6" },
                        { name: "Threat Intelligence", description: "Veille et analyse pr\u00e9dictive", priority: "Moyen", priorityColor: "#3b82f6" },
                        { name: "Am\u00e9lioration continue", description: "Optimisation des processus", priority: "Faible", priorityColor: "#10b981" }
                    ],
                    budget: "200-500k€"
                }
            ]
        };
    }

    /**
     * Helper methods for risk analysis
     * @private
     */
    calculateRiskLevel(probability, impact) {
        const probValue = this.getProbabilityValue(probability);
        const impactValue = this.getImpactValue(impact);
        const riskScore = probValue * impactValue;

        if (riskScore >= 9) return "Critique";
        if (riskScore >= 6) return "\u00c9lev\u00e9";
        if (riskScore >= 3) return "Moyen";
        return "Faible";
    }

    getProbabilityValue(prob) {
        const probMap = { "Faible": 1, "Moyen": 2, "\u00c9lev\u00e9": 3, "Critique": 3 };
        return probMap[prob] || 2;
    }

    getImpactValue(impact) {
        const impactMap = { "Faible": 1, "Moyen": 2, "\u00c9lev\u00e9": 3, "Critique": 3 };
        return impactMap[impact] || 2;
    }

    getProbabilityClass(prob) {
        const classMap = { "Faible": "success", "Moyen": "warning", "\u00c9lev\u00e9": "danger", "Critique": "danger" };
        return classMap[prob] || "info";
    }

    getImpactClass(impact) {
        const classMap = { "Faible": "success", "Moyen": "warning", "\u00c9lev\u00e9": "danger", "Critique": "danger" };
        return classMap[impact] || "info";
    }

    getLevelClass(level) {
        const classMap = { "Faible": "success", "Moyen": "warning", "\u00c9lev\u00e9": "danger", "Critique": "danger" };
        return classMap[level] || "info";
    }

    getImmediateActions(workshops) {
        const actions = [];

        // Base actions on actual identified risks and gaps
        const criticalRisks = this.identifyPriorityRisks(workshops).critical;
        const hasBusinessValues = workshops.workshop1?.businessValues?.length > 0;
        const hasSecurityControls = workshops.workshop1?.securityControls?.length > 0;

        if (criticalRisks > 0) {
            actions.push({
                name: `Traitement des ${criticalRisks} risques critiques`,
                description: "Mise en place imm\u00e9diate des mesures de protection",
                priority: "Critique",
                priorityColor: "#dc2626"
            });
        }

        if (hasBusinessValues && !hasSecurityControls) {
            actions.push({
                name: "Audit de s\u00e9curit\u00e9 des actifs critiques",
                description: `\u00c9valuation de la protection des ${workshops.workshop1.businessValues.length} valeurs m\u00e9tier`,
                priority: "Critique",
                priorityColor: "#dc2626"
            });
        }

        return actions;
    }

    getShortTermActions(workshops) {
        const actions = [];

        const hasRiskSources = workshops.workshop2?.riskSources?.length > 0;
        const hasScenarios = (workshops.workshop3?.strategicScenarios?.length || 0) +
            (workshops.workshop4?.operationalScenarios?.length || 0) > 0;

        if (hasRiskSources) {
            actions.push({
                name: `Surveillance des ${workshops.workshop2.riskSources.length} sources de risque`,
                description: "Mise en place du monitoring des menaces identifi\u00e9es",
                priority: "\u00c9lev\u00e9",
                priorityColor: "#f59e0b"
            });
        }

        if (hasScenarios) {
            const totalScenarios = (workshops.workshop3?.strategicScenarios?.length || 0) +
                (workshops.workshop4?.operationalScenarios?.length || 0);
            actions.push({
                name: `Pr\u00e9paration aux ${totalScenarios} sc\u00e9narios identifi\u00e9s`,
                description: "Formation et proc\u00e9dures de r\u00e9ponse aux incidents",
                priority: "\u00c9lev\u00e9",
                priorityColor: "#f59e0b"
            });
        }

        return actions;
    }

    getLongTermActions(workshops) {
        const actions = [];

        const hasTreatmentPlans = workshops.workshop5?.riskTreatmentPlans?.length > 0;

        if (hasTreatmentPlans) {
            actions.push({
                name: `Optimisation des ${workshops.workshop5.riskTreatmentPlans.length} plans de traitement`,
                description: "Am\u00e9lioration continue des mesures de s\u00e9curit\u00e9",
                priority: "Moyen",
                priorityColor: "#3b82f6"
            });
        }

        // Only add generic actions if we have a complete analysis
        if (this.hasMinimumDataForEnhancedAnalysis(workshops)) {
            actions.push({
                name: "Veille et am\u00e9lioration continue",
                description: "Surveillance des nouvelles menaces et optimisation",
                priority: "Faible",
                priorityColor: "#10b981"
            });
        }

        return actions;
    }

    getResponsibilities(workshops) {
        const responsibilities = [];

        // Only generate responsibilities based on actual data
        const hasBusinessValues = workshops.workshop1?.businessValues?.length > 0;
        const hasRiskSources = workshops.workshop2?.riskSources?.length > 0;
        const hasTreatmentPlans = workshops.workshop5?.riskTreatmentPlans?.length > 0;

        if (hasBusinessValues) {
            responsibilities.push({
                domain: "Protection des Actifs",
                responsible: "DSI",
                resources: `Protection de ${workshops.workshop1.businessValues.length} valeurs m\u00e9tier`,
                deadline: "3 mois",
                status: "\u00c0 planifier",
                statusClass: "secondary"
            });
        }

        if (hasRiskSources) {
            responsibilities.push({
                domain: "Surveillance des Menaces",
                responsible: "RSSI",
                resources: `Monitoring de ${workshops.workshop2.riskSources.length} sources de risque`,
                deadline: "6 mois",
                status: "\u00c0 planifier",
                statusClass: "secondary"
            });
        }

        if (hasTreatmentPlans) {
            responsibilities.push({
                domain: "Impl\u00e9mentation des Contr\u00f4les",
                responsible: "\u00c9quipe S\u00e9curit\u00e9",
                resources: `D\u00e9ploiement de ${workshops.workshop5.riskTreatmentPlans.length} plans de traitement`,
                deadline: "9 mois",
                status: "\u00c0 planifier",
                statusClass: "secondary"
            });
        }

        return responsibilities;
    }

    /**
     * Generate informational section when enhanced analysis is not available
     * @param {Object} workshops - All workshop data
     * @returns {string} Info section HTML
     */
    generateEnhancedAnalysisInfoSection(workshops) {
        const dataStatus = this.analyzeDataCompleteness(workshops);

        return `
    <div class="slide" id="enhanced-analysis-info">
        <div class="slide-header">
            <div class="slide-title">Analyse Avanc\u00e9e</div>
            <div class="page-number"></div>
        </div>
        <div class="slide-content">
            <h1>Analyse Transversale et Matrice de Risques</h1>
            
            <div class="info-box info">
                <h3><i class="fas fa-info-circle"></i> Sections d'Analyse Avanc\u00e9e</h3>
                <p>Les sections d'analyse transversale, matrice de risques consolid\u00e9e et feuille de route d'impl\u00e9mentation seront disponibles lorsque suffisamment de donn\u00e9es auront \u00e9t\u00e9 collect\u00e9es dans les ateliers.</p>
                
                <h4>\u00c9tat Actuel des Donn\u00e9es :</h4>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1rem; margin: 1.5rem 0;">
                    ${dataStatus.map(status => `
                        <div style="background: white; border-radius: 8px; padding: 1rem; border-left: 4px solid ${status.color};">
                            <div style="font-weight: 600; margin-bottom: 0.5rem;">${status.workshop}</div>
                            <div style="font-size: 0.9rem; color: #6b7280;">${status.description}</div>
                            <div style="margin-top: 0.5rem;">
                                <span class="status-badge ${status.statusClass}">${status.status}</span>
                            </div>
                        </div>
                    `).join('')}
                </div>
                
                <h4>Sections Avanc\u00e9es Disponibles une fois les Donn\u00e9es Compl\u00e9t\u00e9es :</h4>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1rem; margin: 1.5rem 0;">
                    <div style="background: white; border-radius: 8px; padding: 1rem; border-left: 4px solid #3b82f6;">
                        <div style="font-weight: 600; margin-bottom: 0.5rem;"><i class="fas fa-project-diagram"></i> Analyse Transversale</div>
                        <div style="font-size: 0.9rem; color: #6b7280;">Corr\u00e9lations entre ateliers, flux de risques, lacunes de couverture</div>
                    </div>
                    <div style="background: white; border-radius: 8px; padding: 1rem; border-left: 4px solid #10b981;">
                        <div style="font-weight: 600; margin-bottom: 0.5rem;"><i class="fas fa-table"></i> Matrice de Risques</div>
                        <div style="font-size: 0.9rem; color: #6b7280;">Consolidation des risques, priorisation, visualisation matricielle</div>
                    </div>
                    <div style="background: white; border-radius: 8px; padding: 1rem; border-left: 4px solid #f59e0b;">
                        <div style="font-weight: 600; margin-bottom: 0.5rem;"><i class="fas fa-road"></i> Feuille de Route</div>
                        <div style="font-size: 0.9rem; color: #6b7280;">Plan d'impl\u00e9mentation, timeline, budget, responsabilit\u00e9s</div>
                    </div>
                </div>
                
                <h4>Conditions Requises :</h4>
                <ul style="margin: 1rem 0; padding-left: 2rem;">
                    <li>Compl\u00e9ter au moins 3 ateliers avec des donn\u00e9es significatives</li>
                    <li>Identifier des valeurs m\u00e9tier dans l'Atelier 1</li>
                    <li>Documenter des sources de risque dans l'Atelier 2</li>
                    <li>D\u00e9velopper des sc\u00e9narios dans les Ateliers 3 ou 4</li>
                </ul>
            </div>
        </div>
    </div>`;
    }

    /**
     * Analyze data completeness for each workshop
     * @private
     */
    analyzeDataCompleteness(workshops) {
        const status = [];

        const workshop1Data = workshops.workshop1 || {};
        const w1Count = (workshop1Data.businessValues?.length || 0) + (workshop1Data.dreadedEvents?.length || 0);
        status.push({
            workshop: "Atelier 1 - Cadrage",
            description: `${w1Count} \u00e9l\u00e9ments identifi\u00e9s`,
            status: w1Count > 0 ? "Donn\u00e9es disponibles" : "En attente",
            statusClass: w1Count > 0 ? "success" : "secondary",
            color: w1Count > 0 ? "#10b981" : "#6b7280"
        });

        const workshop2Data = workshops.workshop2 || {};
        const w2Count = (workshop2Data.riskSources?.length || 0) + (workshop2Data.stakeholders?.length || 0);
        status.push({
            workshop: "Atelier 2 - Sources de Risque",
            description: `${w2Count} \u00e9l\u00e9ments identifi\u00e9s`,
            status: w2Count > 0 ? "Donn\u00e9es disponibles" : "En attente",
            statusClass: w2Count > 0 ? "success" : "secondary",
            color: w2Count > 0 ? "#10b981" : "#6b7280"
        });

        const workshop3Data = workshops.workshop3 || {};
        const w3Count = workshop3Data.strategicScenarios?.length || 0;
        status.push({
            workshop: "Atelier 3 - Sc\u00e9narios Strat\u00e9giques",
            description: `${w3Count} sc\u00e9narios d\u00e9velopp\u00e9s`,
            status: w3Count > 0 ? "Donn\u00e9es disponibles" : "En attente",
            statusClass: w3Count > 0 ? "success" : "secondary",
            color: w3Count > 0 ? "#10b981" : "#6b7280"
        });

        const workshop4Data = workshops.workshop4 || {};
        const w4Count = workshop4Data.operationalScenarios?.length || 0;
        status.push({
            workshop: "Atelier 4 - Sc\u00e9narios Op\u00e9rationnels",
            description: `${w4Count} sc\u00e9narios d\u00e9velopp\u00e9s`,
            status: w4Count > 0 ? "Donn\u00e9es disponibles" : "En attente",
            statusClass: w4Count > 0 ? "success" : "secondary",
            color: w4Count > 0 ? "#10b981" : "#6b7280"
        });

        const workshop5Data = workshops.workshop5 || {};
        const w5Count = workshop5Data.riskTreatmentPlans?.length || 0;
        status.push({
            workshop: "Atelier 5 - Traitement du Risque",
            description: `${w5Count} plans de traitement`,
            status: w5Count > 0 ? "Donn\u00e9es disponibles" : "En attente",
            statusClass: w5Count > 0 ? "success" : "secondary",
            color: w5Count > 0 ? "#10b981" : "#6b7280"
        });

        return status;
    }

    /**
     * Check if there's minimum data for enhanced analysis sections
     * @private
     */
    hasMinimumDataForEnhancedAnalysis(workshops) {
        // Require at least 3 workshops with meaningful data
        const workshopsWithData = Object.values(workshops).filter(workshop => {
            if (!workshop) return false;

            const dataCount = Object.values(workshop).reduce((count, value) => {
                if (Array.isArray(value)) return count + value.length;
                if (value && typeof value === 'object' && Object.keys(value).length > 0) return count + 1;
                return count;
            }, 0);

            return dataCount >= 2; // At least 2 meaningful data points per workshop
        });

        // Also check for specific data types that enable meaningful analysis
        const hasBusinessValues = workshops.workshop1?.businessValues?.length > 0;
        const hasRiskSources = workshops.workshop2?.riskSources?.length > 0;
        const hasScenarios = (workshops.workshop3?.strategicScenarios?.length || 0) +
            (workshops.workshop4?.operationalScenarios?.length || 0) > 0;

        return workshopsWithData.length >= 3 && hasBusinessValues && hasRiskSources && hasScenarios;
    }

    /**
     * Get completion status for a workshop
     * @private
     */
    getCompletionStatus(workshopData) {
        if (!workshopData) return "not-started";

        const hasData = Object.values(workshopData).some(value =>
            Array.isArray(value) ? value.length > 0 : value && Object.keys(value).length > 0
        );

        if (hasData) {
            const dataCount = Object.values(workshopData).reduce((count, value) => {
                if (Array.isArray(value)) return count + value.length;
                if (value && typeof value === 'object') return count + Object.keys(value).length;
                return count;
            }, 0);

            return dataCount >= 5 ? "completed" : "in-progress";
        }

        return "not-started";
    }

    /**
     * Get completion label for a workshop
     * @private
     */
    getCompletionLabel(workshopData) {
        const status = this.getCompletionStatus(workshopData);
        const labels = {
            "completed": "Termin\u00e9",
            "in-progress": "En cours",
            "not-started": "Non d\u00e9marr\u00e9"
        };
        return labels[status] || "Inconnu";
    }

    /**
     * Get completion percentage for a workshop
     * @private
     */
    getCompletionPercentage(workshopData) {
        if (!workshopData) return 0;

        const dataCount = Object.values(workshopData).reduce((count, value) => {
            if (Array.isArray(value)) return count + Math.min(value.length, 3); // Cap at 3 for percentage calculation
            if (value && typeof value === 'object') return count + Math.min(Object.keys(value).length, 3);
            return count;
        }, 0);

        const maxExpected = 9; // Rough estimate of expected data points per workshop
        return Math.min(Math.round((dataCount / maxExpected) * 100), 100);
    }

    /**
     * Get status color for a completion status
     * @private
     */
    getStatusColor(status) {
        const colors = {
            "completed": "#10b981",
            "in-progress": "#f59e0b",
            "not-started": "#ef4444"
        };
        return colors[status] || "#6b7280";
    }

    /**
     * Generate risk matrix visualization
     * @private
     */
    generateRiskMatrixVisualization(riskMatrix) {
        return `
            <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: #e2e8f0; border-radius: 8px; overflow: hidden;">
                <!-- Header row -->
                <div style="background: #1e293b; color: white; padding: 1rem; text-align: center; font-weight: 700;">Impact →<br>Probabilit\u00e9 ↓</div>
                <div style="background: #1e293b; color: white; padding: 1rem; text-align: center; font-weight: 700;">Faible</div>
                <div style="background: #1e293b; color: white; padding: 1rem; text-align: center; font-weight: 700;">Moyen</div>
                <div style="background: #1e293b; color: white; padding: 1rem; text-align: center; font-weight: 700;">\u00c9lev\u00e9</div>
                
                <!-- Probability rows -->
                <div style="background: #1e293b; color: white; padding: 1rem; text-align: center; font-weight: 700;">\u00c9lev\u00e9</div>
                <div style="background: #fed7aa; padding: 1rem; text-align: center; min-height: 60px; display: flex; align-items: center; justify-content: center;">Moyen</div>
                <div style="background: #fecaca; padding: 1rem; text-align: center; min-height: 60px; display: flex; align-items: center; justify-content: center;">\u00c9lev\u00e9</div>
                <div style="background: #fca5a5; padding: 1rem; text-align: center; min-height: 60px; display: flex; align-items: center; justify-content: center; font-weight: 700;">Critique</div>
                
                <div style="background: #1e293b; color: white; padding: 1rem; text-align: center; font-weight: 700;">Moyen</div>
                <div style="background: #bbf7d0; padding: 1rem; text-align: center; min-height: 60px; display: flex; align-items: center; justify-content: center;">Faible</div>
                <div style="background: #fed7aa; padding: 1rem; text-align: center; min-height: 60px; display: flex; align-items: center; justify-content: center;">Moyen</div>
                <div style="background: #fecaca; padding: 1rem; text-align: center; min-height: 60px; display: flex; align-items: center; justify-content: center;">\u00c9lev\u00e9</div>
                
                <div style="background: #1e293b; color: white; padding: 1rem; text-align: center; font-weight: 700;">Faible</div>
                <div style="background: #bbf7d0; padding: 1rem; text-align: center; min-height: 60px; display: flex; align-items: center; justify-content: center;">Faible</div>
                <div style="background: #bbf7d0; padding: 1rem; text-align: center; min-height: 60px; display: flex; align-items: center; justify-content: center;">Faible</div>
                <div style="background: #fed7aa; padding: 1rem; text-align: center; min-height: 60px; display: flex; align-items: center; justify-content: center;">Moyen</div>
            </div>
        `;
    }
}
